import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useModal } from '../context/ModalDialogContext';
import TemplateDesigner, { resolveMappedValue, resolveElementLink, openLinkSafely, PictureFrameImage, calculateFrameBorderRadii, isElementVerifyTarget, resolveQrCodeValue, renderCertifyElement } from './TemplateDesigner';
import { QRCodeSVG } from '../utils/qrCode';
import { extractExifFromBlob, reverseGeocodePlace } from '../utils/exifReader';
import { formatDateTimeValue } from '../utils/dateTimeFormatter';
import FullscreenFaceEditor from './FullscreenFaceEditor';
import FaceTemplateManager from './FaceTemplateManager';
import AssetManager from './AssetManager';
import ErrorBoundary from './ErrorBoundary';
import { isDarkColor, resolveSolidColor } from '../utils/themeUtils';

// Custom Color input wrapper that works with native MacOS color sheet custom palette swatches.
function StableColorInput({ value, onChange, ...props }) {
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current && document.activeElement !== inputRef.current) {
      inputRef.current.value = value;
    }
  }, [value]);

  return (
    <input
      type="color"
      ref={inputRef}
      defaultValue={value}
      onChange={(e) => {
        if (onChange) onChange(e);
      }}
      onBlur={(e) => {
        if (onChange) onChange(e);
      }}
      {...props}
    />
  );
}

// eslint-disable-next-line no-unused-vars
function RenderColorInput({ value, onChange, style = {} }) {
  const hasEyeDropper = typeof window !== 'undefined' && 'EyeDropper' in window;
  return (
    <div style={{ display: 'flex', gap: '4px', alignItems: 'center', width: '100%' }}>
      <StableColorInput
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ flex: 1, height: '28px', border: '1px solid var(--border-color)', borderRadius: '4px', cursor: 'pointer', padding: 0, ...style }}
      />
      {hasEyeDropper ? (
        <button
          type="button"
          onClick={async () => {
            try {
              const eyeDropper = new window.EyeDropper();
              const result = await eyeDropper.open();
              onChange(result.sRGBHex);
            } catch (e) {
              console.log("EyeDropper closed or failed:", e);
            }
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '28px',
            height: '28px',
            backgroundColor: 'var(--bg-card)',
            border: '1px solid var(--border-color)',
            borderRadius: '4px',
            color: 'var(--text-color)',
            cursor: 'pointer',
            padding: 0
          }}
          title="Sample color from screen"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 3L11 13" />
            <path d="M19 5l-2.5-2.5a1.5 1.5 0 0 0-2.12 0l-3 3a1.5 1.5 0 0 0 0 2.12L13.88 10" />
            <path d="M11 11H8.5a1.5 1.5 0 0 0-1.5 1.5v1.79c0 .4-.16.78-.44 1.06L3.5 18.5a1 1 0 0 0 0 1.41l.59.59a1 1 0 0 0 1.41 0l3.15-3.06c.28-.28.66-.44 1.06-.44H11.5A1.5 1.5 0 0 0 13 15.5V13" />
          </svg>
        </button>
      ) : (
        <button
          type="button"
          disabled
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '28px',
            height: '28px',
            backgroundColor: 'var(--bg-card)',
            border: '1px solid var(--border-color)',
            borderRadius: '4px',
            color: 'var(--text-mute)',
            cursor: 'not-allowed',
            padding: 0,
            opacity: 0.5
          }}
          title="Eyedropper requires a secure context (HTTPS or localhost). If using a custom domain over HTTP, enable Chrome flag #unsafely-treat-insecure-origin-as-secure"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.3">
            <path d="M21 3L11 13" />
            <path d="M19 5l-2.5-2.5a1.5 1.5 0 0 0-2.12 0l-3 3a1.5 1.5 0 0 0 0 2.12L13.88 10" />
            <path d="M11 11H8.5a1.5 1.5 0 0 0-1.5 1.5v1.79c0 .4-.16.78-.44 1.06L3.5 18.5a1 1 0 0 0 0 1.41l.59.59a1 1 0 0 0 1.41 0l3.15-3.06c.28-.28.66-.44 1.06-.44H11.5A1.5 1.5 0 0 0 13 15.5V13" />
          </svg>
        </button>
      )}
    </div>
  );
}

export { AssetManager, renderCertifyElement };


const getDynamicFieldsInDesign = (design, datasets) => {
  const list = [];
  const seen = new Set();

  const add = (datasetKey, label, type, sampleVal) => {
    if (seen.has(label)) return;
    seen.add(label);

    let fieldDef = null;
    let foundDatasetKey = datasetKey;
    
    if (datasetKey && datasets[datasetKey]) {
      fieldDef = datasets[datasetKey].fields?.find(f => f.label === label);
    } else if (datasets) {
      for (const dsId of Object.keys(datasets)) {
        const f = datasets[dsId].fields?.find(f => f.label === label);
        if (f) {
          fieldDef = f;
          foundDatasetKey = dsId;
          break;
        }
      }
    }

    list.push({
      datasetKey: foundDatasetKey || '',
      label,
      alias: fieldDef?.alias || '',
      type: fieldDef?.type || type || 'text',
      sampleValue: sampleVal || fieldDef?.sample_data || '',
      options: fieldDef?.options || [],
      fieldDef
    });
  };

  const traverse = (elements) => {
    (elements || []).forEach(el => {
      if (el.type === 'text') {
        if (el.dynamicFields && el.dynamicFields.length > 0) {
          el.dynamicFields.forEach(df => {
            if (df.type === 'dynamic' && df.label) {
              add(df.datasetKey, df.label, 'text', df.sample_data);
            }
          });
        }
        if (el.text) {
          const matches = el.text.match(/\$[a-zA-Z0-9_]+\b/g);
          if (matches) {
            matches.forEach(m => {
              add('', m.substring(1), 'text', '');
            });
          }
        }
      }
      if (el.type === 'image' && el.imageType === 'frame') {
        const varName = el.imageVariable || '$image_source';
        if (varName.startsWith('$')) {
          add('', varName.substring(1), 'image', el.src);
        }
      }
      if (el.type === 'datagrid' && el.rows) {
        el.rows.forEach(row => {
          if (row && row[1]) {
            const matches = row[1].match(/\$[a-zA-Z0-9_]+\b/g);
            if (matches) {
              matches.forEach(m => {
                add('', m.substring(1), 'text', '');
              });
            }
          }
        });
      }
    });
  };

  if (design?.config?.sides) {
    traverse(design.config.sides.front?.elements);
    traverse(design.config.sides.back?.elements);
  }

  return list;
};

const getImageFilterStyle = (effectOrElem) => {
  const effect = typeof effectOrElem === 'object' && effectOrElem !== null ? effectOrElem.effect : effectOrElem;
  const hue = typeof effectOrElem === 'object' && effectOrElem !== null && effectOrElem.hue !== undefined
    ? (parseFloat(effectOrElem.hue) || 0)
    : 0;
  const saturation = typeof effectOrElem === 'object' && effectOrElem !== null && effectOrElem.saturation !== undefined && effectOrElem.saturation !== '' && !Number.isNaN(parseFloat(effectOrElem.saturation))
    ? parseFloat(effectOrElem.saturation)
    : 100;

  const filters = [];
  if (effect === 'mono_slate') {
    filters.push('grayscale(1)', 'contrast(1.15)', 'brightness(0.95)');
  } else if (effect === 'duotone_bronze') {
    filters.push('sepia(0.5)', 'hue-rotate(15deg)', 'contrast(1.15)', 'brightness(0.95)');
  }
  if (hue !== 0 && !Number.isNaN(hue)) {
    filters.push(`hue-rotate(${hue}deg)`);
  }
  if (saturation !== 100 && !Number.isNaN(saturation)) {
    filters.push(`saturate(${saturation}%)`);
  }
  return filters.length > 0 ? { filter: filters.join(' ') } : {};
};

const getImageOverlayStyle = (effect) => {
  let style = null;
  if (effect === 'foil_shimmer') {
    style = {
      background: 'linear-gradient(135deg, rgba(255,255,255,0) 20%, rgba(255,255,255,0.15) 40%, rgba(255,255,255,0.3) 50%, rgba(255,255,255,0.15) 60%, rgba(255,255,255,0) 80%)',
      backgroundSize: '250% 250%',
      backgroundPosition: '0% 0%',
      mixBlendMode: 'overlay',
      animation: 'shimmerFoilLoop 4s ease-in-out infinite'
    };
  } else if (effect === 'warm_leak') {
    style = {
      background: 'radial-gradient(circle at 0% 0%, rgba(255, 140, 50, 0.22) 0%, rgba(255, 200, 100, 0.05) 50%, transparent 100%)',
      mixBlendMode: 'screen'
    };
  } else if (effect === 'cold_frost') {
    style = {
      background: 'radial-gradient(circle at 100% 100%, rgba(100, 180, 255, 0.18) 0%, rgba(150, 220, 255, 0.03) 60%, transparent 100%)',
      mixBlendMode: 'screen'
    };
  } else if (effect === 'matte_vignette') {
    style = {
      background: 'radial-gradient(circle, transparent 50%, rgba(0, 0, 0, 0.4) 100%)',
      mixBlendMode: 'multiply'
    };
  } else if (effect === 'vintage_sepia') {
    style = {
      background: 'rgba(210, 150, 80, 0.1)',
      mixBlendMode: 'multiply'
    };
  } else if (effect === 'gold_dust') {
    style = {
      background: 'repeating-linear-gradient(45deg, rgba(212, 175, 55, 0.03) 0px, rgba(212, 175, 55, 0.03) 1px, transparent 1px, transparent 6px), repeating-linear-gradient(-45deg, rgba(212, 175, 55, 0.03) 0px, rgba(212, 175, 55, 0.03) 1px, transparent 1px, transparent 8px)',
      mixBlendMode: 'overlay'
    };
  } else if (effect === 'silver_satin') {
    style = {
      background: 'linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(200,200,210,0.12) 30%, rgba(255,255,255,0.2) 50%, rgba(200,200,210,0.12) 70%, rgba(255,255,255,0.03) 100%)',
      mixBlendMode: 'overlay'
    };
  } else if (effect === 'warm_velvet') {
    style = {
      background: 'linear-gradient(180deg, rgba(200, 50, 100, 0.06) 0%, rgba(255, 120, 50, 0.1) 100%)',
      mixBlendMode: 'overlay'
    };
  } else if (effect === 'emerald_glaze') {
    style = {
      background: 'linear-gradient(135deg, rgba(16, 185, 129, 0) 30%, rgba(16, 185, 129, 0.15) 50%, rgba(16, 185, 129, 0) 70%)',
      mixBlendMode: 'overlay'
    };
  } else if (effect === 'royal_velvet') {
    style = {
      background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(168, 85, 247, 0.08) 100%)',
      mixBlendMode: 'color-dodge'
    };
  } else if (effect === 'iridescent_rainbow') {
    style = {
      background: 'linear-gradient(135deg, rgba(255,100,100,0.12) 0%, rgba(255,200,100,0.12) 20%, rgba(100,255,100,0.12) 40%, rgba(100,200,255,0.12) 60%, rgba(200,100,255,0.12) 80%, rgba(255,100,100,0.12) 100%)',
      backgroundSize: '400% 400%',
      mixBlendMode: 'overlay',
      animation: 'iridescentRainbowLoop 7s ease infinite'
    };
  } else if (effect === 'silver_glint') {
    style = {
      background: 'linear-gradient(90deg, rgba(255,255,255,0) 30%, rgba(255,255,255,0.35) 50%, rgba(255,255,255,0) 70%)',
      backgroundSize: '200% 100%',
      mixBlendMode: 'overlay',
      animation: 'silverGlintLoop 6s ease-in-out infinite'
    };
  } else if (effect === 'ambient_pulse') {
    style = {
      background: 'radial-gradient(circle at 10% 10%, rgba(255, 120, 40, 0.25) 0%, rgba(255, 180, 80, 0.08) 50%, transparent 100%)',
      mixBlendMode: 'screen',
      animation: 'ambientWarmPulseLoop 8s ease-in-out infinite'
    };
  } else if (effect === 'gold_flare') {
    style = {
      background: 'linear-gradient(110deg, rgba(212,175,55,0) 30%, rgba(212,175,55,0.2) 45%, rgba(255,235,160,0.4) 50%, rgba(212,175,55,0.2) 55%, rgba(212,175,55,0) 70%)',
      backgroundSize: '200% 100%',
      mixBlendMode: 'overlay',
      animation: 'goldFlareLoop 7s ease-in-out infinite'
    };
  } else if (effect === 'glass_reflection') {
    style = {
      background: 'linear-gradient(135deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0) 45%, rgba(255,255,255,0.12) 50%, rgba(255,255,255,0) 55%, rgba(255,255,255,0) 100%)',
      backgroundSize: '200% 200%',
      mixBlendMode: 'overlay',
      animation: 'glassReflectionLoop 8s linear infinite'
    };
  } else if (effect === 'cosmic_aurora') {
    style = {
      background: 'radial-gradient(circle at 80% 20%, rgba(0, 255, 230, 0.15) 0%, rgba(200, 0, 255, 0.1) 40%, transparent 80%)',
      mixBlendMode: 'screen',
      animation: 'cosmicAuroraLoop 12s ease-in-out infinite',
      width: '100%',
      height: '100%'
    };
  }

  if (style) {
    return {
      ...style,
      transform: 'translateZ(2px)',
      zIndex: 10
    };
  }
  return null;
};

export const BragCardCanvas = ({
  design,
  side = 'front',
  scale = 1,
  testDataOverrides = {},
  datasets = {},
  user = null,
  orgData = null,
  tenantConfig = null,
  cardRef = null,
  id = null,
  style = {},
  onVerify = null,
  verifying = false,
  onLinkClick = null,
  isSubscriptionExpired = false
}) => {
  const templateConfig = design?.config || design || {};
  const cardDimensions = templateConfig.cardDimensions || { width: 400, height: 560 };
  const globalStyles = templateConfig.globalStyles || { borderRadius: '18px', borderWidth: 6, borderStyle: 'solid', borderColor: '#c9a054' };
  const sideConfig = templateConfig.sides?.[side] || { background: { type: 'color', value: '#111111' }, elements: [] };
  const fieldMappings = templateConfig.field_mappings || {};
  const lockedOptions = templateConfig.locked_options || {};
  const dateFormats = templateConfig.date_formats || templateConfig.field_date_formats || {};
  const isDev = typeof window !== 'undefined' && window.location.port === '3000';
  const API_BASE = isDev ? `http://${window.location.hostname}:8000` : '';

  const formatBorderRadius = (r) => {
    if (r === undefined || r === null || r === '') return '18px';
    if (typeof r === 'number') return `${r}px`;
    if (typeof r === 'string') return (r.endsWith('px') || r.endsWith('%') || r.endsWith('rem')) ? r : `${r}px`;
    return '18px';
  };

  const formatBorderWidth = (w) => {
    if (w === undefined || w === null || w === '') return '6px';
    if (typeof w === 'number') return `${w}px`;
    if (typeof w === 'string') return w.endsWith('px') ? w : `${parseInt(w, 10) || 0}px`;
    return '6px';
  };

  const getMappedOrOverrideValue = (label) => {
    if (!label) return undefined;
    const cleanLabel = String(label).replace(/^\$/, '');
    if (testDataOverrides) {
      if (testDataOverrides[cleanLabel] !== undefined && testDataOverrides[cleanLabel] !== '') {
        return testDataOverrides[cleanLabel];
      }
      if (testDataOverrides[label] !== undefined && testDataOverrides[label] !== '') {
        return testDataOverrides[label];
      }
    }
    if (lockedOptions) {
      if (lockedOptions[cleanLabel] !== undefined && lockedOptions[cleanLabel] !== null && lockedOptions[cleanLabel] !== '') {
        return lockedOptions[cleanLabel];
      }
      if (lockedOptions[label] !== undefined && lockedOptions[label] !== null && lockedOptions[label] !== '') {
        return lockedOptions[label];
      }
    }
    if (fieldMappings) {
      const mapping = fieldMappings[cleanLabel] || fieldMappings[label];
      if (mapping) {
        const resolved = resolveMappedValue(mapping, user, orgData, tenantConfig);
        if (resolved !== null && resolved !== undefined && resolved !== '') {
          return resolved;
        }
      }
    }
    return undefined;
  };

  const applyTextTransform = (text, modifier) => {
    if (!text) return '';
    if (modifier === 'uppercase') {
      return text.toUpperCase();
    } else if (modifier === 'lowercase') {
      return text.toLowerCase();
    } else if (modifier === 'capitalize') {
      return text.replace(/\S+/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
    }
    return text;
  };

  const applyRenderer = (val, rendererId, fieldObj = null) => {
    if (!rendererId || rendererId === 'none') return val;
    const strVal = String(val).trim();

    if (rendererId === 'uppercase') return strVal.toUpperCase();
    if (rendererId === 'lowercase') return strVal.toLowerCase();
    if (rendererId === 'titlecase') return strVal.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
    if (rendererId === 'metric_to_imperial') {
      const numMatch = strVal.match(/^([\d.]+)\s*(kg|cm|m|g|ml)?$/i);
      if (numMatch) {
        const num = parseFloat(numMatch[1]);
        const unit = numMatch[2] ? numMatch[2].toLowerCase() : '';
        if (unit === 'kg') return `${(num * 2.20462).toFixed(1)} lbs`;
        if (unit === 'g') return `${(num * 0.035274).toFixed(1)} oz`;
        if (unit === 'cm') return `${(num * 0.393701).toFixed(1)} in`;
        if (unit === 'm') return `${(num * 3.28084).toFixed(1)} ft`;
      }
      return strVal;
    }
    if (rendererId === 'pad_5') {
      const cleanNum = strVal.replace(/\D/g, '');
      return cleanNum ? cleanNum.padStart(5, '0') : strVal;
    }
    if (rendererId === 'prepend_hash_pad_5') {
      const cleanNum = strVal.replace(/\D/g, '');
      return cleanNum ? `#${cleanNum.padStart(5, '0')}` : strVal;
    }

    try {
      const saved = localStorage.getItem('customRegexRenderers');
      let customRules = saved ? JSON.parse(saved) : [];
      let rule = customRules.find(r => String(r.id) === String(rendererId));
      if (!rule && fieldObj && fieldObj.custom_renderer && String(fieldObj.custom_renderer.id) === String(rendererId)) {
        rule = fieldObj.custom_renderer;
      }
      if (rule && rule.regex) {
        let pattern = rule.regex;
        let flags = 'g';
        if (pattern.startsWith('/')) {
          const lastSlashIdx = pattern.lastIndexOf('/');
          if (lastSlashIdx > 0) {
            flags = pattern.slice(lastSlashIdx + 1) || 'g';
            pattern = pattern.slice(1, lastSlashIdx);
          }
        }
        return strVal.replace(new RegExp(pattern, flags), rule.replace);
      }
    } catch (e) {
      console.error(e);
    }
    return strVal;
  };

  const resolveTemplateVariables = (str) => {
    if (!str) return '';
    let resolved = String(str);
    
    // 1. Resolve from testDataOverrides
    if (testDataOverrides) {
      Object.keys(testDataOverrides).forEach(key => {
        const cleanKey = key.replace(/^\$/, '');
        let val = testDataOverrides[key] !== undefined ? testDataOverrides[key] : '';
        // If val is empty, check fallbacks from lockedOptions, fieldMappings, or datasets
        if (!val || String(val).trim() === '') {
          if (lockedOptions && (lockedOptions[cleanKey] || lockedOptions[key])) {
            val = lockedOptions[cleanKey] || lockedOptions[key];
          } else if (fieldMappings && (fieldMappings[cleanKey] || fieldMappings[key])) {
            const mapKey = fieldMappings[cleanKey] || fieldMappings[key];
            const mapVal = resolveMappedValue(mapKey, user, orgData, tenantConfig);
            if (mapVal) val = mapVal;
          }
          if (!val && datasets) {
            for (const ds of Object.values(datasets)) {
              const fieldObj = ds?.fields?.find(f => f.label === cleanKey || f.label === key);
              if (fieldObj?.sample_data) {
                val = fieldObj.sample_data;
                break;
              }
              if (ds?.variables?.[cleanKey] || ds?.variables?.[key]) {
                val = ds.variables[cleanKey] || ds.variables[key];
                break;
              }
            }
          }
        }
        // If date format or renderer is configured, apply it
        const dateFmt = (dateFormats && (dateFormats[cleanKey] || dateFormats[key])) || null;
        if (dateFmt && val) {
          val = formatDateTimeValue(val, dateFmt);
        }
        if (datasets) {
          Object.values(datasets).forEach(ds => {
            const fieldObj = ds?.fields?.find(f => f.label === cleanKey || f.label === key);
            const dsDateFmt = fieldObj?.date_format || (fieldObj?.alias && dateFormats && dateFormats[fieldObj.alias]);
            if (dsDateFmt && !dateFmt && val) {
              val = formatDateTimeValue(val, dsDateFmt);
            }
            if (fieldObj && fieldObj.render && fieldObj.render !== 'none') {
              val = applyRenderer(val, fieldObj.render, fieldObj);
            }
          });
        }
        resolved = resolved.replace(new RegExp(`\\$${cleanKey}\\b`, 'gi'), val);
      });
    }

    // 2. Resolve from locked_options
    if (lockedOptions) {
      Object.keys(lockedOptions).forEach(key => {
        const cleanKey = key.replace(/^\$/, '');
        if (testDataOverrides && (testDataOverrides[cleanKey] !== undefined || testDataOverrides[key] !== undefined) && String(testDataOverrides[cleanKey] || testDataOverrides[key] || '').trim() !== '') return;
        let val = lockedOptions[key] !== undefined ? lockedOptions[key] : '';
        resolved = resolved.replace(new RegExp(`\\$${cleanKey}\\b`, 'gi'), val);
      });
    }

    // 3. Resolve from field_mappings
    if (fieldMappings) {
      Object.keys(fieldMappings).forEach(key => {
        const cleanKey = key.replace(/^\$/, '');
        if (testDataOverrides && (testDataOverrides[cleanKey] !== undefined || testDataOverrides[key] !== undefined) && String(testDataOverrides[cleanKey] || testDataOverrides[key] || '').trim() !== '') return;
        if (lockedOptions && (lockedOptions[cleanKey] !== undefined || lockedOptions[key] !== undefined) && String(lockedOptions[cleanKey] || lockedOptions[key] || '').trim() !== '') return;
        let val = resolveMappedValue(fieldMappings[key], user, orgData, tenantConfig);
        if (val !== null && val !== undefined && val !== '') {
          resolved = resolved.replace(new RegExp(`\\$${cleanKey}\\b`, 'gi'), val);
        }
      });
    }

    // 4. Resolve from datasets
    if (datasets) {
      Object.keys(datasets).forEach(dsKey => {
        const ds = datasets[dsKey];
        if (ds && ds.variables) {
          Object.keys(ds.variables).forEach(key => {
            const cleanKey = key.replace(/^\$/, '');
            const mappedVal = (fieldMappings && (fieldMappings[cleanKey] || fieldMappings[key]))
              ? resolveMappedValue(fieldMappings[cleanKey] || fieldMappings[key], user, orgData, tenantConfig)
              : null;
            if (
              (testDataOverrides && (testDataOverrides[cleanKey] !== undefined || testDataOverrides[key] !== undefined) && String(testDataOverrides[cleanKey] || testDataOverrides[key] || '').trim() !== '') ||
              (lockedOptions && (lockedOptions[cleanKey] !== undefined || lockedOptions[key] !== undefined) && String(lockedOptions[cleanKey] || lockedOptions[key] || '').trim() !== '') ||
              (mappedVal !== null && mappedVal !== undefined && mappedVal !== '')
            ) return;
            let val = ds.variables[key] !== undefined ? ds.variables[key] : '';
            const fieldObj = ds.fields?.find(f => f.label === cleanKey || f.label === key);
            if ((val === '' || val === undefined) && fieldObj?.sample_data) {
              val = fieldObj.sample_data;
            }
            if (fieldObj && fieldObj.render && fieldObj.render !== 'none') {
              val = applyRenderer(val, fieldObj.render, fieldObj);
            }
            resolved = resolved.replace(new RegExp(`\\$${cleanKey}\\b`, 'gi'), val);
          });
        }
        if (ds && ds.fields) {
          ds.fields.forEach(fieldObj => {
            const key = fieldObj.label;
            const cleanKey = key.replace(/^\$/, '');
            const mappedVal = (fieldMappings && (fieldMappings[cleanKey] || fieldMappings[key]))
              ? resolveMappedValue(fieldMappings[cleanKey] || fieldMappings[key], user, orgData, tenantConfig)
              : null;
            if (
              (testDataOverrides && (testDataOverrides[cleanKey] !== undefined || testDataOverrides[key] !== undefined) && String(testDataOverrides[cleanKey] || testDataOverrides[key] || '').trim() !== '') ||
              (lockedOptions && (lockedOptions[cleanKey] !== undefined || lockedOptions[key] !== undefined) && String(lockedOptions[cleanKey] || lockedOptions[key] || '').trim() !== '') ||
              (mappedVal !== null && mappedVal !== undefined && mappedVal !== '') ||
              (ds.variables && (ds.variables[cleanKey] !== undefined || ds.variables[key] !== undefined) && String(ds.variables[cleanKey] || ds.variables[key] || '').trim() !== '')
            ) return;
            let val = fieldObj.sample_data || '';
            if (fieldObj.render && fieldObj.render !== 'none') {
              val = applyRenderer(val, fieldObj.render, fieldObj);
            }
            resolved = resolved.replace(new RegExp(`\\$${cleanKey}\\b`, 'gi'), val);
          });
        }
      });
    }
    return resolved;
  };

  const getResolvedImageSrc = (elem, options = {}) => {
    if (!elem) return '';
    let src = '';
    if (elem.imageType === 'frame') {
      const varName = elem.imageVariable || '$image_source';
      const rawLabel = varName.replace(/^\$/, '');
      const mappedVal = getMappedOrOverrideValue(rawLabel);
      if (mappedVal) {
        src = mappedVal;
      } else {
        const resolved = resolveTemplateVariables(varName);
        if (resolved && resolved !== varName) {
          src = resolved;
        }
      }
    }
    if (!src) {
      if ((options.preferOriginal || elem.optimizeImage === false) && elem.originalSrc) {
        src = elem.originalSrc;
      } else if (elem.optimizeImage !== false && elem.optimizedSrc) {
        src = elem.optimizedSrc;
      } else {
        src = elem.src;
      }
    }
    if (!src) return '';

    const wantsOptimize = (elem.optimizeImage !== false && !options.preferOriginal);

    // Normalize uploads path to current API_BASE and avoid mixed-content or foreign dev hosts
    if (src.includes('/uploads/')) {
      const pathPart = src.substring(src.indexOf('/uploads/'));
      const cleanPath = pathPart.split('?')[0];
      const query = pathPart.includes('?') ? pathPart.substring(pathPart.indexOf('?')) : '';
      const base = API_BASE ? API_BASE.replace(/\/+$/, '') : '';
      let separator = query ? '&' : '?';
      let finalUrl = `${base}${cleanPath}${query}${separator}cors=true`;
      if (wantsOptimize && !cleanPath.toLowerCase().endsWith('.svg') && !cleanPath.includes('opt_') && !finalUrl.includes('optimize=')) {
        finalUrl += '&optimize=true';
      }
      return finalUrl;
    }

    if (!src.startsWith('data:') && !src.includes('cors=true')) {
      const separator = src.includes('?') ? '&' : '?';
      return `${src}${separator}cors=true`;
    }
    return src || '';
  };

  const resolveElementText = (elem) => {
    if (!elem) return '';
    let text = '';
    if (elem.type === 'text' && elem.dynamicFields && elem.dynamicFields.length > 0) {
      text = elem.dynamicFields.map((item) => {
        if (item.type === 'static') {
          return item.value;
        } else {
          const mappedVal = getMappedOrOverrideValue(item.label);
          let val = '';
          if (mappedVal !== undefined && mappedVal !== null && mappedVal !== '') {
            val = mappedVal;
          } else {
            const ds = datasets[item.datasetKey];
            if (ds && ds.variables && ds.variables[item.label] !== undefined && ds.variables[item.label] !== '') {
              val = ds.variables[item.label];
            } else {
              val = item.sample_data || '';
            }
          }
          
          let rendererId = item.render || 'none';
          let matchedFieldObj = null;
          const ds = datasets[item.datasetKey];
          if (ds && ds.fields) {
            const fieldObj = ds.fields.find(f => f.label === item.label);
            if (fieldObj) {
              matchedFieldObj = fieldObj;
              if (fieldObj.render) {
                rendererId = fieldObj.render;
              }
            }
          }
          if (!matchedFieldObj && item.custom_renderer) {
            matchedFieldObj = { custom_renderer: item.custom_renderer };
          }
          if (!val) {
            val = item.sample_data || matchedFieldObj?.sample_data || '';
          }
          
          const dateFmt = (dateFormats && (dateFormats[item.label] || (matchedFieldObj?.alias && dateFormats[matchedFieldObj.alias]))) || matchedFieldObj?.date_format || null;
          if (dateFmt && val) {
            val = formatDateTimeValue(val, dateFmt);
          }

          if (rendererId && rendererId !== 'none') {
            val = applyRenderer(val, rendererId, matchedFieldObj);
          }
          return val;
        }
      }).join('');
    } else {
      text = resolveTemplateVariables(elem.text);
    }

    if (text) {
      const modifier = elem.textTransformModifier || elem.textTransform;
      if (modifier === 'uppercase') text = text.toUpperCase();
      else if (modifier === 'lowercase') text = text.toLowerCase();
      else if (modifier === 'capitalize') text = text.replace(/\S+/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
    }

    return text;
  };

  const getElementEffectsStyle = (elem) => {
    if (!elem) return {};
    let shadowStr = '', shadowFilterStr = '', textShadowStr = '';
    if (elem.shadowEnabled) {
      const hex = elem.shadowColor || '#000000';
      const opacity = elem.shadowOpacity !== undefined ? elem.shadowOpacity : 0.5;
      let r = 0, g = 0, b = 0;
      if (hex.startsWith('#')) {
        r = parseInt(hex.slice(1, 3), 16) || 0;
        g = parseInt(hex.slice(3, 5), 16) || 0;
        b = parseInt(hex.slice(5, 7), 16) || 0;
      }
      const colorStr = `rgba(${r}, ${g}, ${b}, ${opacity})`;
      const offsetX = elem.shadowOffsetX || 0;
      const offsetY = elem.shadowOffsetY || 0;
      const blur = elem.shadowBlur !== undefined ? elem.shadowBlur : 5;
      const spread = elem.shadowSpread || 0;
      shadowStr = `${offsetX}px ${offsetY}px ${blur}px ${spread}px ${colorStr}`;
      shadowFilterStr = `drop-shadow(${offsetX}px ${offsetY}px ${blur}px ${colorStr})`;
      textShadowStr = `${offsetX}px ${offsetY}px ${blur}px ${colorStr}`;
    }

    let glowStr = '', glowFilterStr = '', textGlowStr = '';
    if (elem.glowEnabled) {
      const hex = elem.glowColor || '#00ff00';
      const opacity = elem.glowOpacity !== undefined ? elem.glowOpacity : 0.6;
      let r = 0, g = 0, b = 0;
      if (hex.startsWith('#')) {
        r = parseInt(hex.slice(1, 3), 16) || 0;
        g = parseInt(hex.slice(3, 5), 16) || 0;
        b = parseInt(hex.slice(5, 7), 16) || 0;
      }
      const colorStr = `rgba(${r}, ${g}, ${b}, ${opacity})`;
      const blur = elem.glowBlur !== undefined ? elem.glowBlur : 10;
      const spread = elem.glowSpread || 0;
      const isInner = elem.glowType === 'inner';
      const insetPrefix = isInner ? 'inset ' : '';
      glowStr = `${insetPrefix}0px 0px ${blur}px ${spread}px ${colorStr}`;
      glowFilterStr = `drop-shadow(0px 0px ${blur}px ${colorStr})`;
      textGlowStr = `0px 0px ${blur}px ${colorStr}`;
    }

    let borderRadius = undefined;
    if (elem.shadowEnabled || elem.glowEnabled) {
      if (elem.type === 'shape') {
        if (elem.shapeType === 'circle') borderRadius = '50%';
        else if (elem.shapeType === 'pill') borderRadius = '999px';
        else if (elem.shapeType === 'rect') borderRadius = `${elem.hasBorder !== false ? (elem.borderRadius !== undefined ? elem.borderRadius : 0) : 0}px`;
      } else if (elem.type === 'image') {
        borderRadius = `${elem.hasBorder !== false ? (elem.borderRadius !== undefined ? elem.borderRadius : 0) : 0}px`;
      }
    }

    const style = {};
    if (borderRadius) style.borderRadius = borderRadius;
    if (elem.type === 'text') {
      const textShadows = [textShadowStr, textGlowStr].filter(Boolean).join(', ');
      if (textShadows) style.textShadow = textShadows;
    } else if (elem.type === 'shape' && ['triangle', 'polygon', 'diamond', 'star'].includes(elem.shapeType)) {
      const filters = [shadowFilterStr, glowFilterStr].filter(Boolean).join(' ');
      if (filters) style.filter = filters;
    } else {
      const boxShadows = [shadowStr, glowStr].filter(Boolean).join(', ');
      if (boxShadows) style.boxShadow = boxShadows;
    }
    return style;
  };

  return (
    <div
      id={id || `coin-card-side-${side}`}
      ref={cardRef}
      style={{
        width: `${cardDimensions.width}px`,
        height: `${cardDimensions.height}px`,
        transform: scale !== 1 ? `scale(${scale})` : undefined,
        transformOrigin: 'top left',
        borderRadius: formatBorderRadius(globalStyles.borderRadius),
        border: `${formatBorderWidth(globalStyles.borderWidth)} ${globalStyles.borderStyle || 'solid'} ${globalStyles.borderColor || '#c9a054'}`,
        ...(sideConfig.background?.type === 'gradient' || (sideConfig.background?.value && String(sideConfig.background.value).includes('gradient'))
          ? { background: sideConfig.background.value }
          : sideConfig.background?.type === 'pattern'
            ? {
                backgroundColor: sideConfig.background?.pattern?.tintColor || '#111111',
                background: sideConfig.background?.value || '#111111'
              }
            : {
                backgroundColor: sideConfig.background?.value || '#111111'
              }
        ),
        position: 'relative',
        overflow: 'hidden',
        boxSizing: 'border-box',
        boxShadow: '0 8px 30px rgba(0,0,0,0.5)',
        flexShrink: 0,
        ...style
      }}
    >
      {sideConfig.background?.type === 'pattern' && sideConfig.background?.pattern && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          borderRadius: 'inherit',
          pointerEvents: 'none',
          opacity: sideConfig.background.pattern.opacity !== undefined ? (sideConfig.background.pattern.opacity > 1 ? sideConfig.background.pattern.opacity / 100 : sideConfig.background.pattern.opacity) : 1,
          backgroundImage: `url("${sideConfig.background.pattern.image}")`,
          backgroundRepeat: sideConfig.background.pattern.repeat || 'repeat',
          backgroundSize: `${Math.max(2, Math.round((sideConfig.background.pattern.tileWidth || 32) * ((sideConfig.background.pattern.scale !== undefined ? sideConfig.background.pattern.scale : 100) / 100)))}px ${Math.max(2, Math.round((sideConfig.background.pattern.tileHeight || 32) * ((sideConfig.background.pattern.scale !== undefined ? sideConfig.background.pattern.scale : 100) / 100)))}px`,
          backgroundPosition: '0 0',
          mixBlendMode: sideConfig.background.pattern.blendMode || 'normal',
          zIndex: 0
        }} />
      )}
      {sideConfig.elements && sideConfig.elements
        .filter(elem => {
          if (elem.type === 'group') return false;
          if (elem.visible === false) return false;
          if (elem.parentId) {
            const parent = sideConfig.elements.find(el => el.id === elem.parentId);
            if (parent && parent.visible === false) return false;
          }
          return true;
        })
        .map(elem => {
        if (elem.type === 'group') return null;
        const linkInfo = resolveElementLink(
          elem,
          templateConfig.link_mappings || design?.config?.link_mappings || {},
          templateConfig.field_mappings || design?.config?.field_mappings || {},
          testDataOverrides,
          datasets,
          user,
          orgData,
          tenantConfig
        );
        const isVerifyTarget = Boolean(onVerify && isElementVerifyTarget(elem, sideConfig.elements));
        return (
          <div
            key={elem.id}
            onClick={(e) => {
              if (isVerifyTarget) {
                e.stopPropagation();
                onVerify();
                return;
              }
              if (elem.type === 'qrcode' && elem.openOnClick !== false) {
                const qrUrl = resolveQrCodeValue(elem, testDataOverrides, datasets, user, orgData, tenantConfig);
                if (qrUrl && (qrUrl.startsWith('http://') || qrUrl.startsWith('https://') || qrUrl.startsWith('mailto:'))) {
                  e.stopPropagation();
                  if (onLinkClick) {
                    onLinkClick('qrcode', { url: qrUrl, type: qrUrl.startsWith('mailto:') ? 'email' : 'url' }, elem);
                  }
                  if (qrUrl.startsWith('mailto:')) {
                    window.location.href = qrUrl;
                  } else {
                    window.open(qrUrl, '_blank', 'noopener,noreferrer');
                  }
                  return;
                }
              }
              if (linkInfo && linkInfo.url) {
                e.stopPropagation();
                if (isSubscriptionExpired) {
                  // Expired operator subscription suppresses link navigation
                  return;
                }
                // Determine event type for tracking
                let linkType = 'website';
                const lVar = elem.linkVariable || linkInfo.linkVar;
                if (lVar === '$booking_link') {
                  linkType = 'booking';
                } else if (lVar === '$emial_link' || lVar === '$email_link' || linkInfo.type === 'email') {
                  linkType = 'email';
                } else if (lVar === '$website_link_element') {
                  linkType = 'website';
                }

                if (onLinkClick) {
                  onLinkClick(linkType, linkInfo, elem);
                }

                openLinkSafely(linkInfo);
              }
            }}
            title={
              isVerifyTarget
                ? (verifying ? 'Verifying Authenticity Checksum...' : 'Verify Authenticity Checksum')
                : (elem.type === 'qrcode' && elem.openOnClick !== false
                    ? `📱 QR Code: ${resolveQrCodeValue(elem, testDataOverrides, datasets, user, orgData, tenantConfig)}`
                    : (linkInfo ? (isSubscriptionExpired ? 'Operator subscription expired' : `${linkInfo.type === 'email' ? '📧 Email: ' : '🔗 Open Link: '}${linkInfo.url}`) : undefined))
            }
            style={{
              position: 'absolute',
              left: `${elem.x}px`,
              top: `${elem.y}px`,
              width: `${elem.width}px`,
              height: `${elem.height}px`,
              boxSizing: 'border-box',
              border: 'none',
              zIndex: sideConfig.elements.indexOf(elem),
              cursor: isVerifyTarget 
                ? (verifying ? 'wait' : 'pointer') 
                : (elem.type === 'qrcode' && elem.openOnClick !== false 
                    ? 'pointer' 
                    : (linkInfo ? (isSubscriptionExpired ? 'not-allowed' : 'pointer') : 'default')),
              opacity: (() => {
                let opacity = elem.opacity !== undefined ? elem.opacity / 100 : 1;
                if (elem.parentId) {
                  const parent = sideConfig.elements.find(el => el.id === elem.parentId);
                  if (parent && parent.opacity !== undefined) {
                    opacity *= (parent.opacity / 100);
                  }
                }
                if (verifying && isVerifyTarget) {
                  opacity *= 0.65;
                }
                return opacity;
              })(),
              transition: isVerifyTarget ? 'opacity 0.2s ease, transform 0.15s ease' : undefined,
              mixBlendMode: elem.blendMode || 'normal',
              transform: elem.rotation ? `rotate(${elem.rotation}deg)` : 'none',
              transformOrigin: 'center center',
              ...getElementEffectsStyle(elem)
            }}
          >
              {elem.type === 'text' && (
                <div style={{
                  width: '100%',
                  height: '100%',
                  fontSize: `${elem.fontSize}px`,
                  fontFamily: elem.fontFamily,
                  fontWeight: elem.fontWeight,
                  fontStyle: elem.fontStyle || 'normal',
                  textDecoration: elem.textDecoration || 'none',
                  letterSpacing: `${elem.letterSpacing || 0}px`,
                  textTransform: elem.textTransform || 'none',
                  textAlign: elem.alignment || 'left',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: elem.alignment === 'center' ? 'center' : elem.alignment === 'right' ? 'flex-end' : 'flex-start',
                  lineHeight: elem.lineHeight || 1.1,
                  background: elem.colorType === 'gradient' ? (elem.color || 'linear-gradient(90deg, #ffffff 0%, #000000 100%)') : 'transparent',
                  WebkitBackgroundClip: elem.colorType === 'gradient' ? 'text' : 'unset',
                  WebkitTextFillColor: elem.colorType === 'gradient' ? 'transparent' : 'unset',
                  color: elem.colorType === 'gradient' ? 'transparent' : (elem.color || '#ffffff'),
                  transform: `translateY(${elem.baselineShift || 0}px) scaleX(${elem.scaleX !== undefined ? elem.scaleX / 100 : 1}) scaleY(${elem.scaleY !== undefined ? elem.scaleY / 100 : 1}) skewX(${elem.shear || 0}deg)`,
                  transformOrigin: elem.alignment === 'center' ? 'center center' : elem.alignment === 'right' ? 'right center' : 'left center',
                  WebkitTextStrokeWidth: `${elem.textStrokeWidth || 0}px`,
                  WebkitTextStrokeColor: elem.textStrokeColorType === 'gradient' ? (elem.textStrokeGradient?.stops?.[0]?.color || '#ffffff') : (elem.textStrokeColor || '#ffffff')
                }}>
                  {resolveElementText(elem)}
                </div>
              )}

              {elem.type === 'image' && (() => {
                const { rOuter, rGap, rPrimaryOuter, rInner } = calculateFrameBorderRadii(elem);
                return (
                  <div style={{
                    width: '100%',
                    height: '100%',
                    boxSizing: 'border-box',
                    borderRadius: `${rOuter}px`,
                    overflow: 'hidden',
                    
                    // 1. Secondary Border styling (solid, dashed, dotted)
                    border: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 !== 'gradient')
                      ? `${elem.strokeWidth2 || 2}px ${elem.strokeStyle2 || 'solid'} ${elem.strokeColor2 || '#000000'}`
                      : 'none',
                    
                    // 2. Gap/Border padding
                    padding: (elem.hasBorder !== false && elem.hasSecondBorder)
                      ? (elem.borderFillType2 === 'gradient' ? `${elem.strokeWidth2 || 2}px` : `${elem.borderGap || 0}px`)
                      : 0,
                    background: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 === 'gradient')
                      ? elem.strokeColor2 || 'linear-gradient(to right, red, blue)'
                      : 'transparent'
                  }}>
                    {/* Intermediate gap wrapper to support secondary gradient border gap */}
                    <div style={{
                      width: '100%',
                      height: '100%',
                      boxSizing: 'border-box',
                      borderRadius: `${rGap}px`,
                      padding: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 === 'gradient')
                        ? `${elem.borderGap || 0}px`
                        : 0,
                      background: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 === 'gradient')
                        ? (sideConfig.background?.value || '#ffffff')
                        : 'transparent',
                      display: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 === 'gradient')
                        ? 'flex'
                        : 'block',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {/* Inner element wrapper containing primary border */}
                      <div style={{
                        width: '100%',
                        height: '100%',
                        boxSizing: 'border-box',
                        borderRadius: `${rPrimaryOuter}px`,
                        overflow: 'hidden',
                        
                        // 3. Primary Border styling
                        border: (elem.hasBorder !== false && (elem.strokeWidth !== undefined ? elem.strokeWidth : 1) > 0 && elem.borderFillType !== 'gradient')
                          ? `${elem.strokeWidth !== undefined ? elem.strokeWidth : 1}px ${elem.strokeStyle || 'solid'} ${elem.strokeColor || '#ffffff'}`
                          : 'none',
                        
                        // 4. Primary Border Gradient padding
                        padding: (elem.hasBorder !== false && (elem.strokeWidth !== undefined ? elem.strokeWidth : 1) > 0 && elem.borderFillType === 'gradient')
                          ? `${elem.strokeWidth !== undefined ? elem.strokeWidth : 1}px`
                          : 0,
                        background: (elem.hasBorder !== false && (elem.strokeWidth !== undefined ? elem.strokeWidth : 1) > 0 && elem.borderFillType === 'gradient')
                          ? elem.strokeColor || 'linear-gradient(to right, red, blue)'
                          : (elem.fill || 'transparent'),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {/* Inner content box for image clipping */}
                        <div style={{ position: 'relative', width: '100%', height: '100%', borderRadius: `${rInner}px`, overflow: 'hidden' }}>
                          {getResolvedImageSrc(elem) ? (
                            elem.hasBorder !== false && elem.borderFillType === 'gradient' ? (
                              <div style={{ width: '100%', height: '100%', borderRadius: `${rInner}px`, overflow: 'hidden', position: 'relative', zIndex: 1 }}>
                                <PictureFrameImage
                                  elem={elem}
                                  resolvedSrc={getResolvedImageSrc(elem)}
                                  filterStyle={getImageFilterStyle(elem.effect)}
                                />
                              </div>
                            ) : (
                              <PictureFrameImage
                                elem={elem}
                                resolvedSrc={getResolvedImageSrc(elem)}
                                filterStyle={getImageFilterStyle(elem.effect)}
                              />
                            )
                          ) : (
                            <div style={{
                              width: '100%',
                              height: '100%',
                              backgroundColor: '#262629', display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'var(--text-mute)',
                              gap: '6px'
                            }}>
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.7 }}>
                                <circle cx="12" cy="12" r="10" />
                                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                                <line x1="12" y1="17" x2="12.01" y2="17" />
                              </svg>
                              <span style={{ fontSize: '9px', opacity: 0.6 }}>No Source</span>
                            </div>
                          )}
                          {/* Overlay element for gradient/sheen overlays */}
                          {getImageOverlayStyle(elem.effect) && (
                            <div style={{
                              position: 'absolute',
                              top: 0, left: 0, right: 0, bottom: 0,
                              pointerEvents: 'none',
                              borderRadius: `${rInner}px`,
                              ...getImageOverlayStyle(elem.effect), zIndex: 5
                            }} />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {elem.type === 'accent' && (
                <div style={{
                  width: (elem.dividerType === 'vertical' || elem.orientation === 'vertical') ? `${elem.strokeWidth || 2}px` : '100%',
                  height: (elem.dividerType === 'vertical' || elem.orientation === 'vertical') ? '100%' : `${elem.strokeWidth || 2}px`,
                  background: elem.fill || elem.strokeColor || '#cbd5e1'
                }} />
              )}

              {elem.type === 'shape' && (
                elem.shapeType === 'triangle' ? (
                  <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
                    {elem.fillType === 'pattern' && elem.pattern && (
                      <defs>
                        <pattern
                          id={`pat-canvas-${elem.id}`}
                          patternUnits="userSpaceOnUse"
                          width={Math.max(2, Math.round((elem.pattern.tileWidth || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                          height={Math.max(2, Math.round((elem.pattern.tileHeight || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                        >
                          {elem.pattern.tintColor && elem.pattern.tintColor !== 'transparent' && (
                            <rect width="100%" height="100%" fill={elem.pattern.tintColor} />
                          )}
                          <image
                            href={elem.pattern.image}
                            width={Math.max(2, Math.round((elem.pattern.tileWidth || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                            height={Math.max(2, Math.round((elem.pattern.tileHeight || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                            opacity={elem.pattern.opacity !== undefined ? (elem.pattern.opacity > 1 ? elem.pattern.opacity / 100 : elem.pattern.opacity) : 1}
                          />
                        </pattern>
                      </defs>
                    )}
                    <polygon points="50,0 0,100 100,100" fill={elem.fillType === 'pattern' && elem.pattern ? `url(#pat-canvas-${elem.id})` : (elem.fill || '#e6e6e6')} />
                  </svg>
                ) : elem.shapeType === 'diamond' ? (
                  <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
                    {elem.fillType === 'pattern' && elem.pattern && (
                      <defs>
                        <pattern
                          id={`pat-canvas-${elem.id}`}
                          patternUnits="userSpaceOnUse"
                          width={Math.max(2, Math.round((elem.pattern.tileWidth || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                          height={Math.max(2, Math.round((elem.pattern.tileHeight || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                        >
                          {elem.pattern.tintColor && elem.pattern.tintColor !== 'transparent' && (
                            <rect width="100%" height="100%" fill={elem.pattern.tintColor} />
                          )}
                          <image
                            href={elem.pattern.image}
                            width={Math.max(2, Math.round((elem.pattern.tileWidth || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                            height={Math.max(2, Math.round((elem.pattern.tileHeight || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                            opacity={elem.pattern.opacity !== undefined ? (elem.pattern.opacity > 1 ? elem.pattern.opacity / 100 : elem.pattern.opacity) : 1}
                          />
                        </pattern>
                      </defs>
                    )}
                    <polygon points="50,0 100,50 50,100 0,50" fill={elem.fillType === 'pattern' && elem.pattern ? `url(#pat-canvas-${elem.id})` : (elem.fill || '#e6e6e6')} />
                  </svg>
                ) : elem.shapeType === 'polygon' ? (
                  <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
                    {elem.fillType === 'pattern' && elem.pattern && (
                      <defs>
                        <pattern
                          id={`pat-canvas-${elem.id}`}
                          patternUnits="userSpaceOnUse"
                          width={Math.max(2, Math.round((elem.pattern.tileWidth || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                          height={Math.max(2, Math.round((elem.pattern.tileHeight || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                        >
                          {elem.pattern.tintColor && elem.pattern.tintColor !== 'transparent' && (
                            <rect width="100%" height="100%" fill={elem.pattern.tintColor} />
                          )}
                          <image
                            href={elem.pattern.image}
                            width={Math.max(2, Math.round((elem.pattern.tileWidth || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                            height={Math.max(2, Math.round((elem.pattern.tileHeight || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                            opacity={elem.pattern.opacity !== undefined ? (elem.pattern.opacity > 1 ? elem.pattern.opacity / 100 : elem.pattern.opacity) : 1}
                          />
                        </pattern>
                      </defs>
                    )}
                    <polygon points="25,0 75,0 100,50 75,100 25,100 0,50" fill={elem.fillType === 'pattern' && elem.pattern ? `url(#pat-canvas-${elem.id})` : (elem.fill || '#e6e6e6')} />
                  </svg>
                ) : elem.shapeType === 'star' ? (
                  <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
                    {elem.fillType === 'pattern' && elem.pattern && (
                      <defs>
                        <pattern
                          id={`pat-canvas-${elem.id}`}
                          patternUnits="userSpaceOnUse"
                          width={Math.max(2, Math.round((elem.pattern.tileWidth || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                          height={Math.max(2, Math.round((elem.pattern.tileHeight || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                        >
                          {elem.pattern.tintColor && elem.pattern.tintColor !== 'transparent' && (
                            <rect width="100%" height="100%" fill={elem.pattern.tintColor} />
                          )}
                          <image
                            href={elem.pattern.image}
                            width={Math.max(2, Math.round((elem.pattern.tileWidth || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                            height={Math.max(2, Math.round((elem.pattern.tileHeight || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                            opacity={elem.pattern.opacity !== undefined ? (elem.pattern.opacity > 1 ? elem.pattern.opacity / 100 : elem.pattern.opacity) : 1}
                          />
                        </pattern>
                      </defs>
                    )}
                    <polygon points="50,0 61,35 98,35 68,57 79,91 50,70 21,91 32,57 2,35 39,35" fill={elem.fillType === 'pattern' && elem.pattern ? `url(#pat-canvas-${elem.id})` : (elem.fill || '#e6e6e6')} />
                  </svg>
                ) : (
                  // Rectangle, Circle, Pill with border styling
                  <div style={{
                    width: '100%',
                    height: '100%',
                    boxSizing: 'border-box',
                    borderRadius: elem.shapeType === 'circle' ? '50%' : elem.shapeType === 'pill' ? '999px' : `${elem.hasBorder !== false ? (elem.borderRadius !== undefined ? elem.borderRadius : 0) : 0}px`,
                    overflow: 'hidden',
                    
                    // 1. Secondary Border styling (Dotted, dashed, solid/line)
                    border: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 !== 'gradient')
                      ? `${elem.strokeWidth2 || 2}px ${elem.strokeStyle2 || 'solid'} ${elem.strokeColor2 || '#000000'}`
                      : 'none',
                    
                    // 2. Gap/Border padding
                    padding: (elem.hasBorder !== false && elem.hasSecondBorder)
                      ? (elem.borderFillType2 === 'gradient' ? `${elem.strokeWidth2 || 2}px` : `${elem.borderGap || 0}px`)
                      : 0,
                    background: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 === 'gradient')
                      ? elem.strokeColor2 || 'linear-gradient(to right, red, blue)'
                      : 'transparent'
                  }}>
                    {/* Intermediate gap wrapper to support secondary gradient border gap */}
                    <div style={{
                      width: '100%',
                      height: '100%',
                      boxSizing: 'border-box',
                      borderRadius: 'inherit',
                      padding: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 === 'gradient')
                        ? `${elem.borderGap || 0}px`
                        : 0,
                      background: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 === 'gradient')
                        ? (sideConfig.background?.value || '#ffffff')
                        : 'transparent',
                      display: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 === 'gradient')
                        ? 'flex'
                        : 'block',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {/* Inner element wrapper that contains the primary border and fill background */}
                      <div style={{
                        width: '100%',
                        height: '100%',
                        boxSizing: 'border-box',
                        borderRadius: 'inherit', // inherits parent border radius
                        flexShrink: 0,
                        position: 'relative',
                        overflow: 'hidden',
                        
                        // 3. Primary Border styling (only if not gradient border fill)
                        border: (elem.hasBorder !== false && elem.borderFillType !== 'gradient') 
                          ? `${elem.strokeWidth || 0}px ${elem.strokeStyle || 'solid'} ${elem.strokeColor || '#cbd5e1'}` 
                          : 'none',
                        
                        // 4. Primary Border Gradient padding
                        padding: (elem.hasBorder !== false && elem.borderFillType === 'gradient')
                          ? `${elem.strokeWidth || 0}px`
                          : 0,
                        background: (elem.hasBorder !== false && elem.borderFillType === 'gradient')
                          ? elem.strokeColor || 'linear-gradient(to right, red, blue)'
                          : (elem.fillType === 'pattern' ? (elem.pattern?.tintColor || '#e6e6e6') : (elem.fill || '#e6e6e6'))
                      }}>
                        {/* Content box inside primary gradient border */}
                        {elem.hasBorder !== false && elem.borderFillType === 'gradient' && (
                          <div style={{
                            width: '100%',
                            height: '100%',
                            position: 'relative',
                            overflow: 'hidden',
                            background: elem.fillType === 'pattern' ? (elem.pattern?.tintColor || '#e6e6e6') : (elem.fill || '#e6e6e6'),
                            borderRadius: 'inherit'
                          }}>
                            {elem.fillType === 'pattern' && elem.pattern && (
                              <div style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                borderRadius: 'inherit',
                                pointerEvents: 'none',
                                opacity: elem.pattern.opacity !== undefined ? (elem.pattern.opacity > 1 ? elem.pattern.opacity / 100 : elem.pattern.opacity) : 1,
                                backgroundImage: `url("${elem.pattern.image}")`,
                                backgroundRepeat: elem.pattern.repeat || 'repeat',
                                backgroundSize: `${Math.max(2, Math.round((elem.pattern.tileWidth || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}px ${Math.max(2, Math.round((elem.pattern.tileHeight || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}px`,
                                backgroundPosition: '0 0',
                                mixBlendMode: elem.pattern.blendMode || 'normal'
                              }} />
                            )}
                          </div>
                        )}
                        {!(elem.hasBorder !== false && elem.borderFillType === 'gradient') && elem.fillType === 'pattern' && elem.pattern && (
                          <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            borderRadius: 'inherit',
                            pointerEvents: 'none',
                            opacity: elem.pattern.opacity !== undefined ? (elem.pattern.opacity > 1 ? elem.pattern.opacity / 100 : elem.pattern.opacity) : 1,
                            backgroundImage: `url("${elem.pattern.image}")`,
                            backgroundRepeat: elem.pattern.repeat || 'repeat',
                            backgroundSize: `${Math.max(2, Math.round((elem.pattern.tileWidth || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}px ${Math.max(2, Math.round((elem.pattern.tileHeight || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}px`,
                            backgroundPosition: '0 0',
                            mixBlendMode: elem.pattern.blendMode || 'normal'
                          }} />
                        )}
                      </div>
                    </div>
                  </div>
                )
              )}

              {elem.type === 'certify' && renderCertifyElement(elem)}

              {elem.type === 'datagrid' && (
                <div style={{
                  width: '100%',
                  height: '100%',
                  boxSizing: 'border-box',
                  borderRadius: elem.hasBorder !== false ? `${elem.borderRadius || 0}px` : 0,
                  overflow: 'hidden',
                  border: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 !== 'gradient')
                    ? `${elem.strokeWidth2 || 2}px ${elem.strokeStyle2 || 'solid'} ${elem.strokeColor2 || '#000000'}`
                    : 'none',
                  padding: (elem.hasBorder !== false && elem.hasSecondBorder)
                    ? (elem.borderFillType2 === 'gradient' ? `${elem.strokeWidth2 || 2}px` : `${elem.borderGap || 0}px`)
                    : 0,
                  background: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 === 'gradient')
                    ? elem.strokeColor2 || 'linear-gradient(to right, red, blue)'
                    : 'transparent'
                }}>
                  <div style={{
                    width: '100%',
                    height: '100%',
                    boxSizing: 'border-box',
                    borderRadius: 'inherit',
                    padding: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 === 'gradient')
                      ? `${elem.borderGap || 0}px`
                      : 0,
                    background: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 === 'gradient')
                      ? (sideConfig.background?.value || '#ffffff')
                      : 'transparent',
                    display: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 === 'gradient')
                      ? 'flex'
                      : 'block',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <div style={{
                      width: '100%',
                      height: '100%',
                      boxSizing: 'border-box',
                      borderRadius: 'inherit',
                      overflow: 'hidden',
                      flexShrink: 0,
                      border: (elem.hasBorder !== false && elem.borderFillType !== 'gradient')
                        ? `${elem.strokeWidth !== undefined ? elem.strokeWidth : 1}px ${elem.strokeStyle || 'solid'} ${elem.strokeColor || '#ffffff'}`
                        : 'none',
                      padding: (elem.hasBorder !== false && elem.borderFillType === 'gradient')
                        ? `${elem.strokeWidth !== undefined ? elem.strokeWidth : 1}px`
                        : 0,
                      background: (elem.hasBorder !== false && elem.borderFillType === 'gradient')
                        ? elem.strokeColor || 'linear-gradient(to right, red, blue)'
                        : 'transparent',
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                      <div style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        boxSizing: 'border-box',
                        fontFamily: elem.fontFamily || 'Arial',
                        fontSize: `${elem.fontSize || 11}px`,
                        letterSpacing: `${elem.letterSpacing || 0}px`,
                        lineHeight: elem.lineHeight || 1.1,
                        background: elem.fillType === 'gradient'
                          ? (elem.fill || 'linear-gradient(90deg, rgba(255,255,255,0.15) 0%, rgba(0,0,0,0.2) 100%)')
                          : (elem.fillType === 'pattern' ? (elem.pattern?.tintColor || 'rgba(0,0,0,0.06)') : (elem.fill || 'rgba(0,0,0,0.06)')),
                        overflow: 'hidden',
                        borderRadius: 'inherit',
                        position: 'relative'
                      }}>
                        {elem.fillType === 'pattern' && elem.pattern && (
                          <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            borderRadius: 'inherit',
                            pointerEvents: 'none',
                            opacity: elem.pattern.opacity !== undefined ? (elem.pattern.opacity > 1 ? elem.pattern.opacity / 100 : elem.pattern.opacity) : 1,
                            backgroundImage: `url("${elem.pattern.image}")`,
                            backgroundRepeat: elem.pattern.repeat || 'repeat',
                            backgroundSize: `${Math.max(2, Math.round((elem.pattern.tileWidth || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}px ${Math.max(2, Math.round((elem.pattern.tileHeight || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}px`,
                            backgroundPosition: '0 0',
                            mixBlendMode: elem.pattern.blendMode || 'normal',
                            zIndex: 0
                          }} />
                        )}
                        {elem.headerEnabled && (
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            background: elem.headerFillType === 'gradient' ? (elem.headerFill || 'linear-gradient(90deg, rgba(255,255,255,0.15) 0%, rgba(0,0,0,0.2) 100%)') : (elem.headerFill || 'rgba(255,255,255,0.1)'),
                            color: elem.headerColor || '#ffffff',
                            fontWeight: 'bold',
                            paddingTop: `${elem.rowPadding !== undefined ? elem.rowPadding : 4}px`,
                            paddingBottom: `${elem.rowPadding !== undefined ? elem.rowPadding : 4}px`,
                            paddingLeft: `${elem.colPadding !== undefined ? elem.colPadding : 6}px`,
                            paddingRight: `${elem.colPadding !== undefined ? elem.colPadding : 6}px`,
                            borderBottom: (elem.gridLinesEnabled !== false)
                              ? `${elem.gridLineThickness !== undefined ? elem.gridLineThickness : 1}px ${elem.gridLineStyle || 'solid'} ${elem.gridLineColor || 'rgba(255,255,255,0.08)'}`
                              : 'none'
                          }}>
                            <span style={{ width: `${elem.colWidthRatio || 50}%`, textAlign: elem.labelAlign || 'left', boxSizing: 'border-box' }}>
                              {elem.headerLabelText || 'Attribute'}
                            </span>
                            <span style={{ width: `${100 - (elem.colWidthRatio || 50)}%`, textAlign: elem.valueAlign || 'right', boxSizing: 'border-box' }}>
                              {elem.headerValueText || 'Value'}
                            </span>
                          </div>
                        )}

                        {(elem.rows || []).map((row, idx) => (
                          <div key={idx} style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            backgroundColor: (elem.zebraEnabled && idx % 2 === 1) ? (elem.zebraColor || 'rgba(255,255,255,0.03)') : 'transparent',
                            paddingTop: `${elem.rowPadding !== undefined ? elem.rowPadding : 4}px`,
                            paddingBottom: `${elem.rowPadding !== undefined ? elem.rowPadding : 4}px`,
                            paddingLeft: `${elem.colPadding !== undefined ? elem.colPadding : 6}px`,
                            paddingRight: `${elem.colPadding !== undefined ? elem.colPadding : 6}px`,
                            borderBottom: (elem.gridLinesEnabled !== false && idx < elem.rows.length - 1)
                              ? `${elem.gridLineThickness !== undefined ? elem.gridLineThickness : 1}px ${elem.gridLineStyle || 'solid'} ${elem.gridLineColor || 'rgba(255,255,255,0.08)'}`
                              : 'none',
                            boxSizing: 'border-box'
                          }}>
                             <span style={{
                              width: `${elem.colWidthRatio || 50}%`,
                              textAlign: elem.labelAlign || 'left',
                              fontSize: `${elem.labelFontSize || elem.fontSize || 11}px`,
                              fontFamily: elem.labelFontFamily || elem.fontFamily || 'Arial',
                              fontWeight: elem.labelFontWeight || elem.fontWeight || '400',
                              fontStyle: elem.labelFontStyle || elem.fontStyle || 'normal',
                              textDecoration: elem.labelDecoration || elem.textDecoration || 'none',
                              letterSpacing: `${elem.labelLetterSpacing !== undefined ? elem.labelLetterSpacing : (elem.letterSpacing || 0)}px`,
                              background: elem.labelColorType === 'gradient' ? (elem.labelColor || 'linear-gradient(90deg, #ffffff 0%, #000000 100%)') : 'transparent',
                              WebkitBackgroundClip: elem.labelColorType === 'gradient' ? 'text' : 'unset',
                              WebkitTextFillColor: elem.labelColorType === 'gradient' ? 'transparent' : 'unset',
                              color: elem.labelColorType === 'gradient' ? 'transparent' : (elem.labelColor || elem.color || '#ffffff'),
                              display: elem.labelColorType === 'gradient' ? 'inline-block' : 'block',
                              boxSizing: 'border-box',
                              paddingRight: '4px',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>{applyTextTransform(row[0], elem.labelTransformModifier)}</span>
                            <span style={{
                              width: `${100 - (elem.colWidthRatio || 50)}%`,
                              textAlign: elem.valueAlign || 'right',
                              fontSize: `${elem.valueFontSize || elem.fontSize || 11}px`,
                              fontFamily: elem.valueFontFamily || elem.fontFamily || 'Arial',
                              fontWeight: elem.valueFontWeight || (elem.fontWeight ? (parseInt(elem.fontWeight) >= 700 ? elem.fontWeight : '700') : '700'),
                              fontStyle: elem.valueFontStyle || elem.fontStyle || 'normal',
                              textDecoration: elem.valueDecoration || elem.textDecoration || 'none',
                              letterSpacing: `${elem.valueLetterSpacing !== undefined ? elem.valueLetterSpacing : (elem.letterSpacing || 0)}px`,
                              background: elem.valueColorType === 'gradient' ? (elem.valueColor || 'linear-gradient(90deg, #ffffff 0%, #000000 100%)') : 'transparent',
                              WebkitBackgroundClip: elem.valueColorType === 'gradient' ? 'text' : 'unset',
                              WebkitTextFillColor: elem.valueColorType === 'gradient' ? 'transparent' : 'unset',
                              color: elem.valueColorType === 'gradient' ? 'transparent' : (elem.valueColor || elem.color || '#ffffff'),
                              display: elem.valueColorType === 'gradient' ? 'inline-block' : 'block',
                              boxSizing: 'border-box',
                              paddingLeft: '4px',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>{applyTextTransform(resolveTemplateVariables(row[1]), elem.valueTransformModifier)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {elem.type === 'qrcode' && (
                <div style={{
                  width: '100%',
                  height: '100%',
                  boxSizing: 'border-box',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: `${elem.borderRadius !== undefined ? elem.borderRadius : 6}px`,
                  overflow: 'hidden',
                  backgroundColor: elem.bgColor === 'transparent' ? 'transparent' : (elem.bgColor || '#ffffff'),
                  border: (elem.hasBorder && elem.strokeWidth) ? `${elem.strokeWidth}px ${elem.strokeStyle || 'solid'} ${elem.strokeColor || '#cbd5e1'}` : 'none',
                  padding: `${elem.padding || 0}px`
                }}>
                  <QRCodeSVG
                    value={resolveQrCodeValue(elem, testDataOverrides, datasets, user, orgData, tenantConfig)}
                    size={Math.min(elem.width, elem.height)}
                    fgColor={elem.fgColor || '#000000'}
                    bgColor={elem.bgColor === 'transparent' ? 'transparent' : (elem.bgColor || '#ffffff')}
                    margin={elem.margin !== undefined ? elem.margin : 2}
                    style={{ width: '100%', height: '100%', borderRadius: 'inherit' }}
                  />
                </div>
              )}
            </div>
          );
        })}
    </div>
  );
};

export default function DesignStudio({ 
  user, 
  tenantConfig, 
  initialTab = 'coin', 
  hideTabHeaders = false,
  initialCoinId = null,
  initialProofingDesignId = null
}) {
  const token = sessionStorage.getItem('token') || localStorage.getItem('token');
  const isDev = window.location.port === '3000';
  const API_BASE = isDev ? `http://${window.location.hostname}:8000` : '';
  const modal = useModal();

  // Core User Roles
  const isGlobalRole = ["GLOBAL_ADMIN", "GLOBAL_POWER", "GLOBAL_STANDARD"].includes(user.role);
  const isTenantAdmin = user.role === "TENANT_ADMIN";
  const isOperator = user.role === "OPERATOR";

  // Context Selection States
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(''); // Tenant ID
  const [allUsers, setAllUsers] = useState([]);
  const [operators, setOperators] = useState([]);
  const [selectedUserContext, setSelectedUserContext] = useState('self'); // 'self' or User ID (string)

  const formatScopeUserLabel = (op) => {
    if (!op) return '';
    const org = (op.organization_name || op.company_name || '').trim();
    if (org) {
      return org;
    }
    const userName = (
      op.name ||
      (op.first_name || op.last_name ? `${op.first_name || ''} ${op.last_name || ''}`.trim() : '') ||
      op.email ||
      ''
    ).trim();
    return userName || 'Unknown Operator';
  };

  // Loaded/Saved Designs States
  const [designs, setDesigns] = useState([]);
  const [selectedDesignId, setSelectedDesignId] = useState(null);
  const [designName, setDesignName] = useState('My Custom Coin');
  const [isDesignTemplate, setIsDesignTemplate] = useState(false);

  // Coin Sculptor States
  const [metal, setMetal] = useState('gold');
  const [faceSide, setFaceSide] = useState('heads'); // 'heads' | 'tails'
  const [symbol, setSymbol] = useState('');
  const [upperText, setUpperText] = useState('TROPHYCOIN OFFICIAL');
  const [lowerText, setLowerText] = useState('MINTED PROTOCOL');
  const [textMode, setTextMode] = useState('split'); // 'split' | 'single'
  const [singleText, setSingleText] = useState('TROPHYCOIN OFFICIAL MINTED PROTOCOL');
  const [textFontWeight, setTextFontWeight] = useState('900');
  const [textFontStyle, setTextFontStyle] = useState('normal');
  const [textCase, setTextCase] = useState('uppercase');
  const [edgeStyle, setEdgeStyle] = useState('reeded');
  const [depth, setDepth] = useState('medium'); // low, medium, high
  const [isFullscreenFaceEditorOpen, setIsFullscreenFaceEditorOpen] = useState(false);
  const [faceTemplatesVersion, setFaceTemplatesVersion] = useState(0);
  const [coinViewMode, setCoinViewMode] = useState('grid'); // 'grid' | 'proofer'
  const [selectedCoin, setSelectedCoin] = useState(null); // PhysicalCoin
  const [selectedGridCoinId, setSelectedGridCoinId] = useState(null);
  const [adminCoinFilter, setAdminCoinFilter] = useState('normal'); // 'normal' | 'system' | 'archived' | 'all'
  const [physicalCoins, setPhysicalCoins] = useState([]);
  const [isSetupWizardOpen, setIsSetupWizardOpen] = useState(false);
  const [isCoin3DPreviewOpen, setIsCoin3DPreviewOpen] = useState(false);
  const [coinBodyColor, setCoinBodyColor] = useState('#1A1A1A'); // default body color
  const [coinAccentColor, setCoinAccentColor] = useState('#D4AF37'); // default accent color
  const [coinInvert, setCoinInvert] = useState(false); // whether to invert coin faces
  const [wizardHeadsDesignId, setWizardHeadsDesignId] = useState('');
  const [wizardTailsDesignId, setWizardTailsDesignId] = useState('');
  const [wizardCoinName, setWizardCoinName] = useState('');
  const [wizardSelectingFace, setWizardSelectingFace] = useState(null); // null | 'heads' | 'tails'
  const [wizardIsSystemCoin, setWizardIsSystemCoin] = useState(false);
  const [wizardBodyFilament, setWizardBodyFilament] = useState('');
  const [wizardAccentFilament, setWizardAccentFilament] = useState('');
  const [availableProductionFilaments, setAvailableProductionFilaments] = useState([]);
  const [coinChatMessages, setCoinChatMessages] = useState([]);
  const [coinChatMessage, setCoinChatMessage] = useState('');
  const [coinSupportTab, setCoinSupportTab] = useState('chat'); // 'chat' | 'files'
  const [textGrayscale, setTextGrayscale] = useState(60); // 0 (white) to 100 (black)
  const chatContainerRef = useRef(null);
  const [textRadius, setTextRadius] = useState(68); // circular radius (45 to 90)
  const [textSize, setTextSize] = useState(16); // text font size (14 to 30)
  const [textKerning, setTextKerning] = useState(1.2); // letter spacing (0 to 5)
  const [textFont, setTextFont] = useState('"Arial Black", sans-serif'); // active font family
  const [rings, setRings] = useState([
    {
      id: 'typography-layer',
      name: 'Typography',
      type: 'text',
      visible: true,
      locked: false
    },
    {
      id: 1,
      name: 'Outer Rim',
      outerRadiusX: 95,
      outerRadiusY: 95,
      innerRadiusX: 85,
      innerRadiusY: 85,
      offsetX: 0,
      offsetY: 0,
      grayscale: 60,
      outerBlur: 0,
      innerBlur: 0,
      noise: 0,
      noiseSize: 50,
      patternUrl: '',
      locked: false,
      constrainOuter: true,
      constrainInner: true,
      lockOffset: false
    },
    {
      id: 2,
      name: 'Inner Base',
      outerRadiusX: 84,
      outerRadiusY: 84,
      innerRadiusX: 0,
      innerRadiusY: 0,
      offsetX: 0,
      offsetY: 0,
      grayscale: 100,
      outerBlur: 0,
      innerBlur: 0,
      noise: 0,
      noiseSize: 50,
      patternUrl: '',
      locked: false,
      constrainOuter: true,
      constrainInner: true,
      lockOffset: false
    }
  ]);
  const [selectedRingId, setSelectedRingId] = useState(1);




  // Dynamic Ring Stack operations
  const addRing = () => {
    const numericIds = rings.map(r => Number(r.id)).filter(id => !isNaN(id));
    const nextId = numericIds.length > 0 ? Math.max(...numericIds) + 1 : 1;
    const newRing = {
      id: nextId,
      name: `Ring Layer ${nextId}`,
      outerRadiusX: 80,
      outerRadiusY: 80,
      innerRadiusX: 0,
      innerRadiusY: 0,
      offsetX: 0,
      offsetY: 0,
      grayscale: 50,
      outerBlur: 0,
      innerBlur: 0,
      noise: 0,
      noiseSize: 50,
      patternUrl: '',
      locked: false,
      constrainOuter: true,
      constrainInner: true,
      lockOffset: false
    };
    setRings([newRing, ...rings]);
    setSelectedRingId(newRing.id);
  };

  const handleAssetDoubleClick = (imageUrl, filename) => {
    const numericIds = rings.map(r => Number(r.id)).filter(id => !isNaN(id));
    const nextId = numericIds.length > 0 ? Math.max(...numericIds) + 1 : 1;
    const newLayer = {
      id: nextId,
      name: filename ? (filename.includes('.') ? (filename.split('.').slice(0, -1).join('.') || filename) : filename) : `Image Layer ${nextId}`,
      type: 'image',
      imageUrl: imageUrl,
      offsetX: 0,
      offsetY: 0,
      width: 160,
      height: 160,
      opacity: 100,
      maskRadius: 80,
      gaussianBlur: 0,
      constrainSize: true,
      locked: false
    };
    setRings([newLayer, ...rings]);
    setSelectedRingId(nextId);
  };

  const deleteRing = (ringId) => {
    if (rings.length <= 1) {
      alert("Cannot delete the last layer. You need at least one layer to render.");
      return;
    }
    const updated = rings.filter(r => r.id !== ringId);
    setRings(updated);
    if (selectedRingId === ringId) {
      setSelectedRingId(updated[0].id);
    }
  };

  const moveRingUp = (index) => {
    if (index === 0) return;
    const updated = [...rings];
    const temp = updated[index];
    updated[index] = updated[index - 1];
    updated[index - 1] = temp;
    setRings(updated);
  };

  const moveRingDown = (index) => {
    if (index === rings.length - 1) return;
    const updated = [...rings];
    const temp = updated[index];
    updated[index] = updated[index + 1];
    updated[index + 1] = temp;
    setRings(updated);
  };





  const updateSelectedRing = (field, value) => {
    setRings(rings.map(r => {
      if (r.id === selectedRingId) {
        if (r.locked) return r;
        
        let updated = { ...r, [field]: value };
        
        // Auto-scale height and maskRadius for image types when width/height changes
        if (r.type === 'image') {
          const constrain = r.constrainSize !== false;
          if (field === 'width') {
            if (constrain) {
              updated.height = value;
              updated.maskRadius = Math.round(value / 2);
            }
          } else if (field === 'height') {
            if (constrain) {
              updated.width = value;
              updated.maskRadius = Math.round(value / 2);
            }
          } else if (field === 'constrainSize' && value === true) {
            updated.height = r.width;
            updated.maskRadius = Math.round(r.width / 2);
          }
        }
        
        // Handle constrained outer radius X/Y
        if (updated.constrainOuter) {
          if (field === 'outerRadiusX') {
            updated.outerRadiusY = value;
          } else if (field === 'outerRadiusY') {
            updated.outerRadiusX = value;
          } else if (field === 'constrainOuter' && value === true) {
            updated.outerRadiusY = updated.outerRadiusX;
          }
        }
        
        // Handle constrained inner radius X/Y
        if (updated.constrainInner) {
          if (field === 'innerRadiusX') {
            updated.innerRadiusY = value;
          } else if (field === 'innerRadiusY') {
            updated.innerRadiusX = value;
          } else if (field === 'constrainInner' && value === true) {
            updated.innerRadiusY = updated.innerRadiusX;
          }
        }
        
        return updated;
      }
      return r;
    }));
  };

  // Workspace UI Layout Mode States
  const [studioAssets, setStudioAssets] = useState([]);
  const [assetsSearch, setAssetsSearch] = useState('');
  const [assetsType, setAssetsType] = useState('all');

  // Brag Page Stylist States (Digital Brag Card Designer)
  const [studioTab, setStudioTab] = useState(initialTab); // 'coin' or 'brag'
 // gold, holo, silver, obsidian







  
  // Front Data Fields














  // Back Data Fields






  // Trophy Cards workflow states
  const [bragViewMode, setBragViewMode] = useState('grid'); // 'grid', 'wizard', 'proofer'
  const [proofingDesign, setProofingDesign] = useState(null);
  const [orders, setOrders] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [chatLoading, setChatLoading] = useState(false);
  const [newMessage, setNewMessage] = useState('');

  
  // Wizard setup states
  const [wizardStep, setWizardStep] = useState(1);
  const [selectedBasisDesign, setSelectedBasisDesign] = useState(null);
  const [selectedOrderSlot, setSelectedOrderSlot] = useState(null);
  const [wizardBrief, setWizardBrief] = useState('');
  const [activeCarouselIndex, setActiveCarouselIndex] = useState(0);
  
  // Asset attachments inside Wizard/Chat
  const [selectedAssetsForBrief, setSelectedAssetsForBrief] = useState([]); 
  const [showColorPickerForBrief, setShowColorPickerForBrief] = useState(false);
  const [colorPickerHex, setColorPickerHex] = useState('#ff0000');
  const [showAssetSelectorForBrief, setShowAssetSelectorForBrief] = useState(false);

  // eslint-disable-next-line no-unused-vars
  const [formErrors, setFormErrors] = useState({});

  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewFlipped, setPreviewFlipped] = useState(false);
  const [previewDesign, setPreviewDesign] = useState(null);

  // Brag Page sub-tab state
  // eslint-disable-next-line no-unused-vars
  const [bragSubTab, setBragSubTab] = useState('data'); // 'data', 'template', 'layout', 'style'

  // Card Proofer States
  const [prooferSide, setProoferSide] = useState('front');
  const [prooferZoom, setProoferZoom] = useState(1.0);
  const [prooferRightTab, setProoferRightTab] = useState('chat'); // 'chat' | 'test-data'
  const [prooferEditingDesignId, setProoferEditingDesignId] = useState(null);
  const [isProofer3DOpen, setIsProofer3DOpen] = useState(false);
  const [isProoferApprovalOpen, setIsProoferApprovalOpen] = useState(false);
  const [isCoinApprovalOpen, setIsCoinApprovalOpen] = useState(false);
  const [showArchivedCards, setShowArchivedCards] = useState(false);
  const [showArchivedCoins, setShowArchivedCoins] = useState(false);
  const [testDataOverrides, setTestDataOverrides] = useState({});
  const [datasets, setDatasets] = useState({});
  const [collapsedProoferDatasets, setCollapsedProoferDatasets] = useState({});
  const [orgData, setOrgData] = useState(null);

  useEffect(() => {
    if (token && user?.organization_id) {
      fetch(`${API_BASE}/api/v1/organizations/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      })
        .then(res => res.ok ? res.json() : null)
        .then(data => {
          if (data) setOrgData(data);
        })
        .catch(err => console.log('No organization found in DesignStudio', err));
    }
  }, [token, API_BASE, user?.organization_id]);

  // Brag Page Custom Layout States






 // 'left', 'center', 'right'
 // 'classic', 'minimalist', 'sidebyside'

  // Brag Page Custom Style States





















  // Brag Page Certify State Variables

 // 'ribbon', 'stamp', 'watermark'







 // 'normal', 'overlay', 'screen', 'difference'

  // General Status Messages
  const [saveStatus, setSaveStatus] = useState('');
  const [statusError, setStatusError] = useState('');

  // Universal Top Banner Slide-in Notification Observers
  useEffect(() => {
    if (saveStatus) modal.notify(saveStatus, 'success');
  }, [saveStatus, modal]);

  useEffect(() => {
    if (statusError) modal.notify(statusError, 'error');
  }, [statusError, modal]);



  // 1. Load Tenants (if Global Admin)
  useEffect(() => {
    if (isGlobalRole && token) {
      fetch(`${API_BASE}/api/v1/users/tenants`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
        .then(res => res.json())
        .then(data => {
          if (Array.isArray(data)) {
            setTenants(data);
            if (data.length > 0) {
              // Pre-select current active tenant
              const current = data.find(t => t.domain_name === window.location.hostname);
              setSelectedTenant(current ? current.id.toString() : data[0].id.toString());
            }
          }
        })
        .catch(err => console.error("Failed to load tenants list", err));
    } else {
      setSelectedTenant(tenantConfig?.id?.toString() || '');
    }
  }, [isGlobalRole, token, tenantConfig, API_BASE]);

  // 2. Load Users/Operators for the active tenant
  useEffect(() => {
    if ((isGlobalRole || isTenantAdmin) && token) {
      fetch(`${API_BASE}/api/v1/users`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      })
        .then(res => res.json())
        .then(data => {
          if (Array.isArray(data)) {
            setAllUsers(data);
          }
        })
        .catch(err => {
          console.error("Failed to load users list", err);
        });
    }
  }, [isGlobalRole, isTenantAdmin, token, API_BASE]);

  // 3. Filter Operators based on chosen Tenant context and group by organization
  useEffect(() => {
    if (allUsers.length > 0) {
      let filtered = [];
      if (isGlobalRole && selectedTenant) {
        filtered = allUsers.filter(u => u.tenant_id === parseInt(selectedTenant) && u.role === 'OPERATOR');
      } else if (isTenantAdmin) {
        filtered = allUsers.filter(u => u.role === 'OPERATOR');
      }

      // Group by organization so multiple users in the same organization appear as one organization scope
      const groupedScopes = [];
      const seenOrgs = new Set();
      for (const op of filtered) {
        const org = (op.organization_name || op.company_name || '').trim();
        if (org) {
          const orgKey = op.organization_id ? `id_${op.organization_id}` : `name_${org.toLowerCase()}`;
          if (!seenOrgs.has(orgKey)) {
            seenOrgs.add(orgKey);
            groupedScopes.push({
              ...op,
              displayName: org
            });
          }
        } else {
          groupedScopes.push(op);
        }
      }
      setOperators(groupedScopes);
    }
  }, [allUsers, selectedTenant, isGlobalRole, isTenantAdmin]);

  // 3b. Fetch Assets for Design Studio usage
  function fetchStudioAssets() {
    if (!token) return Promise.resolve([]);
    let url = `${API_BASE}/api/upload?`;
    const params = new URLSearchParams();
    if (selectedUserContext && selectedUserContext !== 'self') {
      params.append('associated_user_id', selectedUserContext);
    }
    if (selectedTenant) {
      params.append('tenant_id', selectedTenant);
    }
    url += params.toString();

    return fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-Tenant-Domain': window.location.hostname
      }
    })
      .then(res => res.ok ? res.json() : [])
      .then(data => {
        setStudioAssets(data);
        return data;
      })
      .catch(err => {
        console.error("Failed to fetch assets for studio", err);
        return [];
      });
  }

  // Register onto window context for dynamic uploads success triggers
  if (typeof window !== 'undefined') {
    window.fetchStudioAssets = fetchStudioAssets;
  }

  useEffect(() => {
    fetchStudioAssets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedTenant, selectedUserContext]);

  // 4. Fetch Designs based on current scope selections
  const fetchDesigns = async () => {
    if (!token) return;
    
    const targetDesignType = (studioTab === 'coin' || studioTab === 'face_templates' || isFullscreenFaceEditorOpen) ? 'coin_face' : 'brag_page';
    let url = `${API_BASE}/api/v1/designs?design_type=${targetDesignType}`;

    if (isOperator) {
      // Locked to self
      url += `&associated_user_id=${user.id}`;
    } else if (isTenantAdmin) {
      if (selectedUserContext !== 'self') {
        url += `&associated_user_id=${selectedUserContext}`;
      } else {
        url += `&associated_user_id=${user.id}`;
      }
    } else if (isGlobalRole) {
      if (selectedTenant) {
        url += `&tenant_id=${selectedTenant}`;
      }
      if (selectedUserContext !== 'self') {
        url += `&associated_user_id=${selectedUserContext}`;
      }
    }

    try {
      const res = await fetch(url, {
        cache: 'no-store',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      });
      if (!res.ok) throw new Error("Failed to fetch designs");
      const data = await res.json();
      if (Array.isArray(data)) {
        setDesigns(data);
      }
      return data;
    } catch (err) {
      console.error("Error loading designs", err);
    }
  };

  const fetchOrders = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE}/api/v1/mint/orders`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      });
      const data = await res.json();
      if (res.ok) {
        setOrders(data);
      }
    } catch (err) {
      console.error('Failed to fetch orders:', err);
    }
  };
  const exitToGrid = () => {
    fetchDesigns();
    fetchOrders();
    setBragViewMode('grid');
    setProofingDesign(null);
  };
  const handleCreateAdminCardSlot = async () => {
    const targetUserId = selectedUserContext === 'self' ? user.id : parseInt(selectedUserContext);
    if (!targetUserId) {
      alert("No active user scope selected.");
      return;
    }
    
    try {
      const res = await fetch(`${API_BASE}/api/v1/mint/orders/create-unconfigured-card`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ user_id: targetUserId })
      });
      const data = await res.json();
      if (res.ok) {
        fetchOrders();
        alert("A new unconfigured card slot has been successfully added to the user!");
      } else {
        alert(data.detail || "Failed to create unconfigured card.");
      }
    } catch (err) {
      console.error(err);
      alert("Error occurred while injecting card slot.");
    }
  };

  const fetchChatMessages = async (designId) => {
    if (!token || !designId) return;
    try {
      const res = await fetch(`${API_BASE}/api/v1/chat/design/${designId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      });
      const data = await res.json();
      if (res.ok) {
        setChatMessages(data);
      }
    } catch (err) {
      console.error('Failed to fetch chat messages:', err);
    }
  };

  useEffect(() => {
    if (studioTab === 'brag' || studioTab === 'coin' || studioTab === 'face_templates') {
      fetchDesigns();
    }
    if (studioTab === 'brag') {
      fetchOrders();
    }
    if (studioTab === 'coin' || studioTab === 'face_templates') {
      fetchPhysicalCoins();
      fetchProductionFilaments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTenant, selectedUserContext, studioTab, token, API_BASE]);

  const fetchProofingDesign = async (designId) => {
    if (!token || !designId) return;
    try {
      const res = await fetch(`${API_BASE}/api/v1/designs/${designId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      });
      const data = await res.json();
      if (res.ok) {
        setProofingDesign(data);
      }
    } catch (err) {
      console.error('Failed to fetch proofing design:', err);
    }
  };

  useEffect(() => {
    let interval;
    if (bragViewMode === 'proofer' && proofingDesign) {
      handleLoadDesign(proofingDesign);
      fetchChatMessages(proofingDesign.id);
      fetchProofingDesign(proofingDesign.id);
      interval = setInterval(() => {
        fetchChatMessages(proofingDesign.id);
        fetchProofingDesign(proofingDesign.id);
      }, 5000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bragViewMode, proofingDesign?.id]);
  const fetchCoinChatMessages = async (coinDetailId) => {
    if (!token || !coinDetailId) return;
    try {
      const res = await fetch(`${API_BASE}/api/v1/chat/coin/${coinDetailId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      });
      const data = await res.json();
      if (res.ok) {
        setCoinChatMessages(data);
      }
    } catch (err) {
      console.error('Failed to fetch coin chat messages:', err);
    }
  };

  const fetchProductionFilaments = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE}/api/v1/filaments`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      });
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data) && data.length > 0) {
          setAvailableProductionFilaments(data);
          return;
        }
      }
    } catch (err) {
      console.warn('Failed to fetch filaments from API, using catalog presets:', err);
    }
    setAvailableProductionFilaments([
      { id: 'f1', brand: 'Polymaker', product_name: 'PolyLite ABS', color_name: 'Black', color_hex: '#1A1A1A', material_type: 'ABS' },
      { id: 'f2', brand: 'Polymaker', product_name: 'PolyLite ABS', color_name: 'White', color_hex: '#FFFFFF', material_type: 'ABS' },
      { id: 'f3', brand: 'Polymaker', product_name: 'PolyLite ABS', color_name: 'Dark Purple', color_hex: '#46394E', material_type: 'ABS' },
      { id: 'f4', brand: 'Polymaker', product_name: 'PolyLite ABS', color_name: 'Galaxy Black', color_hex: '#212721', material_type: 'ABS' },
      { id: 'f5', brand: 'Polymaker', product_name: 'PolyLite ABS', color_name: 'Gold', color_hex: '#D4AF37', material_type: 'ABS' },
      { id: 'f6', brand: 'Polymaker', product_name: 'PolyLite ABS', color_name: 'Red', color_hex: '#D32F2F', material_type: 'ABS' },
      { id: 'f7', brand: 'Polymaker', product_name: 'PolyLite ABS', color_name: 'Blue', color_hex: '#1976D2', material_type: 'ABS' },
      { id: 'f8', brand: 'Polymaker', product_name: 'PolyLite ABS', color_name: 'Green', color_hex: '#2E7D32', material_type: 'ABS' },
      { id: 'f9', brand: 'Polymaker', product_name: 'PolyLite ABS', color_name: 'Neon Magenta', color_hex: '#FF007F', material_type: 'ABS' }
    ]);
  };

  const fetchPhysicalCoins = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE}/api/v1/physical-coins`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      });
      const data = await res.json();
      if (res.ok) {
        setPhysicalCoins(data);
      }
    } catch (err) {
      console.error('Failed to fetch physical coins:', err);
    }
  };

  const fetchCoinDetail = async (coinId) => {
    if (!token || !coinId) return;
    try {
      const res = await fetch(`${API_BASE}/api/v1/physical-coins/${coinId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      });
      const data = await res.json();
      if (res.ok) {
        setSelectedCoin(data);
      }
    } catch (err) {
      console.error('Failed to fetch coin detail:', err);
    }
  };

  const resolveFilamentHex = useCallback((filamentStr, defaultHex = '#1A1A1A') => {
    if (!filamentStr) return defaultHex;
    const clean = String(filamentStr).trim().toLowerCase();

    const ensureHex = (hex) => {
      if (!hex) return defaultHex;
      const h = String(hex).trim();
      return h.startsWith('#') ? h : `#${h}`;
    };

    // Direct hex code check (#FFF, #FFFFFF, FFFFFF, etc.)
    if (clean.startsWith('#') && (clean.length === 4 || clean.length === 7 || clean.length === 9)) {
      return ensureHex(filamentStr);
    }
    if (/^[0-9a-f]{6}$/i.test(clean)) {
      return `#${clean}`;
    }

    // 1. Check in availableProductionFilaments
    if (Array.isArray(availableProductionFilaments) && availableProductionFilaments.length > 0) {
      // 1a. Exact full string match: "${brand} ${product_name} - ${color_name}"
      let match = availableProductionFilaments.find(f => {
        const full = `${f.brand || ''} ${f.product_name || ''} - ${f.color_name || ''}`.toLowerCase();
        return full === clean;
      });
      if (match?.color_hex) return ensureHex(match.color_hex);

      // 1b. Match "${product_name} - ${color_name}"
      match = availableProductionFilaments.find(f => {
        const part = `${f.product_name || ''} - ${f.color_name || ''}`.toLowerCase();
        return part === clean;
      });
      if (match?.color_hex) return ensureHex(match.color_hex);

      // 1c. SKU match if SKU exists and is contained in string
      match = availableProductionFilaments.find(f => {
        return f.sku && clean.includes(f.sku.toLowerCase());
      });
      if (match?.color_hex) return ensureHex(match.color_hex);

      // 1d. Color name match (longest color_name first to prevent partial collision)
      const sortedByColor = [...availableProductionFilaments]
        .filter(f => f.color_name)
        .sort((a, b) => (b.color_name?.length || 0) - (a.color_name?.length || 0));

      match = sortedByColor.find(f => {
        const cName = f.color_name.toLowerCase();
        return clean === cName || clean.includes(cName) || cName.includes(clean);
      });
      if (match?.color_hex) return ensureHex(match.color_hex);
    }

    // 2. Comprehensive built-in catalog & known color names fallback
    const COLOR_MAP = {
      'galaxy black': '#212721',
      'cold white': '#F8F9FA',
      'dark purple': '#46394E',
      'crimson red': '#990000',
      'sky blue': '#4FC3F7',
      'dark blue': '#0D47A1',
      'forest green': '#228B22',
      'neon green': '#39FF14',
      'lime green': '#76FF03',
      'neon magenta': '#FF007F',
      'copper pla': '#B87333',
      'copper': '#B87333',
      'bronze': '#CD7F32',
      'silver pla': '#C0C0C0',
      'silver': '#C0C0C0',
      'gold pla': '#D4AF37',
      'gold': '#D4AF37',
      'steel': '#708090',
      'black': '#1A1A1A',
      'white': '#FFFFFF',
      'grey': '#707070',
      'gray': '#707070',
      'red': '#D32F2F',
      'blue': '#1976D2',
      'green': '#2E7D32',
      'yellow': '#FFEB3B',
      'orange': '#FF6D00',
      'magenta': '#E91E63',
      'purple': '#8E24AA',
      'teal': '#009688',
      'brown': '#5D4037',
      'pink': '#FF80AB'
    };

    const sortedEntries = Object.entries(COLOR_MAP).sort((a, b) => b[0].length - a[0].length);
    for (const [colorName, hex] of sortedEntries) {
      if (clean.includes(colorName)) {
        return ensureHex(hex);
      }
    }

    return ensureHex(defaultHex);
  }, [availableProductionFilaments]);

  // Synchronize 3D preview & proofer colors whenever selected coin or filaments change
  useEffect(() => {
    if (selectedCoin && (selectedCoin.body_filament || selectedCoin.accent_filament)) {
      const bColor = resolveFilamentHex(selectedCoin.body_filament, '#1A1A1A');
      const aColor = resolveFilamentHex(selectedCoin.accent_filament, '#D4AF37');
      setCoinBodyColor(bColor);
      setCoinAccentColor(aColor);
    }
  }, [selectedCoin?.id, selectedCoin?.body_filament, selectedCoin?.accent_filament, availableProductionFilaments, resolveFilamentHex]);

  useEffect(() => {
    let interval;
    if (coinViewMode === 'proofer' && selectedCoin) {
      fetchCoinChatMessages(selectedCoin.id);
      fetchCoinDetail(selectedCoin.id);
      interval = setInterval(() => {
        fetchCoinChatMessages(selectedCoin.id);
        fetchCoinDetail(selectedCoin.id);
      }, 5000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coinViewMode, selectedCoin?.id]);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [coinChatMessages, coinSupportTab]);
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [chatMessages, prooferRightTab]);

  useEffect(() => {
    if (bragViewMode === 'proofer' && token) {
      fetch(`${API_BASE}/api/v1/data-groups`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      })
        .then(res => res.ok ? res.json() : [])
        .then(groups => {
          const formatted = {};
          groups.forEach(g => {
            const variables = {};
            g.fields.forEach(f => {
              variables[f.label] = f.sample_data || (f.type === 'number' ? '42.5' : 'Sample Data');
            });
            formatted[g.id.toString()] = {
              name: g.name,
              variables,
              fields: g.fields || []
            };
          });
          setDatasets(formatted);
        })
        .catch(err => console.error("Failed to fetch data groups in studio", err));
    }
  }, [bragViewMode, token, API_BASE]);



  // Helper to process, inline, and supersample all images & vector SVGs in coin SVG clone
  const processSvgImagesAndVectors = async (clone, targetSize = 4096) => {
    // 1. Remove editor overlays, bounding boxes, handles, and pan/zoom buttons
    clone.querySelectorAll('.selection-outline').forEach(el => el.remove());

    // 2. Set high-precision rendering attributes on root SVG
    clone.setAttribute('shape-rendering', 'geometricPrecision');
    clone.setAttribute('text-rendering', 'geometricPrecision');
    clone.setAttribute('image-rendering', 'high-quality');

    // Helper to resolve absolute URL
    const resolveAbsoluteUrl = (src) => {
      if (!src) return '';
      if (src.startsWith('data:') || src.startsWith('blob:')) return src;
      const base = API_BASE ? API_BASE.replace(/\/+$/, '') : '';
      if (src.includes('/uploads/')) {
        const pathPart = src.substring(src.indexOf('/uploads/'));
        return `${base}${pathPart}`;
      } else if (src.startsWith('/')) {
        return `${base}${src}`;
      }
      return src;
    };

    // Helper to load single image from URL asynchronously
    const loadSingleImage = (src) => {
      return new Promise((resolve) => {
        const itemImg = new Image();
        itemImg.crossOrigin = 'anonymous';
        itemImg.onload = () => resolve(itemImg);
        itemImg.onerror = (e) => {
          console.error("Failed to load canvas image: ", src, e);
          resolve(null);
        };
        const abs = resolveAbsoluteUrl(src);
        itemImg.src = abs + (abs.includes('?') ? '&' : '?') + 'cb=' + Date.now();
      });
    };

    const images = Array.from(clone.querySelectorAll('image'));
    for (let imgEl of images) {
      const href = imgEl.getAttribute('href') || imgEl.getAttribute('xlink:href');
      if (!href) continue;

      // Prevent duplicate CSS contrast/grayscale filters if an SVG filter is already applied
      if (imgEl.getAttribute('filter') && imgEl.style && imgEl.style.filter) {
        imgEl.style.filter = '';
      }

      // Check if image is a vector SVG
      const isSvgUrl = href.includes('.svg') || href.startsWith('data:image/svg+xml');
      let inlinedAsNativeVector = false;

      // ATTEMPT 1: Native SVG vector inlining (preserves 100% infinite mathematical precision)
      let svgContentText = null;
      if (href.startsWith('data:image/svg+xml;utf8,') || href.startsWith('data:image/svg+xml;utf-8,')) {
        const commaIdx = href.indexOf(',');
        svgContentText = decodeURIComponent(href.slice(commaIdx + 1));
      } else if (href.startsWith('data:image/svg+xml;base64,')) {
        const commaIdx = href.indexOf(',');
        try { svgContentText = atob(href.slice(commaIdx + 1)); } catch (e) {}
      } else if (href.startsWith('data:image/svg+xml')) {
        const commaIdx = href.indexOf(',');
        try {
          svgContentText = decodeURIComponent(href.slice(commaIdx + 1));
        } catch {
          try { svgContentText = atob(href.slice(commaIdx + 1)); } catch (e) {}
        }
      } else if (isSvgUrl || href.includes('/uploads/')) {
        try {
          const abs = resolveAbsoluteUrl(href);
          const headers = { 'X-Tenant-Domain': window.location.hostname };
          if (token) headers['Authorization'] = `Bearer ${token}`;
          const res = await fetch(abs, { headers });
          if (res.ok) {
            const text = await res.text();
            if (text.includes('<svg')) {
              svgContentText = text;
            }
          }
        } catch (fetchErr) {
          console.warn("[Heightmap Export] Could not fetch SVG text, falling back: ", fetchErr);
        }
      }

      if (svgContentText) {
        try {
          const parser = new DOMParser();
          const doc = parser.parseFromString(svgContentText, 'image/svg+xml');
          const parseError = doc.querySelector('parsererror');
          if (!parseError && doc.documentElement && doc.documentElement.nodeName.toLowerCase() === 'svg') {
            const innerSvg = doc.documentElement;

            // Create a native nested <svg> element to replace <image>
            const inlineSvg = clone.ownerDocument.createElementNS('http://www.w3.org/2000/svg', 'svg');
            inlineSvg.setAttribute('x', imgEl.getAttribute('x') || '0');
            inlineSvg.setAttribute('y', imgEl.getAttribute('y') || '0');
            inlineSvg.setAttribute('width', imgEl.getAttribute('width') || '100%');
            inlineSvg.setAttribute('height', imgEl.getAttribute('height') || '100%');

            // Extract or calculate viewBox
            let vb = innerSvg.getAttribute('viewBox');
            if (!vb) {
              const w = parseFloat(innerSvg.getAttribute('width')) || 100;
              const h = parseFloat(innerSvg.getAttribute('height')) || 100;
              vb = `0 0 ${w} ${h}`;
            }
            inlineSvg.setAttribute('viewBox', vb);
            inlineSvg.setAttribute('preserveAspectRatio', imgEl.getAttribute('preserveAspectRatio') || innerSvg.getAttribute('preserveAspectRatio') || 'none');
            inlineSvg.setAttribute('overflow', 'visible');
            inlineSvg.setAttribute('shape-rendering', 'geometricPrecision');
            inlineSvg.setAttribute('text-rendering', 'geometricPrecision');

            // Transfer all clipping, filter, transform, and style attributes from <image> to the inline <svg>
            ['clip-path', 'clipPath', 'transform', 'filter', 'style', 'opacity', 'mask'].forEach(attr => {
              if (imgEl.hasAttribute(attr)) {
                inlineSvg.setAttribute(attr, imgEl.getAttribute(attr));
              }
            });
            if (imgEl.style && imgEl.style.cssText) {
              inlineSvg.style.cssText = imgEl.style.cssText;
            }

            // Namespace all internal IDs in innerSvg to prevent collisions with coin SVG
            const uid = `v${Math.random().toString(36).substring(2, 7)}_`;
            const idElements = innerSvg.querySelectorAll('[id]');
            idElements.forEach(el => {
              const oldId = el.getAttribute('id');
              const newId = `${uid}${oldId}`;
              el.setAttribute('id', newId);
              innerSvg.querySelectorAll(`[fill="url(#${oldId})"]`).forEach(e => e.setAttribute('fill', `url(#${newId})`));
              innerSvg.querySelectorAll(`[stroke="url(#${oldId})"]`).forEach(e => e.setAttribute('stroke', `url(#${newId})`));
              innerSvg.querySelectorAll(`[clip-path="url(#${oldId})"]`).forEach(e => e.setAttribute('clip-path', `url(#${newId})`));
              innerSvg.querySelectorAll(`[filter="url(#${oldId})"]`).forEach(e => e.setAttribute('filter', `url(#${newId})`));
              innerSvg.querySelectorAll(`[mask="url(#${oldId})"]`).forEach(e => e.setAttribute('mask', `url(#${newId})`));
              innerSvg.querySelectorAll(`[*|href="#${oldId}"]`).forEach(e => {
                if (e.hasAttribute('href')) e.setAttribute('href', `#${newId}`);
                if (e.hasAttribute('xlink:href')) e.setAttribute('xlink:href', `#${newId}`);
              });
            });

            // Append all vector children into the inline <svg>
            while (innerSvg.firstChild) {
              const imported = clone.ownerDocument.importNode(innerSvg.firstChild, true);
              inlineSvg.appendChild(imported);
            }

            // Replace <image> with native inline <svg>
            imgEl.parentNode.replaceChild(inlineSvg, imgEl);
            inlinedAsNativeVector = true;
          }
        } catch (parseErr) {
          console.warn("[Heightmap Export] Error parsing vector SVG for inlining: ", parseErr);
        }
      }

      // ATTEMPT 2: Ultra-High-Resolution Canvas Rasterization (for raster images or fallback)
      if (!inlinedAsNativeVector) {
        if (!href.startsWith('data:')) {
          const loadedImg = await loadSingleImage(href);
          if (loadedImg) {
            // Calculate real rendered pixel footprint on the target heightmap canvas
            const elementW = parseFloat(imgEl.getAttribute('width')) || 100;
            const elementH = parseFloat(imgEl.getAttribute('height')) || 100;
            const targetPixelW = Math.round(elementW * (targetSize / 200));
            const targetPixelH = Math.round(elementH * (targetSize / 200));

            // Super-sample resolution: never downsample to small default viewBox (e.g. 100x100)
            const natW = loadedImg.naturalWidth || loadedImg.width || targetPixelW;
            const natH = loadedImg.naturalHeight || loadedImg.height || targetPixelH;

            // If it's an SVG or being magnified, scale canvas up to at least targetPixel dimensions (or 2048+)
            let canvasW = natW;
            let canvasH = natH;
            if (isSvgUrl || natW < targetPixelW) {
              canvasW = Math.max(natW, targetPixelW, 2048);
              canvasH = Math.max(natH, targetPixelH, 2048);
            }

            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = canvasW;
            tempCanvas.height = canvasH;
            const tempCtx = tempCanvas.getContext('2d');
            tempCtx.imageSmoothingEnabled = true;
            tempCtx.imageSmoothingQuality = 'high';
            tempCtx.drawImage(loadedImg, 0, 0, canvasW, canvasH);

            try {
              const base64 = tempCanvas.toDataURL('image/png');
              imgEl.setAttribute('href', base64);
              imgEl.removeAttribute('xlink:href');
            } catch (e) {
              console.error("[Heightmap Export] Failed to convert image to base64 inside SVG clone:", e);
            }
          }
        }
      }
    }
  };

  // Helper to export high resolution 4K png heightmap for 3D slicing
  const handleExportHighResPNG = async () => {
    setSaveStatus('Exporting high-resolution 4K heightmap...');
    const svgElement = document.getElementById('studio-coin-svg');
    if (!svgElement) {
      setStatusError('SVG element not found.');
      setTimeout(() => setStatusError(''), 3000);
      return;
    }

    try {
      const size = 4096; // 4K Ultra-High Definition heightmap for precision 3D slicing
      const clone = svgElement.cloneNode(true);

      // Process and inline all vector SVGs into pure native vector paths & high-res images
      await processSvgImagesAndVectors(clone, size);

      // Set high-resolution dimensions on the cloned SVG
      clone.setAttribute('width', size.toString());
      clone.setAttribute('height', size.toString());

      // Serialize cloned SVG with native vectors and inline assets
      const svgString = new XMLSerializer().serializeToString(clone);
      const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(blob);

      const img = new Image();
      img.crossOrigin = 'anonymous';

      const loadPromise = new Promise((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = (err) => reject(err);
      });

      img.src = url;
      await loadPromise;

      // Draw the entire composite high-res SVG directly onto the canvas with high smoothing
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, size, size);
      
      const pngUrl = canvas.toDataURL('image/png');
      URL.revokeObjectURL(url);

      // Trigger download
      const safeName = designName
        ? designName.trim().replace(/[^a-zA-Z0-9_.-]/g, '_')
        : `coin_face_${Date.now()}`;

      const downloadLink = document.createElement('a');
      downloadLink.href = pngUrl;
      downloadLink.download = `${safeName}_heightmap_4k.png`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);

      setSaveStatus('4K Heightmap PNG exported successfully (4096×4096).');
      setTimeout(() => setSaveStatus(''), 4000);

    } catch (err) {
      console.error("Failed to export PNG: ", err);
      setStatusError('Failed to generate high-resolution PNG.');
      setTimeout(() => setStatusError(''), 3000);
    }
  };

  // Helper to export typography layers as a standalone SVG file
  const exportTypographySVG = () => {
    const brightness = 255 - Math.round((textGrayscale / 100) * 255);
    const textColor = `rgb(${brightness}, ${brightness}, ${brightness})`;

    // 1. Calculate path coordinates for arc paths based on textRadius
    const topArcD = `M ${100 - textRadius} 100 A ${textRadius} ${textRadius} 0 0 1 ${100 + textRadius} 100`;
    const bottomArcD = `M ${100 + textRadius} 100 A ${textRadius} ${textRadius} 0 0 1 ${100 - textRadius} 100`;

    // 2. Generate center symbol if it is a text symbol
    const isTextSymbol = symbol && !(symbol.startsWith('http') || symbol.includes('.') || symbol.includes('/'));
    const centerSymbolSVG = isTextSymbol ? `\n  <!-- Center Symbol -->\n  <text x="100" y="114" font-size="46" text-anchor="middle" font-family="system-ui, sans-serif" fill="${textColor}">${symbol}</text>` : '';

    // 3. Assemble full SVG document string
    const svgContent = `<?xml version="1.0" encoding="utf-8"?>
<svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200" width="100%" height="100%">
  <defs>
    <path id="topArcPath" d="${topArcD}" fill="none" />
    <path id="bottomArcPath" d="${bottomArcD}" fill="none" />
  </defs>
  
  <!-- Upper Curved Text -->
  <text 
    font-family="${textFont.replace(/"/g, '&quot;')}" 
    font-size="${textSize}" 
    font-weight="900" 
    letter-spacing="${textKerning}"
    fill="${textColor}"
  >
    <textPath href="#topArcPath" startOffset="50%" text-anchor="middle">${upperText || ''}</textPath>
  </text>
  
  <!-- Lower Curved Text -->
  <text 
    font-family="${textFont.replace(/"/g, '&quot;')}" 
    font-size="${textSize}" 
    font-weight="900" 
    letter-spacing="${textKerning}"
    fill="${textColor}"
  >
    <textPath href="#bottomArcPath" startOffset="50%" text-anchor="middle">${lowerText || ''}</textPath>
  </text>${centerSymbolSVG}
</svg>`;

    // 4. Create Blob and trigger browser download
    const blob = new Blob([svgContent], { type: 'image/svg+xml;charset=utf-8' });
    const blobUrl = URL.createObjectURL(blob);
    
    const safeName = designName
      ? designName.trim().replace(/[^a-zA-Z0-9_.-]/g, '_')
      : `coin_face_${Date.now()}`;

    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = `typography_layers_${safeName}.svg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
    
    setSaveStatus('Typography layers exported successfully as SVG.');
    setTimeout(() => setSaveStatus(''), 3000);
  };

  // 5. Handle Save Design to Database
  const handleSaveConfig = async (e, forceIsTemplate = null) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!token) {
      setStatusError('Authentication token is missing. Please sign in again.');
      return;
    }

    let previewUrl = null;

    if (studioTab === 'coin' || studioTab === 'face_templates' || isFullscreenFaceEditorOpen) {
      const svgElement = document.getElementById('studio-coin-svg');
      if (svgElement) {
        try {
          const clone = svgElement.cloneNode(true);
          await processSvgImagesAndVectors(clone, 400);

          const getCanvasData = async (size) => {
            clone.setAttribute('width', size.toString());
            clone.setAttribute('height', size.toString());
            const svgString = new XMLSerializer().serializeToString(clone);
            const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const img = new Image();
            img.crossOrigin = 'anonymous';
            const loadPromise = new Promise((resolve, reject) => {
              img.onload = () => resolve();
              img.onerror = (err) => reject(err);
            });
            img.src = url;
            await loadPromise;
            const canvas = document.createElement('canvas');
            canvas.width = size;
            canvas.height = size;
            const ctx = canvas.getContext('2d');
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            ctx.drawImage(img, 0, 0, size, size);
            const dataUrl = canvas.toDataURL('image/png');
            URL.revokeObjectURL(url);
            return dataUrl;
          };

          previewUrl = await getCanvasData(400);
        } catch (err) {
          console.error("Failed to generate preview PNG: ", err);
        }
      }
    }

    const existingDesignObj = designs.find(d => d.id === selectedDesignId);
    const existingConfig = (existingDesignObj && typeof existingDesignObj.config === 'object' && existingDesignObj.config !== null)
      ? existingDesignObj.config
      : {};
    const isExplicitNonTemplate = (
      existingConfig.is_template === false ||
      existingConfig.is_template === 'false' ||
      Boolean(existingConfig.coin_id)
    );
    const isTemplateVal = forceIsTemplate !== null
      ? forceIsTemplate
      : (!isExplicitNonTemplate && (isDesignTemplate || (existingConfig.is_template === true) || (studioTab === 'face_templates')));

    const configPayload = {
      ...existingConfig,
      metal,
      side: faceSide,
      symbol,
      upperText,
      lowerText,
      textMode,
      singleText,
      textFontWeight,
      textFontStyle,
      textCase,
      edgeStyle,
      depth,
      textGrayscale,
      textRadius,
      textSize,
      textKerning,
      textFont,
      rings,
      selectedRingId,
      previewImage: previewUrl,
      is_template: isTemplateVal
    };

    let associatedId = user.id;
    if (selectedUserContext !== 'self') {
      associatedId = parseInt(selectedUserContext);
    }

    const payload = {
      name: designName,
      design_type: 'coin_face',
      config: configPayload,
      associated_user_id: associatedId,
      tenant_id: isGlobalRole ? (selectedTenant ? parseInt(selectedTenant) : null) : parseInt(tenantConfig.id)
    };

    const isEditing = selectedDesignId !== null;
    const saveUrl = isEditing ? `${API_BASE}/api/v1/designs/${selectedDesignId}` : `${API_BASE}/api/v1/designs`;
    const saveMethod = isEditing ? 'PATCH' : 'POST';

    if (isEditing && isTemplateVal) {
      try {
        const usageRes = await fetch(`${API_BASE}/api/v1/designs/${selectedDesignId}/coin-usage`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-Tenant-Domain': window.location.hostname
          }
        });
        if (usageRes.ok) {
          const usageData = await usageRes.json();
          if (usageData && usageData.used_in_coins && usageData.count > 0) {
            const coinNames = (usageData.coins || []).map(c => `"${c.name}"`).join(', ');
            const shouldUpdateCoins = await modal.confirm(
              `This face template is used in ${usageData.count} coin${usageData.count === 1 ? '' : 's'} (${coinNames}).\n\nDo you want to update the associated faces in ${usageData.count === 1 ? 'that coin' : 'those coins'} as well?`,
              {
                title: 'Update Associated Coins?',
                confirmText: 'Update Associated Coins',
                cancelText: 'Update Template Only'
              }
            );
            payload.update_associated_coins = Boolean(shouldUpdateCoins);
          }
        }
      } catch (usageErr) {
        console.warn('Could not check face template coin usage:', usageErr);
      }
    }

    try {
      const res = await fetch(saveUrl, {
        method: saveMethod,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error("Failed to save design context");
      const data = await res.json();
      const updatedCoinsMsg = payload.update_associated_coins
        ? `Design "${data.name}" and associated coin faces updated!`
        : `Design "${data.name}" saved successfully in database!`;
      setSaveStatus(updatedCoinsMsg);
      setSelectedDesignId(data.id);
      await fetchDesigns();
      fetchPhysicalCoins();
      if (selectedCoin?.id && selectedCoin.id !== 'new') {
        fetchCoinDetail(selectedCoin.id);
      }
      setFaceTemplatesVersion(v => v + 1);
      setTimeout(() => setSaveStatus(''), 4000);
      return data;
    } catch (err) {
      setStatusError(`Failed to save: ${err.message}`);
      setTimeout(() => setStatusError(''), 4000);
      throw err;
    }
  };



  const handleCreatePhysicalCoin = () => {
    if (!token) return;
    let ownerId = user.id;
    if (selectedUserContext && selectedUserContext !== 'self') {
      ownerId = parseInt(selectedUserContext);
    }
    const mockCoin = {
      id: 'new',
      name: 'New Custom Coin',
      face_1_design_id: null,
      face_2_design_id: null,
      status: 'new',
      is_system_coin: false,
      associated_user_id: ownerId
    };
    setSelectedCoin(mockCoin);
    setWizardCoinName('New Custom Coin');
    setWizardHeadsDesignId('');
    setWizardTailsDesignId('');
    setWizardIsSystemCoin(false);
    const defaultBody = availableProductionFilaments[0]
      ? `${availableProductionFilaments[0].brand} ${availableProductionFilaments[0].product_name} - ${availableProductionFilaments[0].color_name}`
      : 'Polymaker PolyLite ABS - Black';
    const defaultAccent = availableProductionFilaments[1]
      ? `${availableProductionFilaments[1].brand} ${availableProductionFilaments[1].product_name} - ${availableProductionFilaments[1].color_name}`
      : 'Polymaker PolyLite ABS - Gold';
    setWizardBodyFilament(defaultBody);
    setWizardAccentFilament(defaultAccent);
    setIsSetupWizardOpen(true);
  };

  const handleCreateSystemCoin = () => {
    if (!token) return;
    let ownerId = user.id;
    if (selectedUserContext && selectedUserContext !== 'self') {
      ownerId = parseInt(selectedUserContext);
    }
    const mockCoin = {
      id: 'new',
      name: 'New System Coin',
      face_1_design_id: null,
      face_2_design_id: null,
      status: 'new',
      is_system_coin: true,
      associated_user_id: ownerId
    };
    setSelectedCoin(mockCoin);
    setWizardCoinName('New System Coin');
    setWizardHeadsDesignId('');
    setWizardTailsDesignId('');
    setWizardIsSystemCoin(true);
    const defaultBody = availableProductionFilaments[0]
      ? `${availableProductionFilaments[0].brand} ${availableProductionFilaments[0].product_name} - ${availableProductionFilaments[0].color_name}`
      : 'Polymaker PolyLite ABS - Black';
    const defaultAccent = availableProductionFilaments[1]
      ? `${availableProductionFilaments[1].brand} ${availableProductionFilaments[1].product_name} - ${availableProductionFilaments[1].color_name}`
      : 'Polymaker PolyLite ABS - Gold';
    setWizardBodyFilament(defaultBody);
    setWizardAccentFilament(defaultAccent);
    setIsSetupWizardOpen(true);
  };

  const handleOpen3DPreviewForCoin = (coin) => {
    if (!coin) return;
    setSelectedCoin(coin);
    const bColor = resolveFilamentHex(coin.body_filament, '#1A1A1A');
    const aColor = resolveFilamentHex(coin.accent_filament, '#D4AF37');
    setCoinBodyColor(bColor);
    setCoinAccentColor(aColor);
    setIsCoin3DPreviewOpen(true);
  };

  const handleOpenCoin = (coin) => {
    if (!coin) return;
    if (coin.is_system_coin || coin.status === 'new' || !coin.face_1_design_id || !coin.face_2_design_id) {
      setSelectedCoin(coin);
      setWizardCoinName(coin.name);
      setWizardHeadsDesignId(coin.face_1_design_id || '');
      setWizardTailsDesignId(coin.face_2_design_id || '');
      setWizardIsSystemCoin(Boolean(coin.is_system_coin));
      setWizardBodyFilament(coin.body_filament || '');
      setWizardAccentFilament(coin.accent_filament || '');
      setIsSetupWizardOpen(true);
    } else {
      setSelectedCoin(coin);
      const bColor = resolveFilamentHex(coin.body_filament, '#1A1A1A');
      const aColor = resolveFilamentHex(coin.accent_filament, '#D4AF37');
      setCoinBodyColor(bColor);
      setCoinAccentColor(aColor);
      setCoinViewMode('proofer');
      setCoinSupportTab('chat');
    }
  };

  useEffect(() => {
    if (initialTab) {
      setStudioTab(initialTab);
    }
  }, [initialTab]);

  useEffect(() => {
    if (initialCoinId) {
      setStudioTab('coin');
      const openInitialCoin = async () => {
        try {
          const res = await fetch(`${API_BASE}/api/v1/physical-coins/${initialCoinId}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'X-Tenant-Domain': window.location.hostname
            }
          });
          if (res.ok) {
            const coinData = await res.json();
            handleOpenCoin(coinData);
          }
        } catch (e) {
          console.error('Failed to open initial coin:', e);
        }
      };
      openInitialCoin();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCoinId]);

  useEffect(() => {
    if (initialProofingDesignId) {
      setStudioTab('brag');
      const openInitialDesign = async () => {
        try {
          const res = await fetch(`${API_BASE}/api/v1/designs/${initialProofingDesignId}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'X-Tenant-Domain': window.location.hostname
            }
          });
          if (res.ok) {
            const designData = await res.json();
            setProofingDesign(designData);
            setBragViewMode('proofer');
          }
        } catch (e) {
          console.error('Failed to open initial design:', e);
        }
      };
      openInitialDesign();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialProofingDesignId]);

  // eslint-disable-next-line no-unused-vars
  const handleCreateAndEditCoin = async (orderSlot = null, faceType = 1) => {
    const defaultRings = [
      {
        id: 1,
        name: 'White Base Disk',
        outerRadiusX: 95,
        outerRadiusY: 95,
        innerRadiusX: 0,
        innerRadiusY: 0,
        offsetX: 0,
        offsetY: 0,
        grayscale: 0,
        outerBlur: 0,
        innerBlur: 0,
        noise: 0,
        noiseSize: 50,
        patternUrl: '',
        locked: false,
        constrainOuter: true,
        constrainInner: true,
        lockOffset: false
      }
    ];

    const defaultConfig = {
      metal: 'white',
      symbol: '',
      upperText: '',
      lowerText: '',
      textMode: 'split',
      singleText: '',
      textFontWeight: 'normal',
      textFontStyle: 'normal',
      textCase: 'uppercase',
      edgeStyle: 'reeded',
      depth: 'medium',
      textGrayscale: 60,
      textRadius: 68,
      textSize: 16,
      textKerning: 1.2,
      textFont: '"Arial Black", sans-serif',
      rings: defaultRings,
      selectedRingId: 1
    };

    let associatedId = user.id;
    if (selectedUserContext && selectedUserContext !== 'self') {
      associatedId = parseInt(selectedUserContext);
    }

    const name = orderSlot 
      ? `${orderSlot.packageName} - Face ${faceType}`
      : `${user.first_name || user.email}'s Custom Coin Face`;

    const payload = {
      name: name,
      design_type: 'coin_face',
      config: defaultConfig,
      associated_user_id: associatedId,
      tenant_id: isGlobalRole ? (selectedTenant ? parseInt(selectedTenant) : null) : parseInt(tenantConfig.id)
    };

    try {
      const dRes = await fetch(API_BASE + "/api/v1/designs", {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': "Bearer " + token,
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify(payload)
      });
      const designData = await dRes.json();
      if (!dRes.ok) throw new Error(designData.detail || 'Failed to create coin design');

      if (orderSlot) {
        const assignPayload = faceType === 1 
          ? { face_1_design_id: designData.id }
          : { face_2_design_id: designData.id };

        const aRes = await fetch(API_BASE + "/api/v1/mint/orders/" + orderSlot.orderId + "/assign-artwork", {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': "Bearer " + token,
            'X-Tenant-Domain': window.location.hostname
          },
          body: JSON.stringify(assignPayload)
        });
        if (!aRes.ok) {
          const aData = await aRes.json();
          throw new Error(aData.detail || 'Failed to assign coin face to order');
        }
      }

      fetchDesigns();
      fetchOrders();

      handleLoadDesign(designData);
      setIsFullscreenFaceEditorOpen(true);
    } catch (err) {
      alert(err.message || 'Error occurred during coin creation.');
    }
  };

  const handleCreateNewFaceTemplate = async (name, side = 'heads', metal = 'gold') => {
    const defaultRings = [
      {
        id: 1,
        name: 'Base Disk',
        outerRadiusX: 95,
        outerRadiusY: 95,
        innerRadiusX: 0,
        innerRadiusY: 0,
        offsetX: 0,
        offsetY: 0,
        grayscale: 0,
        outerBlur: 0,
        innerBlur: 0,
        noise: 0,
        noiseSize: 50,
        patternUrl: '',
        locked: false,
        constrainOuter: true,
        constrainInner: true,
        lockOffset: false
      }
    ];

    const defaultConfig = {
      metal: metal || 'gold',
      side: side || 'heads',
      symbol: '',
      upperText: '',
      lowerText: '',
      textMode: 'split',
      singleText: '',
      textFontWeight: 'normal',
      textFontStyle: 'normal',
      textCase: 'uppercase',
      edgeStyle: 'reeded',
      depth: 'medium',
      textGrayscale: 60,
      textRadius: 68,
      textSize: 16,
      textKerning: 1.2,
      textFont: '"Arial Black", sans-serif',
      rings: defaultRings,
      selectedRingId: 1,
      is_template: true,
      shared_all: true,
      shared_user_emails: []
    };

    let associatedId = user.id;
    if (selectedUserContext && selectedUserContext !== 'self') {
      associatedId = parseInt(selectedUserContext);
    }

    const payload = {
      name: name || `${user.first_name || user.email}'s Face Template`,
      design_type: 'coin_face',
      config: defaultConfig,
      associated_user_id: associatedId,
      tenant_id: isGlobalRole ? (selectedTenant ? parseInt(selectedTenant) : null) : parseInt(tenantConfig.id)
    };

    try {
      const dRes = await fetch(API_BASE + "/api/v1/designs", {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': "Bearer " + token,
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify(payload)
      });
      const designData = await dRes.json();
      if (!dRes.ok) throw new Error(designData.detail || 'Failed to create face template');

      fetchDesigns();
      handleLoadDesign(designData);
      setIsFullscreenFaceEditorOpen(true);
    } catch (err) {
      alert(err.message || 'Error occurred during face template creation.');
    }
  };

  // 6. Load a selected design config
  const handleLoadDesign = (design) => {
    if (!design) return;
    setSelectedDesignId(design.id);
    setDesignName(design.name || 'Untitled Design');
    
    let cfg = design.config;
    if (typeof cfg === 'string') {
      try {
        cfg = JSON.parse(cfg);
      } catch (e) {
        cfg = {};
      }
    }
    if (!cfg || typeof cfg !== 'object') {
      cfg = {};
    }

    setDesigns(prev => {
      if (!Array.isArray(prev)) return [{ ...design, config: cfg }];
      const exists = prev.some(d => d.id === design.id);
      return exists
        ? prev.map(d => d.id === design.id ? { ...design, config: cfg } : d)
        : [{ ...design, config: cfg }, ...prev];
    });

    if (design.design_type === 'coin_face') {
      const isExplicitNonTemplate = (
        cfg.is_template === false ||
        cfg.is_template === 'false' ||
        Boolean(cfg.coin_id)
      );
      setIsDesignTemplate(!isExplicitNonTemplate);
    } else {
      setIsDesignTemplate(cfg.is_template === true || cfg.is_template === 'true');
    }
    
    if (design.design_type === 'coin_face') {
      if (cfg.metal) setMetal(cfg.metal);
      if (cfg.side) setFaceSide(cfg.side); else setFaceSide('heads');
      if (cfg.symbol) setSymbol(cfg.symbol);
      if (cfg.upperText) setUpperText(cfg.upperText);
      if (cfg.lowerText) setLowerText(cfg.lowerText);
      if (cfg.textMode) setTextMode(cfg.textMode); else setTextMode('split');
      if (cfg.singleText) setSingleText(cfg.singleText); else setSingleText('TROPHYCOIN OFFICIAL MINTED PROTOCOL');
      if (cfg.textFontWeight) setTextFontWeight(cfg.textFontWeight); else setTextFontWeight('900');
      if (cfg.textFontStyle) setTextFontStyle(cfg.textFontStyle); else setTextFontStyle('normal');
      if (cfg.textCase) setTextCase(cfg.textCase); else setTextCase('uppercase');
      if (cfg.edgeStyle) setEdgeStyle(cfg.edgeStyle);
      if (cfg.depth) setDepth(cfg.depth);
      if (cfg.textGrayscale !== undefined) setTextGrayscale(cfg.textGrayscale);
      if (cfg.textRadius !== undefined) setTextRadius(cfg.textRadius); else setTextRadius(68);
      if (cfg.textSize !== undefined) setTextSize(cfg.textSize); else setTextSize(16);
      if (cfg.textKerning !== undefined) setTextKerning(cfg.textKerning); else setTextKerning(1.2);
      if (cfg.textFont) setTextFont(cfg.textFont); else setTextFont('"Arial Black", sans-serif');
      
      const rawRings = Array.isArray(cfg.rings) ? cfg.rings.filter(Boolean) : null;
      if (rawRings !== null) {
        let loadedRings = [...rawRings];
        if (!loadedRings.some(r => r && r.type === 'text')) {
          loadedRings.unshift({
            id: 'typography-layer',
            name: 'Typography',
            type: 'text',
            visible: true,
            locked: false
          });
        }
        setRings(loadedRings);
        if (cfg.selectedRingId !== undefined) {
          setSelectedRingId(cfg.selectedRingId);
        } else if (loadedRings.length > 0) {
          setSelectedRingId(loadedRings[0]?.id || 'typography-layer');
        }
      } else {
        const legacyMetal = cfg.metal || 'gold';
        let baseGrayscale = 30;
        let rimGrayscale = 50;
        if (legacyMetal === 'white') {
          baseGrayscale = 0;
          rimGrayscale = 0;
        } else if (legacyMetal === 'obsidian') {
          baseGrayscale = 90;
          rimGrayscale = 20;
        } else if (legacyMetal === 'gold') {
          baseGrayscale = 25;
          rimGrayscale = 15;
        } else if (legacyMetal === 'silver') {
          baseGrayscale = 30;
          rimGrayscale = 20;
        } else if (legacyMetal === 'copper') {
          baseGrayscale = 40;
          rimGrayscale = 30;
        } else if (legacyMetal === 'bronze') {
          baseGrayscale = 45;
          rimGrayscale = 35;
        }
        const converted = [
          {
            id: 'typography-layer',
            name: 'Typography',
            type: 'text',
            visible: true,
            locked: false
          },
          {
            id: 1,
            name: 'Outer Rim',
            outerRadiusX: 95,
            outerRadiusY: 95,
            innerRadiusX: 85,
            innerRadiusY: 85,
            offsetX: 0,
            offsetY: 0,
            grayscale: rimGrayscale,
            outerBlur: 0,
            innerBlur: 0,
            noise: 0,
            noiseSize: 50,
            patternUrl: '',
            locked: false,
            constrainOuter: true,
            constrainInner: true,
            lockOffset: false
          },
          {
            id: 2,
            name: 'Inner Base',
            outerRadiusX: 84,
            outerRadiusY: 84,
            innerRadiusX: 0,
            innerRadiusY: 0,
            offsetX: 0,
            offsetY: 0,
            grayscale: baseGrayscale,
            outerBlur: 0,
            innerBlur: 0,
            noise: 0,
            noiseSize: 50,
            patternUrl: '',
            locked: false,
            constrainOuter: true,
            constrainInner: true,
            lockOffset: false
          }
        ];
        setRings(converted);
        setSelectedRingId(converted[0].id);
      }
      if (studioTab !== 'face_templates' && !design.config?.is_template) {
        setStudioTab('coin');
      }


    } else if (design.design_type === 'brag_page') {
      setProofingDesign(design);
      setBragViewMode('proofer');
    }
  };

  // 7. Delete Design
  const handleDeleteDesign = async (id, e) => {
    e.stopPropagation(); // Prevent loading design on click
    
    // Find the design
    const targetDesign = designs.find(d => d.id === id);
    const isApprovedCard = targetDesign && targetDesign.design_type === 'brag_page' && targetDesign.config && (targetDesign.config.status === 'approved' || targetDesign.config.status === 'published');
    
    if (isApprovedCard && user && user.role === 'OPERATOR') {
      // Archive instead of delete!
      if (!token) return;
      const confirmed = await modal.confirm("Are you sure you want to archive this trophy card? Once archived, it will be hidden from your grid.", {
        title: "Archive Trophy Card",
        confirmText: "Archive",
        danger: true
      });
      if (!confirmed) return;
      
      const updatedConfig = { ...targetDesign.config, status: 'archived' };
      fetch(`${API_BASE}/api/v1/designs/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify({ config: updatedConfig })
      })
        .then(res => {
          if (!res.ok) throw new Error("Failed to archive design");
          
          // Post chat confirmation
          fetch(API_BASE + "/api/v1/chat/design/" + id, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': "Bearer " + token
            },
            body: JSON.stringify({ message_text: `📁 Trophy Card archived by operator (${user?.email || 'unknown user'}).` })
          }).catch(console.error);

          setSaveStatus("Trophy card archived successfully.");
          fetchDesigns();
          setTimeout(() => setSaveStatus(''), 4000);
        })
        .catch(err => {
          setStatusError(`Archive failed: ${err.message}`);
          setTimeout(() => setStatusError(''), 4000);
        });
        
      return;
    }

    if (!token) return;
    const confirmed = await modal.confirm("Are you sure you want to delete this design?", {
      title: "Delete Design",
      confirmText: "Delete",
      danger: true
    });
    if (!confirmed) return;

    fetch(`${API_BASE}/api/v1/designs/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-Tenant-Domain': window.location.hostname
      }
    })
      .then(res => {
        if (!res.ok) throw new Error("Failed to delete design");
        if (selectedDesignId === id) {
          setSelectedDesignId(null);
        }
        setSaveStatus("Design deleted successfully.");
        fetchDesigns();
        setTimeout(() => setSaveStatus(''), 4000);
      })
      .catch(err => {
        setStatusError(`Delete failed: ${err.message}`);
        setTimeout(() => setStatusError(''), 4000);
      });
  };

  // 7.5 Duplicate Design
  const parseMessageText = (text) => {
    if (!text) return '';
    const parts = [];
    let lastIndex = 0;
    const regex = /\[(color|asset|asset_img):([^\]]+)\]/g;
    let match;
    
    while ((match = regex.exec(text)) !== null) {
      const index = match.index;
      const type = match[1];
      const val = match[2];
      
      if (index > lastIndex) {
        parts.push(text.substring(lastIndex, index));
      }
      
      if (type === 'color') {
        parts.push(
          <span key={index} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', verticalAlign: 'middle', margin: '0 4px', padding: '2px 4px', borderRadius: '4px', border: '1px solid var(--border-color)', backgroundColor: 'var(--bg-secondary)', fontSize: '10px' }}>
            <span style={{ display: 'inline-block', width: '12px', height: '12px', borderRadius: '2px', backgroundColor: val, border: '1px solid #ccc' }}></span>
            {val}
          </span>
        );
      } else if (type === 'asset' || type === 'asset_img') {
        const url = type === 'asset_img' ? val : (studioAssets.find(a => a.id.toString() === val)?.file_url || '');
        if (url) {
          parts.push(
            <span key={index} style={{ display: 'inline-block', verticalAlign: 'middle', margin: '0 4px' }}>
              <img src={url} alt="Inline Asset" style={{ maxHeight: '36px', maxWidth: '36px', borderRadius: '4px', border: '1px solid var(--border-color)', display: 'block', cursor: 'pointer' }} onClick={() => window.open(url, '_blank')} />
            </span>
          );
        } else {
          parts.push("[Asset #" + val + "]");
        }
      }
      
      lastIndex = regex.lastIndex;
    }
    
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }
    
    return parts.length > 0 ? parts : text;
  };

  // eslint-disable-next-line no-unused-vars
  const handleDuplicateDesign = async (design, e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    if (!token) return;
    
    const newName = await modal.prompt("Enter a name for the duplicated card/template:", `${design.name} (Copy)`, {
      title: "Duplicate Design",
      confirmText: "Duplicate"
    });
    if (!newName) return;
    
    const duplicatedPayload = {
      name: newName,
      design_type: design.design_type,
      config: {
        ...design.config,
        is_template: !!design.config.is_template
      },
      associated_user_id: design.associated_user_id,
      tenant_id: design.tenant_id
    };
    
    try {
      const res = await fetch(`${API_BASE}/api/v1/designs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify(duplicatedPayload)
      });
      const data = await res.json();
      if (res.ok) {
        setSaveStatus(`Duplicated successfully as "${data.name}"!`);
        fetchDesigns();
        setTimeout(() => setSaveStatus(''), 4000);
      } else {
        throw new Error(data.detail || 'Failed to duplicate design');
      }
    } catch (err) {
      setStatusError(`Duplication failed: ${err.message}`);
      setTimeout(() => setStatusError(''), 4000);
    }
  };

  // Metal configurations
  const metalConfig = {
    white: { base: '#ffffff', rim: '#ffffff', textFill: '#999999', glow: 'none' },
    gold: { base: 'url(#goldBase)', rim: 'url(#goldRim)', textFill: '#AA7C11', glow: 'none' },
    silver: { base: 'url(#silverBase)', rim: 'url(#silverRim)', textFill: '#5E6B73', glow: 'none' },
    copper: { base: 'url(#copperBase)', rim: 'url(#copperRim)', textFill: '#8B4A2F', glow: 'none' },
    bronze: { base: 'url(#bronzeBase)', rim: 'url(#bronzeRim)', textFill: '#6E4724', glow: 'none' },
    obsidian: { base: 'url(#obsidianBase)', rim: 'url(#goldRim)', textFill: '#D4AF37', glow: 'none' }
  };

  const getSafeCoinMetalConfig = (metalKey) => {
    const key = typeof metalKey === 'string' ? metalKey.trim().toLowerCase() : 'gold';
    return metalConfig[key] || metalConfig.gold || { base: '#AA7C11', rim: '#AA7C11', textFill: '#AA7C11' };
  };



  // eslint-disable-next-line no-unused-vars
  const renderBragCard = (side = 'front', scale = 1, forceTheme = null) => {
    const width = 320 * scale;
    const height = 448 * scale;
    
    let img = null;
    if (proofingDesign && proofingDesign.config) {
      img = side === 'front' ? proofingDesign.config.preview_image : proofingDesign.config.preview_image_back;
    }
    
    if (img) {
      return (
        <div style={{
          width: `${width}px`,
          height: `${height}px`,
          borderRadius: `${16 * scale}px`,
          border: '1px solid var(--border-color)',
          boxSizing: 'border-box',
          overflow: 'hidden',
          backgroundColor: '#0c0a09',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative'
        }}>
          <img src={img} alt={`${side} view`} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        </div>
      );
    }

    return (
      <div style={{
        width: `${width}px`,
        height: `${height}px`,
        borderRadius: `${16 * scale}px`,
        border: '2px dashed var(--border-color)',
        boxSizing: 'border-box',
        backgroundColor: 'var(--card-bg)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--text-mute)',
        fontSize: '13px',
        padding: '20px',
        textAlign: 'center'
      }}>
        <span>No Preview Available</span>
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {/* Status Alerts */}
        {saveStatus && (
          <div style={{
            backgroundColor: 'rgba(46, 160, 67, 0.15)',
            border: '1px solid rgba(46, 160, 67, 0.4)',
            borderRadius: '8px',
            color: '#3fb950',
            padding: '12px 18px',
            fontSize: '13px',
            fontWeight: '600'
          }}>
            ✅ {saveStatus}
          </div>
        )}

        {statusError && (
          <div style={{
            backgroundColor: 'rgba(248, 81, 73, 0.15)',
            border: '1px solid rgba(248, 81, 73, 0.4)',
            borderRadius: '8px',
            color: '#ff7b72',
            padding: '12px 18px',
            fontSize: '13px',
            fontWeight: '600'
          }}>
            ⚠️ {statusError}
          </div>
        )}

      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {/* Sub-tab Selectors (Sculptor vs Stylist vs Assets) */}
        {!hideTabHeaders && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          borderBottom: '1px solid var(--border-color)',
          paddingBottom: '0',
          gap: '4px',
          zIndex: 1,
          position: 'relative'
        }}>
          {/* LEFT: Tab buttons */}
          <div style={{ display: 'flex', gap: '4px', alignItems: 'flex-end' }}>
            {user?.role !== 'OPERATOR' && (
              <button
                type="button"
                onClick={() => { setStudioTab('coin'); setSelectedDesignId(null); }}
                style={{
                  background: studioTab === 'coin' ? 'var(--panel-color)' : 'transparent',
                  color: studioTab === 'coin' ? 'var(--primary-color)' : 'var(--text-mute)',
                  border: '1px solid transparent',
                  borderLeftColor: studioTab === 'coin' ? 'var(--border-color)' : 'transparent',
                  borderRightColor: studioTab === 'coin' ? 'var(--border-color)' : 'transparent',
                  borderTopColor: studioTab === 'coin' ? 'var(--border-color)' : 'transparent',
                  borderBottomColor: studioTab === 'coin' ? 'var(--panel-color)' : 'transparent',
                  borderRadius: '8px 8px 0 0',
                  padding: '8px 18px',
                  fontSize: '13px',
                  fontWeight: studioTab === 'coin' ? '600' : '500',
                  cursor: 'pointer',
                  marginBottom: '-1px',
                  transition: 'all 0.15s ease',
                  zIndex: studioTab === 'coin' ? 2 : 1,
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  boxShadow: studioTab === 'coin' ? '0 -3px 8px rgba(0, 0, 0, 0.08)' : 'none'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.color = 'var(--text-main)';
                  if (studioTab !== 'coin') {
                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                    e.currentTarget.style.borderLeftColor = 'var(--border-color)';
                    e.currentTarget.style.borderRightColor = 'var(--border-color)';
                    e.currentTarget.style.borderTopColor = 'var(--border-color)';
                  }
                }}
                onMouseOut={(e) => {
                  if (studioTab !== 'coin') {
                    e.currentTarget.style.color = 'var(--text-mute)';
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.borderLeftColor = 'transparent';
                    e.currentTarget.style.borderRightColor = 'transparent';
                    e.currentTarget.style.borderTopColor = 'transparent';
                  } else {
                    e.currentTarget.style.color = 'var(--primary-color)';
                  }
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.85 }}>
                  <circle cx="12" cy="12" r="10" />
                  <circle cx="12" cy="12" r="6" />
                  <path d="M12 8v8" />
                </svg>
                Coins
              </button>
            )}

            <button
              type="button"
              onClick={() => { setStudioTab('brag'); setSelectedDesignId(null); }}
              style={{
                background: studioTab === 'brag' ? 'var(--panel-color)' : 'transparent',
                color: studioTab === 'brag' ? 'var(--primary-color)' : 'var(--text-mute)',
                border: '1px solid transparent',
                borderLeftColor: studioTab === 'brag' ? 'var(--border-color)' : 'transparent',
                borderRightColor: studioTab === 'brag' ? 'var(--border-color)' : 'transparent',
                borderTopColor: studioTab === 'brag' ? 'var(--border-color)' : 'transparent',
                borderBottomColor: studioTab === 'brag' ? 'var(--panel-color)' : 'transparent',
                borderRadius: '8px 8px 0 0',
                padding: '8px 18px',
                fontSize: '13px',
                fontWeight: studioTab === 'brag' ? '600' : '500',
                cursor: 'pointer',
                marginBottom: '-1px',
                transition: 'all 0.15s ease',
                zIndex: studioTab === 'brag' ? 2 : 1,
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                boxShadow: studioTab === 'brag' ? '0 -3px 8px rgba(0, 0, 0, 0.08)' : 'none'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.color = 'var(--text-main)';
                if (studioTab !== 'brag') {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                  e.currentTarget.style.borderLeftColor = 'var(--border-color)';
                  e.currentTarget.style.borderRightColor = 'var(--border-color)';
                  e.currentTarget.style.borderTopColor = 'var(--border-color)';
                }
              }}
              onMouseOut={(e) => {
                if (studioTab !== 'brag') {
                  e.currentTarget.style.color = 'var(--text-mute)';
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.borderLeftColor = 'transparent';
                  e.currentTarget.style.borderRightColor = 'transparent';
                  e.currentTarget.style.borderTopColor = 'transparent';
                } else {
                  e.currentTarget.style.color = 'var(--primary-color)';
                }
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.85 }}>
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
                <polyline points="10 9 9 9 8 9" />
              </svg>
              Trophy Cards
            </button>

            {(isGlobalRole || isTenantAdmin) && (
              <button
                type="button"
                onClick={() => { setStudioTab('template_designer'); setSelectedDesignId(null); }}
                style={{
                  background: studioTab === 'template_designer' ? 'var(--panel-color)' : 'transparent',
                  color: studioTab === 'template_designer' ? 'var(--primary-color)' : 'var(--text-mute)',
                  border: '1px solid transparent',
                  borderLeftColor: studioTab === 'template_designer' ? 'var(--border-color)' : 'transparent',
                  borderRightColor: studioTab === 'template_designer' ? 'var(--border-color)' : 'transparent',
                  borderTopColor: studioTab === 'template_designer' ? 'var(--border-color)' : 'transparent',
                  borderBottomColor: studioTab === 'template_designer' ? 'var(--panel-color)' : 'transparent',
                  borderRadius: '8px 8px 0 0',
                  padding: '8px 18px',
                  fontSize: '13px',
                  fontWeight: studioTab === 'template_designer' ? '600' : '500',
                  cursor: 'pointer',
                  marginBottom: '-1px',
                  transition: 'all 0.15s ease',
                  zIndex: studioTab === 'template_designer' ? 2 : 1,
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  boxShadow: studioTab === 'template_designer' ? '0 -3px 8px rgba(0, 0, 0, 0.08)' : 'none'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.color = 'var(--text-main)';
                  if (studioTab !== 'template_designer') {
                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                    e.currentTarget.style.borderLeftColor = 'var(--border-color)';
                    e.currentTarget.style.borderRightColor = 'var(--border-color)';
                    e.currentTarget.style.borderTopColor = 'var(--border-color)';
                  }
                }}
                onMouseOut={(e) => {
                  if (studioTab !== 'template_designer') {
                    e.currentTarget.style.color = 'var(--text-mute)';
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.borderLeftColor = 'transparent';
                    e.currentTarget.style.borderRightColor = 'transparent';
                    e.currentTarget.style.borderTopColor = 'transparent';
                  } else {
                    e.currentTarget.style.color = 'var(--primary-color)';
                  }
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.85 }}>
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                </svg>
                Card Templates
              </button>
            )}

            {(user.tenant_id !== null || isTenantAdmin || isGlobalRole) && (
              <button
                type="button"
                onClick={() => { setStudioTab('face_templates'); setSelectedDesignId(null); }}
                style={{
                  background: studioTab === 'face_templates' ? 'var(--panel-color)' : 'transparent',
                  color: studioTab === 'face_templates' ? 'var(--primary-color)' : 'var(--text-mute)',
                  border: '1px solid transparent',
                  borderLeftColor: studioTab === 'face_templates' ? 'var(--border-color)' : 'transparent',
                  borderRightColor: studioTab === 'face_templates' ? 'var(--border-color)' : 'transparent',
                  borderTopColor: studioTab === 'face_templates' ? 'var(--border-color)' : 'transparent',
                  borderBottomColor: studioTab === 'face_templates' ? 'var(--panel-color)' : 'transparent',
                  borderRadius: '8px 8px 0 0',
                  padding: '8px 18px',
                  fontSize: '13px',
                  fontWeight: studioTab === 'face_templates' ? '600' : '500',
                  cursor: 'pointer',
                  marginBottom: '-1px',
                  transition: 'all 0.15s ease',
                  zIndex: studioTab === 'face_templates' ? 2 : 1,
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  boxShadow: studioTab === 'face_templates' ? '0 -3px 8px rgba(0, 0, 0, 0.08)' : 'none'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.color = 'var(--text-main)';
                  if (studioTab !== 'face_templates') {
                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                    e.currentTarget.style.borderLeftColor = 'var(--border-color)';
                    e.currentTarget.style.borderRightColor = 'var(--border-color)';
                    e.currentTarget.style.borderTopColor = 'var(--border-color)';
                  }
                }}
                onMouseOut={(e) => {
                  if (studioTab !== 'face_templates') {
                    e.currentTarget.style.color = 'var(--text-mute)';
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.borderLeftColor = 'transparent';
                    e.currentTarget.style.borderRightColor = 'transparent';
                    e.currentTarget.style.borderTopColor = 'transparent';
                  } else {
                    e.currentTarget.style.color = 'var(--primary-color)';
                  }
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.85 }}>
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 7v10" />
                  <path d="M8 12h8" />
                </svg>
                Face Templates
              </button>
            )}

            {/* Assets/Media Tab (always the last tab) */}
            <button
              type="button"
              onClick={() => { setStudioTab('assets'); setSelectedDesignId(null); }}
              style={{
                background: studioTab === 'assets' ? 'var(--panel-color)' : 'transparent',
                color: studioTab === 'assets' ? 'var(--primary-color)' : 'var(--text-mute)',
                border: '1px solid transparent',
                borderLeftColor: studioTab === 'assets' ? 'var(--border-color)' : 'transparent',
                borderRightColor: studioTab === 'assets' ? 'var(--border-color)' : 'transparent',
                borderTopColor: studioTab === 'assets' ? 'var(--border-color)' : 'transparent',
                borderBottomColor: studioTab === 'assets' ? 'var(--panel-color)' : 'transparent',
                borderRadius: '8px 8px 0 0',
                padding: '8px 18px',
                fontSize: '13px',
                fontWeight: studioTab === 'assets' ? '600' : '500',
                cursor: 'pointer',
                marginBottom: '-1px',
                transition: 'all 0.15s ease',
                zIndex: studioTab === 'assets' ? 2 : 1,
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                boxShadow: studioTab === 'assets' ? '0 -3px 8px rgba(0, 0, 0, 0.08)' : 'none'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.color = 'var(--text-main)';
                if (studioTab !== 'assets') {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                  e.currentTarget.style.borderLeftColor = 'var(--border-color)';
                  e.currentTarget.style.borderRightColor = 'var(--border-color)';
                  e.currentTarget.style.borderTopColor = 'var(--border-color)';
                }
              }}
              onMouseOut={(e) => {
                if (studioTab !== 'assets') {
                  e.currentTarget.style.color = 'var(--text-mute)';
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.borderLeftColor = 'transparent';
                  e.currentTarget.style.borderRightColor = 'transparent';
                  e.currentTarget.style.borderTopColor = 'transparent';
                } else {
                  e.currentTarget.style.color = 'var(--primary-color)';
                }
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.85 }}>
                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
              </svg>
              Assets/Media
            </button>
          </div>

          {/* RIGHT: Condensed Context Scope Dropdowns */}
          {(isGlobalRole || isTenantAdmin) && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              paddingBottom: '6px',
              fontSize: '12px'
            }}>
              <span style={{ fontWeight: '700', color: 'var(--text-mute)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Scope:</span>
              
              {/* Tenant Dropdown for Global Admin */}
              {isGlobalRole && (
                <select
                  value={selectedTenant}
                  onChange={(e) => {
                    setSelectedTenant(e.target.value);
                    setSelectedUserContext('self');
                    setSelectedDesignId(null);
                  }}
                  style={{
                    backgroundColor: 'var(--input-bg)',
                    border: '1px solid var(--input-border)',
                    borderRadius: '4px',
                    padding: '4px 8px',
                    color: 'var(--input-text)',
                    fontSize: '11px',
                    cursor: 'pointer',
                    maxWidth: '140px'
                  }}
                >
                  <option value="">Global / Unscoped</option>
                  {tenants.map(t => (
                    <option key={t.id} value={t.id.toString()}>{t.brand_name}</option>
                  ))}
                </select>
              )}

              {/* Owner Context Dropdown */}
              <select
                value={selectedUserContext}
                onChange={(e) => {
                  setSelectedUserContext(e.target.value);
                  setSelectedDesignId(null);
                }}
                style={{
                  backgroundColor: 'var(--input-bg)',
                  border: '1px solid var(--input-border)',
                  borderRadius: '4px',
                  padding: '4px 8px',
                  color: 'var(--input-text)',
                  fontSize: '11px',
                  cursor: 'pointer',
                  maxWidth: '180px'
                }}
              >
                <option value="self">Self ({isTenantAdmin ? 'Tenant-Wide' : 'Global' })</option>
                {operators.map(op => (
                  <option key={op.id} value={op.id.toString()}>{formatScopeUserLabel(op)}</option>
                ))}
              </select>
            </div>
          )}
        </div>
        )}

        <div style={{
          background: 'var(--panel-color)',
          border: '1px solid var(--border-color)',
          borderTop: hideTabHeaders ? '1px solid var(--border-color)' : 'none',
          borderRadius: hideTabHeaders ? '8px' : '0 0 8px 8px',
          padding: (studioTab === 'template_designer' || studioTab === 'face_templates') ? '0px' : '24px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
          position: 'relative',
          zIndex: 0
        }}>
          {studioTab === 'assets' ? (
          <AssetManager
            user={user}
            tenantConfig={tenantConfig}
            selectedTenant={selectedTenant}
            selectedUserContext={selectedUserContext}
          />

        ) : studioTab === 'template_designer' ? (
          <TemplateDesigner
            user={user}
            tenantConfig={tenantConfig}
            tenants={tenants}
            selectedTenant={selectedTenant}
          />
        ) : studioTab === 'face_templates' ? (
          <ErrorBoundary
            fallback={(error, reset) => (
              <div style={{
                padding: '40px',
                textAlign: 'center',
                backgroundColor: 'var(--card-bg, #1e1e22)',
                borderRadius: '12px',
                margin: '20px',
                border: '1px solid #7f1d1d'
              }}>
                <div style={{ fontSize: '36px', marginBottom: '12px' }}>⚠️</div>
                <h3 style={{ margin: '0 0 8px 0', color: '#f87171' }}>Failed to load Face Template Manager</h3>
                <p style={{ margin: '0 0 16px 0', color: '#94a3b8', fontSize: '13px' }}>
                  {error?.message || 'An unexpected rendering error occurred.'}
                </p>
                <button
                  type="button"
                  onClick={reset}
                  style={{
                    backgroundColor: 'var(--primary-color, #3b82f6)',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '8px 16px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Retry Loading Templates
                </button>
              </div>
            )}
          >
            <FaceTemplateManager
              user={user}
              tenantConfig={tenantConfig}
              tenants={tenants}
              selectedTenant={selectedTenant}
              selectedUserContext={selectedUserContext}
              isFullscreenFaceEditorOpen={isFullscreenFaceEditorOpen}
              refreshKey={faceTemplatesVersion}
              onEditFace={(design) => {
                handleLoadDesign(design);
                setIsFullscreenFaceEditorOpen(true);
              }}
              onCreateNewFace={async (name, side, metal) => {
                await handleCreateNewFaceTemplate(name, side, metal);
              }}
            />
          </ErrorBoundary>
        ) : studioTab === 'coin' ? (
          /* COINS GRID / PROOFER WORKSPACE */
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', width: '100%' }}>
            {coinViewMode === 'grid' ? (
              <>
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-color)', paddingBottom: '12px', flexWrap: 'wrap', gap: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: 'var(--text-main)' }}>Your Coins</h3>
                    <span style={{ fontSize: '11px', color: 'var(--text-mute)', fontStyle: 'italic' }}>
                      (Click to select • Double-click to open)
                    </span>
                  </div>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    {/* 3D Preview Button appears if a selected coin has body and accent filaments defined */}
                    {(() => {
                      const selectedGridCoin = physicalCoins.find(c => c.id === selectedGridCoinId);
                      if (selectedGridCoin && selectedGridCoin.body_filament && selectedGridCoin.accent_filament) {
                        return (
                          <button
                            type="button"
                            onClick={() => handleOpen3DPreviewForCoin(selectedGridCoin)}
                            style={{
                              backgroundColor: 'var(--primary-color)',
                              color: 'var(--button-primary-text, white)',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '6px 14px',
                              fontSize: '12px',
                              fontWeight: '700',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              boxShadow: '0 2px 10px rgba(0, 0, 0, 0.15)',
                              transition: 'all 0.15s ease'
                            }}
                            onMouseOver={(e) => e.currentTarget.style.filter = 'brightness(1.1)'}
                            onMouseOut={(e) => e.currentTarget.style.filter = 'none'}
                          >
                            <span>🎥</span>
                            <span>3D Preview</span>
                          </button>
                        );
                      }
                      return null;
                    })()}

                    {/* Scope Info */}
                    {user.tenant_id !== null && (
                      <span style={{ fontSize: '11px', color: 'var(--text-mute)' }}>
                        Scope: <strong>{selectedUserContext === 'self' ? 'Self' : (formatScopeUserLabel(operators.find(op => op.id.toString() === selectedUserContext)) || 'Selected User') }</strong>
                      </span>
                    )}
                    
                    {/* Filter for Administrators: Dropdown for Normal Coins (default), System Coins, Archived, All */}
                    {(isGlobalRole || isTenantAdmin) ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <label htmlFor="admin-coin-filter-select" style={{ fontSize: '11px', color: 'var(--text-mute)', fontWeight: '600' }}>
                          Filter:
                        </label>
                        <select
                          id="admin-coin-filter-select"
                          value={adminCoinFilter}
                          onChange={(e) => {
                            setAdminCoinFilter(e.target.value);
                            setSelectedGridCoinId(null);
                          }}
                          style={{
                            backgroundColor: 'var(--input-bg, #161b22)',
                            border: '1px solid var(--input-border, var(--border-color))',
                            borderRadius: '6px',
                            padding: '6px 10px',
                            color: 'var(--input-text, var(--text-main))',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            outline: 'none',
                            transition: 'border-color 0.15s ease'
                          }}
                        >
                          <option value="normal">
                            Normal Coins ({physicalCoins.filter(c => !c.is_system_coin && c.status !== 'archived').length})
                          </option>
                          <option value="system">
                            System Coins ({physicalCoins.filter(c => Boolean(c.is_system_coin) && c.status !== 'archived').length})
                          </option>
                          <option value="archived">
                            Archived ({physicalCoins.filter(c => c.status === 'archived').length})
                          </option>
                          <option value="all">
                            All ({physicalCoins.length})
                          </option>
                        </select>
                      </div>
                    ) : (
                      user.tenant_id !== null && user.role !== 'OPERATOR' && (
                        <button
                          type="button"
                          onClick={() => setShowArchivedCoins(!showArchivedCoins)}
                          style={{
                            backgroundColor: showArchivedCoins ? 'var(--primary-color)' : 'transparent',
                            color: showArchivedCoins ? 'white' : 'var(--text-mute)',
                            border: '1px solid var(--border-color)',
                            borderRadius: '6px',
                            padding: '6px 12px',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            transition: 'all 0.15s ease'
                          }}
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                          </svg>
                          {showArchivedCoins ? 'Hide Archived' : 'Show Archived'}
                        </button>
                      )
                    )}

                    {(isGlobalRole || (user && user.role === 'TENANT_ADMIN')) && (
                      <button
                        type="button"
                        onClick={handleCreateSystemCoin}
                        style={{
                          backgroundColor: 'var(--button-secondary-bg, rgba(255, 255, 255, 0.08))',
                          color: 'var(--button-secondary-text, var(--text-main))',
                          border: '1px solid var(--button-secondary-border, var(--border-color))',
                          borderRadius: '6px',
                          padding: '6px 14px',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          transition: 'all 0.15s ease'
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.borderColor = 'var(--primary-color)';
                          e.currentTarget.style.color = 'var(--primary-color)';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.borderColor = 'var(--button-secondary-border, var(--border-color))';
                          e.currentTarget.style.color = 'var(--button-secondary-text, var(--text-main))';
                        }}
                      >
                        <span>⚙</span>
                        <span>New System Coin</span>
                      </button>
                    )}

                    {user.tenant_id !== null && user.role !== 'OPERATOR' && (
                      <button
                        type="button"
                        onClick={handleCreatePhysicalCoin}
                        style={{
                          backgroundColor: 'var(--primary-color)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '6px 14px',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.filter = 'brightness(1.1)'}
                        onMouseOut={(e) => e.currentTarget.style.filter = 'none'}
                      >
                        + New
                      </button>
                    )}
                  </div>
                </div>

                {/* Coins Grid */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                  gap: '24px',
                  width: '100%'
                }}>
                  {(() => {
                    const isAdminUser = isGlobalRole || isTenantAdmin;
                    const filteredCoins = physicalCoins.filter(coin => {
                      if (isAdminUser) {
                        if (adminCoinFilter === 'normal') {
                          return !coin.is_system_coin && coin.status !== 'archived';
                        }
                        if (adminCoinFilter === 'system') {
                          return Boolean(coin.is_system_coin) && coin.status !== 'archived';
                        }
                        if (adminCoinFilter === 'archived') {
                          return coin.status === 'archived';
                        }
                        if (adminCoinFilter === 'all') {
                          return true;
                        }
                        return true;
                      }

                      // Non-admin view logic
                      if (coin.is_system_coin) {
                        return false;
                      }
                      if (coin.status === 'archived') {
                        if (user && user.role === 'OPERATOR') return false;
                        return showArchivedCoins;
                      }
                      return true;
                    });

                    if (filteredCoins.length === 0) {
                      let emptyMsg = "No physical coins found.";
                      if (isAdminUser) {
                        if (adminCoinFilter === 'normal') emptyMsg = "No normal physical coins found.";
                        else if (adminCoinFilter === 'system') emptyMsg = "No system coins found.";
                        else if (adminCoinFilter === 'archived') emptyMsg = "No archived coins found.";
                        else if (adminCoinFilter === 'all') emptyMsg = "No coins found.";
                      }
                      return (
                        <div style={{ gridColumn: '1/-1', padding: '60px 20px', textAlign: 'center', border: '1px dashed var(--border-color)', borderRadius: '12px', color: 'var(--text-mute)', width: '100%' }}>
                          {emptyMsg}
                        </div>
                      );
                    }

                    return filteredCoins.map((coin, index) => {
                      const headsDesign = designs.find(d => d.id === coin.face_1_design_id) || coin.face_1_design;
                      const tailsDesign = designs.find(d => d.id === coin.face_2_design_id) || coin.face_2_design;
                      const isSelected = selectedGridCoinId === coin.id;
                      
                      // Status colors
                      let badgeBg = 'var(--badge-bg, rgba(255,255,255,0.06))';
                      let badgeColor = 'var(--text-mute)';
                      if (coin.status === 'proofing') {
                        badgeBg = 'var(--info-bg, rgba(56, 139, 253, 0.15))';
                        badgeColor = 'var(--info-text, #0369a1)';
                      } else if (coin.status === 'approved') {
                        badgeBg = 'var(--success-bg, rgba(46, 160, 67, 0.15))';
                        badgeColor = 'var(--success-text, #15803d)';
                      } else if (coin.status === 'published') {
                        badgeBg = 'rgba(187, 128, 255, 0.15)';
                        badgeColor = 'var(--primary-color, #7c3aed)';
                      } else if (coin.status === 'archived') {
                        badgeBg = 'var(--badge-bg, rgba(110, 118, 129, 0.15))';
                        badgeColor = 'var(--badge-text, var(--text-mute, #8b949e))';
                      }

                      return (
                        <div
                          key={`coin-card-${coin.id}-${index}`}
                          onClick={() => setSelectedGridCoinId(coin.id)}
                          onDoubleClick={() => handleOpenCoin(coin)}
                          title="Click to select • Double-click to open"
                          style={{
                            border: isSelected ? '2px solid var(--primary-color)' : '1px solid var(--border-color)',
                            boxShadow: isSelected ? '0 0 0 2px rgba(56, 139, 253, 0.4), 0 8px 24px rgba(0,0,0,0.3)' : '0 4px 8px rgba(0,0,0,0.1)',
                            borderRadius: '12px',
                            backgroundColor: isSelected ? 'rgba(56, 139, 253, 0.05)' : 'var(--card-bg)',
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            padding: '16px',
                            position: 'relative',
                            transition: 'all 0.2s ease',
                            gap: '16px'
                          }}
                          onMouseOver={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.borderColor = 'var(--primary-color)';
                            }
                            e.currentTarget.style.transform = 'translateY(-2px)';
                          }}
                          onMouseOut={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.borderColor = 'var(--border-color)';
                            }
                            e.currentTarget.style.transform = 'none';
                          }}
                        >
                          {/* Top Status */}
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1, overflow: 'hidden' }}>
                              {isSelected && (
                                <span style={{
                                  fontSize: '9px',
                                  fontWeight: '800',
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  backgroundColor: 'var(--primary-color)',
                                  color: '#ffffff',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '3px',
                                  flexShrink: 0
                                }}>
                                  ✓ SELECTED
                                </span>
                              )}
                              <span style={{ fontSize: '10px', color: 'var(--text-mute)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                Owner: {coin.associated_user?.email || 'System'}
                              </span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              {coin.is_system_coin && (
                                <span style={{
                                  fontSize: '9px',
                                  fontWeight: '700',
                                  padding: '2px 8px',
                                  borderRadius: '10px',
                                  backgroundColor: 'var(--badge-bg, rgba(255, 255, 255, 0.08))',
                                  color: 'var(--accent-color, var(--primary-color))',
                                  border: '1px solid var(--border-color)',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}>
                                  ⚙ System Coin
                                </span>
                              )}
                              <span style={{
                                fontSize: '9px',
                                fontWeight: '700',
                                padding: '2px 8px',
                                borderRadius: '10px',
                                backgroundColor: badgeBg,
                                color: badgeColor,
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                              }}>
                                {coin.status}
                              </span>

                              {/* Archive button in Grid (Visible to clients/tenant admins for approved/published coins) */}
                              {(coin.status === 'approved' || coin.status === 'published') && user.tenant_id !== null && user.role !== 'OPERATOR' && (
                                <button
                                  type="button"
                                  title="Archive Coin"
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    const confirmed = await modal.confirm("Are you sure you want to archive this coin? Once archived, it will be hidden from your grid.", {
                                      title: "Archive Coin",
                                      confirmText: "Archive",
                                      danger: true
                                    });
                                    if (!confirmed) return;
                                    try {
                                      const res = await fetch(`${API_BASE}/api/v1/physical-coins/${coin.id}`, {
                                        method: 'PATCH',
                                        headers: {
                                          'Content-Type': 'application/json',
                                          'Authorization': `Bearer ${token}`,
                                          'X-Tenant-Domain': window.location.hostname
                                        },
                                        body: JSON.stringify({ status: 'archived' })
                                      });
                                      if (!res.ok) throw new Error("Failed to archive coin");

                                      await fetch(`${API_BASE}/api/v1/chat/coin/${coin.id}`, {
                                        method: 'POST',
                                        headers: {
                                          'Content-Type': 'application/json',
                                          'Authorization': `Bearer ${token}`
                                        },
                                        body: JSON.stringify({ message_text: `📁 Coin archived by user (${user?.email || 'unknown user'}).` })
                                      });

                                      alert("Coin archived successfully.");
                                      fetchPhysicalCoins();
                                    } catch (err) {
                                      alert(err.message);
                                    }
                                  }}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontSize: '11px',
                                    color: '#ff7b72',
                                    fontWeight: '700',
                                    padding: '2px 4px',
                                    lineHeight: 1
                                  }}
                                >
                                  ✕
                                </button>
                              )}
                            </div>
                          </div>

                          {/* Heads & Tails previews */}
                          <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', margin: '8px 0' }}>
                            {/* Heads Side */}
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                              {headsDesign ? (
                                <div style={{ width: '70px', height: '70px', borderRadius: '50%', overflow: 'hidden', border: '1px solid var(--border-color)' }}>
                                  {headsDesign.config?.previewImage ? (
                                    <img src={headsDesign.config.previewImage} alt="Heads" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                  ) : (
                                    <div style={{ width: '100%', height: '100%', backgroundColor: 'var(--border-color)' }} />
                                  )}
                                </div>
                              ) : (
                                <div style={{
                                  width: '70px',
                                  height: '70px',
                                  borderRadius: '50%',
                                  border: '2px dashed var(--border-color)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: 'var(--text-mute)',
                                  fontSize: '10px',
                                  fontWeight: 'bold'
                                }}>
                                  Blank
                                </div>
                              )}
                              <span style={{ fontSize: '9px', color: 'var(--text-mute)' }}>Heads (Face 1)</span>
                            </div>

                            {/* Tails Side */}
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                              {tailsDesign ? (
                                <div style={{ width: '70px', height: '70px', borderRadius: '50%', overflow: 'hidden', border: '1px solid var(--border-color)' }}>
                                  {tailsDesign.config?.previewImage ? (
                                    <img src={tailsDesign.config.previewImage} alt="Tails" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                  ) : (
                                    <div style={{ width: '100%', height: '100%', backgroundColor: 'var(--border-color)' }} />
                                  )}
                                </div>
                              ) : (
                                <div style={{
                                  width: '70px',
                                  height: '70px',
                                  borderRadius: '50%',
                                  border: '2px dashed var(--border-color)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: 'var(--text-mute)',
                                  fontSize: '10px',
                                  fontWeight: 'bold'
                                }}>
                                  Blank
                                </div>
                              )}
                              <span style={{ fontSize: '9px', color: 'var(--text-mute)' }}>Tails (Face 2)</span>
                            </div>
                          </div>

                          {/* Bottom metadata */}
                          <div style={{ textAlign: 'center', borderTop: '1px solid var(--border-color)', paddingTop: '10px' }}>
                            <div style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-main)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {coin.name}
                            </div>
                            <div style={{ fontSize: '10px', color: 'var(--text-mute)', marginTop: '2px' }}>
                              Coin #{coin.id} {coin.status === 'new' && '• 🛠️ Launch Setup'}
                            </div>
                            {(coin.body_filament || coin.accent_filament) && (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '8px', fontSize: '10px', textAlign: 'left', backgroundColor: 'var(--input-bg)', padding: '6px 8px', borderRadius: '6px', border: '1px solid var(--border-color)' }}>
                                {coin.body_filament && (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', overflow: 'hidden' }}>
                                    <span style={{ fontWeight: '700', color: 'var(--text-mute)', fontSize: '9px', textTransform: 'uppercase' }}>Body:</span>
                                    <span style={{ color: 'var(--text-main)', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>{coin.body_filament}</span>
                                  </div>
                                )}
                                {coin.accent_filament && (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', overflow: 'hidden' }}>
                                    <span style={{ fontWeight: '700', color: 'var(--text-mute)', fontSize: '9px', textTransform: 'uppercase' }}>Accent:</span>
                                    <span style={{ color: 'var(--text-main)', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>{coin.accent_filament}</span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </>
            ) : (
              /* COIN PROOFER WORKSPACE */
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', width: '100%' }}>
                {/* Proofer Toolbar */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-color)', paddingBottom: '12px' }}>
                  <button
                    type="button"
                    onClick={() => { setCoinViewMode('grid'); setSelectedCoin(null); fetchPhysicalCoins(); }}
                    style={{
                      backgroundColor: 'transparent',
                      border: '1px solid var(--border-color)',
                      color: 'var(--text-main)',
                      borderRadius: '6px',
                      padding: '6px 12px',
                      fontSize: '12px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    Back to Coins
                  </button>

                  <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-main)' }}>
                    Coin Proofer - {selectedCoin.name} (Coin #{selectedCoin.id})
                  </div>

                  <div style={{ display: 'flex', gap: '8px' }}>
                    {/* Re-enter Setup to modify coin properties / filaments */}
                    {selectedCoin.status === 'proofing' && (
                      <button
                        type="button"
                        onClick={() => {
                          setWizardCoinName(selectedCoin.name || '');
                          setWizardHeadsDesignId(selectedCoin.face_1_design_id || '');
                          setWizardTailsDesignId(selectedCoin.face_2_design_id || '');
                          setWizardIsSystemCoin(Boolean(selectedCoin.is_system_coin));
                          setWizardBodyFilament(selectedCoin.body_filament || '');
                          setWizardAccentFilament(selectedCoin.accent_filament || '');
                          setIsSetupWizardOpen(true);
                        }}
                        style={{
                          backgroundColor: 'transparent',
                          border: '1px solid var(--border-color)',
                          color: 'var(--text-main)',
                          borderRadius: '6px',
                          padding: '6px 12px',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        ⚙️ Edit Setup
                      </button>
                    )}

                    {/* Tenant User Action Toggles */}
                    {selectedCoin.status === 'proofing' && (
                      <button
                        type="button"
                        onClick={() => setIsCoinApprovalOpen(true)}
                        style={{
                          backgroundColor: 'var(--success-color, #2ea44f)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '6px 14px',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        Approve Coin
                      </button>
                    )}

                    {(selectedCoin.status === 'approved' || selectedCoin.status === 'published') && user.tenant_id !== null && user.role !== 'OPERATOR' && (
                      <button
                        type="button"
                        onClick={async () => {
                          const confirmed = await modal.confirm("Are you sure you want to revoke approval for this coin?", {
                            title: "Revoke Coin Approval",
                            confirmText: "Revoke Approval",
                            danger: true
                          });
                          if (!confirmed) return;
                          try {
                            const res = await fetch(`${API_BASE}/api/v1/physical-coins/${selectedCoin.id}`, {
                              method: 'PATCH',
                              headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`,
                                'X-Tenant-Domain': window.location.hostname
                              },
                              body: JSON.stringify({ status: 'proofing' })
                            });
                            if (!res.ok) throw new Error("Failed to revoke approval");

                            // Post audit log
                            await fetch(`${API_BASE}/api/v1/chat/coin/${selectedCoin.id}`, {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`
                              },
                              body: JSON.stringify({ message_text: `[AUDIT] Coin approval revoked by ${user.email}. Status returned to proofing.` })
                            });

                            fetchCoinDetail(selectedCoin.id);
                            alert("Coin approval revoked.");
                          } catch (err) {
                            alert(err.message);
                          }
                        }}
                        style={{
                          backgroundColor: '#ff7b72',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '6px 14px',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        Revoke Approval
                      </button>
                    )}

                    {/* Unarchive Option */}

                    {user.tenant_id !== null && user.role !== 'OPERATOR' && selectedCoin.status === 'archived' && (
                      <button
                        type="button"
                        onClick={async () => {
                          const confirmed = await modal.confirm("Are you sure you want to unarchive this coin?", {
                            title: "Unarchive Coin",
                            confirmText: "Unarchive"
                          });
                          if (!confirmed) return;
                          try {
                            const res = await fetch(`${API_BASE}/api/v1/physical-coins/${selectedCoin.id}`, {
                              method: 'PATCH',
                              headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`,
                                'X-Tenant-Domain': window.location.hostname
                              },
                              body: JSON.stringify({ status: 'proofing' })
                            });
                            if (!res.ok) throw new Error("Failed to unarchive coin");

                            await fetch(`${API_BASE}/api/v1/chat/coin/${selectedCoin.id}`, {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`
                              },
                              body: JSON.stringify({ message_text: `🔓 Coin unarchived by client/operator (${user?.email || 'unknown user'}).` })
                            });

                            fetchCoinDetail(selectedCoin.id);
                            alert("Coin unarchived successfully.");
                          } catch (err) {
                            alert(err.message);
                          }
                        }}
                        style={{
                          backgroundColor: 'var(--success-color, #2ea44f)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '6px 14px',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        Unarchive
                      </button>
                    )}

                    {/* Operator/Admin Publish Option */}
                    {selectedCoin.status === 'approved' && (isTenantAdmin || isGlobalRole || user.role === 'OPERATOR') && (
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            const res = await fetch(`${API_BASE}/api/v1/physical-coins/${selectedCoin.id}`, {
                              method: 'PATCH',
                              headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`,
                                'X-Tenant-Domain': window.location.hostname
                              },
                              body: JSON.stringify({ status: 'published' })
                            });
                            if (!res.ok) throw new Error("Failed to publish coin");

                            // Post audit log
                            await fetch(`${API_BASE}/api/v1/chat/coin/${selectedCoin.id}`, {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`
                              },
                              body: JSON.stringify({ message_text: `[AUDIT] Coin marked as PUBLISHED/MINTED by ${user.email}.` })
                            });

                            fetchCoinDetail(selectedCoin.id);
                            alert("Coin successfully published!");
                          } catch (err) {
                            alert(err.message);
                          }
                        }}
                        style={{
                          backgroundColor: 'var(--primary-color)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '6px 14px',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        Publish Coin
                      </button>
                    )}
                  </div>
                </div>

                {/* Main Split-Screen Panel */}
                <div style={{ display: 'flex', gap: '20px', alignItems: 'stretch', width: '100%', minHeight: '520px' }}>
                  {/* Left Column: Side-by-Side Face Previews / Empty State */}
                  {(!selectedCoin.face_1_design_id || !selectedCoin.face_2_design_id) ? (
                    <div style={{
                      flex: 1.2,
                      background: 'var(--panel-color)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '12px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '40px',
                      color: 'var(--text-mute)',
                      textAlign: 'center',
                      minHeight: '520px'
                    }}>
                      <div style={{ fontSize: '48px', marginBottom: '16px', userSelect: 'none' }}>🪙</div>
                      <h4 style={{ margin: '0 0 8px 0', color: '#fff', fontSize: '16px', fontWeight: '600' }}>Preview Panel Hidden</h4>
                      <p style={{ margin: 0, fontSize: '13px', lineHeight: '1.5', maxWidth: '320px' }}>
                        Both Heads (front) and Tails (back) faces must be selected for this coin before the preview panel and 3D experience can be displayed.
                      </p>
                    </div>
                  ) : (
                    <div style={{
                      flex: 1.2,
                      backgroundImage: 'conic-gradient(#2b2b2f 25%, transparent 25% 50%, #2b2b2f 50% 75%, transparent 75%)',
                      backgroundSize: '24px 24px',
                      backgroundColor: '#1a1a1c',
                      border: '1px solid var(--border-color)',
                      borderRadius: '12px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '24px',
                      gap: '24px'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'center', gap: '30px', flexWrap: 'wrap', width: '100%' }}>
                      {(() => {
                        const hDesign = designs.find(d => d.id === selectedCoin.face_1_design_id) || selectedCoin.face_1_design;
                        const tDesign = designs.find(d => d.id === selectedCoin.face_2_design_id) || selectedCoin.face_2_design;

                        const renderCoinFaceSVG = (design, title = 'Heads') => {
                          if (!design) {
                            return (
                              <div style={{
                                width: '180px',
                                height: '180px',
                                borderRadius: '50%',
                                border: '2px dashed var(--border-color)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'var(--text-mute)',
                                fontSize: '12px'
                              }}>
                                No {title} Face
                              </div>
                            );
                          }
                          const cfg = (typeof design.config === 'object' && design.config !== null) ? design.config : {};
                          const coinMetal = cfg.metal || 'gold';
                          const coinSymbol = cfg.symbol || '';
                          const coinMetalConfig = getSafeCoinMetalConfig(coinMetal);

                          return (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                              <div
                                onClick={async () => {
                                  let targetDesign = design;
                                  const isLegacyTemplateRef = (
                                    cfg.is_template !== false &&
                                    cfg.is_template !== 'false' &&
                                    !cfg.coin_id
                                  );
                                  if (isLegacyTemplateRef && selectedCoin?.id && selectedCoin.id !== 'new') {
                                    try {
                                      const patchRes = await fetch(`${API_BASE}/api/v1/physical-coins/${selectedCoin.id}`, {
                                        method: 'PATCH',
                                        headers: {
                                          'Content-Type': 'application/json',
                                          'Authorization': `Bearer ${token}`,
                                          'X-Tenant-Domain': window.location.hostname
                                        },
                                        body: JSON.stringify({
                                          face_1_design_id: selectedCoin.face_1_design_id,
                                          face_2_design_id: selectedCoin.face_2_design_id
                                        })
                                      });
                                      if (patchRes.ok) {
                                        const patchedCoin = await patchRes.json();
                                        setSelectedCoin(patchedCoin);
                                        fetchPhysicalCoins();
                                        fetchDesigns();
                                        const clonedFace = title === 'Heads' ? patchedCoin.face_1_design : patchedCoin.face_2_design;
                                        if (clonedFace) {
                                          targetDesign = clonedFace;
                                        }
                                      }
                                    } catch (cloneErr) {
                                      console.warn('Could not auto-clone legacy coin face before editing:', cloneErr);
                                    }
                                  }
                                  handleLoadDesign(targetDesign);
                                  setIsFullscreenFaceEditorOpen(true);
                                }}
                                style={{
                                  width: '180px',
                                  height: '180px',
                                  borderRadius: '50%',
                                  overflow: 'hidden',
                                  boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                                  position: 'relative',
                                  cursor: 'pointer'
                                }}
                                title="Click to edit design in fullscreen editor"
                              >
                                {cfg.previewImage ? (
                                  <img
                                    src={cfg.previewImage}
                                    alt={design.name}
                                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                  />
                                ) : (
                                  <svg viewBox="0 0 200 200" style={{ width: '100%', height: '100%', overflow: 'visible' }}>
                                    {coinMetal === 'white' ? (
                                      <circle cx="100" cy="100" r="95" fill="white" />
                                    ) : (
                                      <>
                                        <circle cx="100" cy="100" r="95" fill={coinMetalConfig?.rim || '#ca8a04'} />
                                        <circle cx="100" cy="100" r="85" fill={coinMetalConfig?.base || '#854d0e'} />
                                        {coinSymbol && (coinSymbol.startsWith('http') || coinSymbol.includes('.') || coinSymbol.includes('/')) ? (
                                          <image href={coinSymbol} x="59" y="59" width="82" height="82" />
                                        ) : (
                                          <text x="100" y="115" fontSize="50" textAnchor="middle">{coinSymbol}</text>
                                        )}
                                      </>
                                    )}
                                  </svg>
                                )}
                              </div>
                              <span style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-mute)' }}>{title}: {design.name}</span>
                            </div>
                          );
                        };

                        return (
                          <>
                            {renderCoinFaceSVG(hDesign, 'Heads')}
                            {renderCoinFaceSVG(tDesign, 'Tails')}
                          </>
                        );
                      })()}
                    </div>

                    {/* Controls container below previews */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '20px',
                      marginTop: '8px',
                      backgroundColor: 'rgba(0,0,0,0.4)',
                      padding: '10px 20px',
                      borderRadius: '8px',
                      border: '1px solid var(--border-color)',
                      flexWrap: 'wrap',
                      justifyContent: 'center'
                    }}>
                      {/* 3D Preview Button */}
                      <button
                        type="button"
                        onClick={() => {
                          if (selectedCoin?.body_filament && coinBodyColor === '#1A1A1A') {
                            setCoinBodyColor(resolveFilamentHex(selectedCoin.body_filament, '#1A1A1A'));
                          }
                          if (selectedCoin?.accent_filament && coinAccentColor === '#D4AF37') {
                            setCoinAccentColor(resolveFilamentHex(selectedCoin.accent_filament, '#D4AF37'));
                          }
                          setIsCoin3DPreviewOpen(true);
                        }}
                        style={{
                          backgroundColor: 'var(--primary-color)',
                          color: 'var(--button-primary-text, white)',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '8px 18px',
                          fontSize: '12px',
                          fontWeight: '700',
                          cursor: 'pointer',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          transition: 'all 0.15s ease'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.filter = 'brightness(1.1)'}
                        onMouseOut={(e) => e.currentTarget.style.filter = 'none'}
                      >
                        🎥 3D Preview Coin
                      </button>

                      {/* Color Pickers with Filament Labels */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }} title={selectedCoin.body_filament ? `Assigned Body Filament: ${selectedCoin.body_filament}` : 'Body Color'}>
                          <label style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-mute)', textTransform: 'uppercase' }}>Body</label>
                          <input
                            type="color"
                            value={coinBodyColor}
                            onChange={(e) => setCoinBodyColor(e.target.value)}
                            style={{
                              width: '28px',
                              height: '28px',
                              border: '1px solid var(--border-color)',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              backgroundColor: 'transparent',
                              padding: 0
                            }}
                          />
                          {selectedCoin.body_filament && (
                            <span style={{ fontSize: '11px', color: 'var(--text-main)', maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={selectedCoin.body_filament}>
                              {selectedCoin.body_filament.split(' - ')[1] || selectedCoin.body_filament}
                            </span>
                          )}
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }} title={selectedCoin.accent_filament ? `Assigned Accent Filament: ${selectedCoin.accent_filament}` : 'Accent Color'}>
                          <label style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-mute)', textTransform: 'uppercase' }}>Accent</label>
                          <input
                            type="color"
                            value={coinAccentColor}
                            onChange={(e) => setCoinAccentColor(e.target.value)}
                            style={{
                              width: '28px',
                              height: '28px',
                              border: '1px solid var(--border-color)',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              backgroundColor: 'transparent',
                              padding: 0
                            }}
                          />
                          {selectedCoin.accent_filament && (
                            <span style={{ fontSize: '11px', color: 'var(--text-main)', maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={selectedCoin.accent_filament}>
                              {selectedCoin.accent_filament.split(' - ')[1] || selectedCoin.accent_filament}
                            </span>
                          )}
                        </div>

                        {(selectedCoin.body_filament || selectedCoin.accent_filament) && (
                          <button
                            type="button"
                            onClick={() => {
                              const b = resolveFilamentHex(selectedCoin.body_filament, '#1A1A1A');
                              const a = resolveFilamentHex(selectedCoin.accent_filament, '#D4AF37');
                              setCoinBodyColor(b);
                              setCoinAccentColor(a);
                            }}
                            title="Reset colors to assigned production filaments"
                            style={{
                              backgroundColor: 'transparent',
                              border: '1px solid var(--border-color)',
                              color: 'var(--text-mute)',
                              borderRadius: '4px',
                              padding: '4px 8px',
                              fontSize: '10px',
                              fontWeight: '600',
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                          >
                            ↺ Reset Filaments
                          </button>
                        )}
                      </div>

                      {/* Invert Toggle Button */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <label style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-mute)', textTransform: 'uppercase', userSelect: 'none', cursor: 'pointer' }}>Invert</label>
                        <input
                          type="checkbox"
                          checked={coinInvert}
                          onChange={(e) => setCoinInvert(e.target.checked)}
                          style={{
                            width: '18px',
                            height: '18px',
                            cursor: 'pointer',
                            accentColor: '#0969da'
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  )}

                  {/* Right Column: Support Chat / Production Files Tabbed Panel */}
                  <div style={{
                    flex: 1,
                    background: 'var(--panel-color)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '12px',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '520px',
                    overflow: 'hidden'
                  }}>
                    {/* Tab Selection */}
                    <div style={{ display: 'flex', borderBottom: '1px solid var(--border-color)', backgroundColor: 'var(--bg-color)' }}>
                      <button
                        type="button"
                        onClick={() => setCoinSupportTab('chat')}
                        style={{
                          flex: 1,
                          padding: '12px',
                          border: 'none',
                          backgroundColor: coinSupportTab === 'chat' ? 'var(--card-bg)' : 'transparent',
                          color: coinSupportTab === 'chat' ? 'var(--primary-color)' : 'var(--text-mute)',
                          fontWeight: '600',
                          fontSize: '12px',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease'
                        }}
                      >
                        Chat & Support
                      </button>
                      <button
                        type="button"
                        onClick={() => setCoinSupportTab('files')}
                        style={{
                          flex: 1,
                          padding: '12px',
                          border: 'none',
                          backgroundColor: coinSupportTab === 'files' ? 'var(--card-bg)' : 'transparent',
                          color: coinSupportTab === 'files' ? 'var(--primary-color)' : 'var(--text-mute)',
                          fontWeight: '600',
                          fontSize: '12px',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease'
                        }}
                      >
                        Production Files
                      </button>
                    </div>

                    {/* Content View */}
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                      {coinSupportTab === 'chat' ? (
                        /* CHAT THREAD */
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                          <div
                            ref={chatContainerRef}
                            style={{
                              flex: 1,
                              overflowY: 'auto',
                              padding: '16px',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '12px'
                            }}
                          >
                            {coinChatMessages.map((msg) => {
                              const isSelf = msg.sender_id === user.id;
                              const isAudit = msg.message_text.startsWith('[AUDIT]');
                              
                              if (isAudit) {
                                return (
                                  <div
                                    key={msg.id}
                                    style={{
                                      alignSelf: 'center',
                                      backgroundColor: 'rgba(56, 139, 253, 0.1)',
                                      color: '#58a6ff',
                                      border: '1px solid rgba(56, 139, 253, 0.2)',
                                      borderRadius: '6px',
                                      padding: '6px 12px',
                                      fontSize: '10px',
                                      maxWidth: '90%',
                                      textAlign: 'center'
                                    }}
                                  >
                                    {msg.message_text.replace('[AUDIT] ', '')}
                                  </div>
                                );
                              }

                              return (
                                <div
                                  key={msg.id}
                                  style={{
                                    alignSelf: isSelf ? 'flex-end' : 'flex-start',
                                    backgroundColor: isSelf ? 'rgba(56, 139, 253, 0.15)' : '#2a2b30',
                                    color: isSelf ? '#e8f3ff' : '#f5f5f7',
                                    border: isSelf ? '1px solid rgba(56, 139, 253, 0.3)' : '1px solid #3e4046',
                                    borderRadius: '12px',
                                    padding: '8px 12px',
                                    maxWidth: '80%',
                                    position: 'relative',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                  }}
                                >
                                  {!isSelf && (
                                    <div style={{ fontSize: '9px', color: 'var(--text-mute)', marginBottom: '4px', fontWeight: 'bold' }}>
                                      {msg.sender?.first_name || msg.sender?.email || 'Support'}
                                    </div>
                                  )}
                                  <div style={{ fontSize: '11px', lineHeight: '1.4', whiteSpace: 'pre-wrap' }}>
                                    {msg.message_text}
                                  </div>
                                  <div style={{ fontSize: '8px', color: 'var(--text-mute)', textAlign: 'right', marginTop: '4px' }}>
                                    {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          {/* Message input */}
                          <form
                            onSubmit={async (e) => {
                              e.preventDefault();
                              if (!coinChatMessage.trim()) return;
                              const textToSend = coinChatMessage;
                              setCoinChatMessage('');

                              try {
                                const res = await fetch(`${API_BASE}/api/v1/chat/coin/${selectedCoin.id}`, {
                                  method: 'POST',
                                  headers: {
                                    'Content-Type': 'application/json',
                                    'Authorization': `Bearer ${token}`
                                  },
                                  body: JSON.stringify({ message_text: textToSend })
                                });
                                if (res.ok) {
                                  fetchCoinChatMessages(selectedCoin.id);
                                }
                              } catch (err) {
                                console.error(err);
                              }
                            }}
                            style={{
                              padding: '12px',
                              borderTop: '1px solid var(--border-color)',
                              display: 'flex',
                              gap: '8px',
                              backgroundColor: 'var(--bg-color)'
                            }}
                          >
                            <input
                              type="text"
                              value={coinChatMessage}
                              onChange={(e) => setCoinChatMessage(e.target.value)}
                              placeholder="Type a message to support..."
                              style={{
                                flex: 1,
                                backgroundColor: 'var(--card-bg)',
                                border: '1px solid var(--border-color)',
                                borderRadius: '6px',
                                color: 'var(--text-main)',
                                padding: '8px 12px',
                                fontSize: '11px'
                              }}
                            />
                            <button
                              type="submit"
                              style={{
                                backgroundColor: 'var(--primary-color)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                padding: '8px 14px',
                                fontSize: '11px',
                                fontWeight: '600',
                                cursor: 'pointer'
                              }}
                            >
                              Send
                            </button>
                          </form>
                        </div>
                      ) : (
                        /* PRODUCTION FILES */
                        <div style={{ flex: 1, overflowY: 'auto', padding: '20px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
                          {/* 1. Preview File */}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', padding: '16px', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid var(--border-color)', borderRadius: '8px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-main)' }}>3D Preview Render File</span>
                              {selectedCoin.preview_file ? (
                                <a
                                  href={selectedCoin.preview_file}
                                  target="_blank"
                                  rel="noreferrer"
                                  style={{
                                    fontSize: '11px',
                                    color: 'var(--primary-color)',
                                    fontWeight: '600',
                                    textDecoration: 'none'
                                  }}
                                >
                                  Download File
                                </a>
                              ) : (
                                <span style={{ fontSize: '10px', color: 'var(--text-mute)' }}>Not generated yet</span>
                              )}
                            </div>
                            
                            {/* Upload widget for operators */}
                            {(isTenantAdmin || isGlobalRole || user.role === 'OPERATOR') && (
                              <div style={{ marginTop: '8px', borderTop: '1px dashed var(--border-color)', paddingTop: '10px' }}>
                                <label style={{ fontSize: '10px', color: 'var(--text-mute)', display: 'block', marginBottom: '6px' }}>
                                  Upload 3D Preview (.png, .jpg):
                                </label>
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={async (e) => {
                                    const file = e.target.files[0];
                                    if (!file) return;
                                    const formData = new FormData();
                                    formData.append('file', file);
                                    try {
                                      const uRes = await fetch(`${API_BASE}/api/upload`, {
                                        method: 'POST',
                                        headers: { 'Authorization': `Bearer ${token}` },
                                        body: formData
                                      });
                                      const uData = await uRes.json();
                                      if (!uRes.ok) throw new Error(uData.detail || 'Upload failed');
                                      
                                      const fileUrl = uData.file_url || `${API_BASE}/${uData.location}`;
                                      
                                      const pRes = await fetch(`${API_BASE}/api/v1/physical-coins/${selectedCoin.id}`, {
                                        method: 'PATCH',
                                        headers: {
                                          'Content-Type': 'application/json',
                                          'Authorization': `Bearer ${token}`,
                                          'X-Tenant-Domain': window.location.hostname
                                        },
                                        body: JSON.stringify({ preview_file: fileUrl })
                                      });
                                      if (!pRes.ok) throw new Error("Failed to save preview link");
                                      
                                      // Post audit log
                                      await fetch(`${API_BASE}/api/v1/chat/coin/${selectedCoin.id}`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                                        body: JSON.stringify({ message_text: `[AUDIT] Production 3D preview render uploaded by ${user.email}.` })
                                      });

                                      fetchCoinDetail(selectedCoin.id);
                                      alert("Preview render uploaded and associated successfully!");
                                    } catch (err) {
                                      alert(err.message);
                                    }
                                  }}
                                  style={{ fontSize: '10px', color: 'var(--text-main)' }}
                                />
                              </div>
                            )}
                          </div>

                          {/* 2. STL Production File */}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', padding: '16px', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid var(--border-color)', borderRadius: '8px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-main)' }}>3D STL Model File</span>
                              {selectedCoin.stl_file ? (
                                <a
                                  href={selectedCoin.stl_file}
                                  target="_blank"
                                  rel="noreferrer"
                                  style={{
                                    fontSize: '11px',
                                    color: 'var(--primary-color)',
                                    fontWeight: '600',
                                    textDecoration: 'none'
                                  }}
                                >
                                  Download STL File
                                </a>
                              ) : (
                                <span style={{ fontSize: '10px', color: 'var(--text-mute)' }}>Not generated yet</span>
                              )}
                            </div>

                            {/* Upload widget for operators */}
                            {(isTenantAdmin || isGlobalRole || user.role === 'OPERATOR') && (
                              <div style={{ marginTop: '8px', borderTop: '1px dashed var(--border-color)', paddingTop: '10px' }}>
                                <label style={{ fontSize: '10px', color: 'var(--text-mute)', display: 'block', marginBottom: '6px' }}>
                                  Upload Production STL:
                                </label>
                                <input
                                  type="file"
                                  onChange={async (e) => {
                                    const file = e.target.files[0];
                                    if (!file) return;
                                    const formData = new FormData();
                                    formData.append('file', file);
                                    try {
                                      const uRes = await fetch(`${API_BASE}/api/upload`, {
                                        method: 'POST',
                                        headers: { 'Authorization': `Bearer ${token}` },
                                        body: formData
                                      });
                                      const uData = await uRes.json();
                                      if (!uRes.ok) throw new Error(uData.detail || 'Upload failed');
                                      
                                      const fileUrl = uData.file_url || `${API_BASE}/${uData.location}`;
                                      
                                      const pRes = await fetch(`${API_BASE}/api/v1/physical-coins/${selectedCoin.id}`, {
                                        method: 'PATCH',
                                        headers: {
                                          'Content-Type': 'application/json',
                                          'Authorization': `Bearer ${token}`,
                                          'X-Tenant-Domain': window.location.hostname
                                        },
                                        body: JSON.stringify({ stl_file: fileUrl })
                                      });
                                      if (!pRes.ok) throw new Error("Failed to save STL link");
                                      
                                      // Post audit log
                                      await fetch(`${API_BASE}/api/v1/chat/coin/${selectedCoin.id}`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                                        body: JSON.stringify({ message_text: `[AUDIT] Production 3D STL file model uploaded by ${user.email}.` })
                                      });

                                      fetchCoinDetail(selectedCoin.id);
                                      alert("STL production file uploaded successfully!");
                                    } catch (err) {
                                      alert(err.message);
                                    }
                                  }}
                                  style={{ fontSize: '10px', color: 'var(--text-main)' }}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          /* TROPHY CARDS WORKSPACE */
          <>
            {bragViewMode === 'grid' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', width: '100%' }}>
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-color)', paddingBottom: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: 'var(--text-main)' }}>Your Trophy Cards</h3>
                    
                    {/* Show Archived Cards Toggle (Tenant Users Only) */}
                    {user && user.role !== 'OPERATOR' && (
                      <button
                        type="button"
                        onClick={() => setShowArchivedCards(!showArchivedCards)}
                        style={{
                          backgroundColor: showArchivedCards ? 'var(--primary-color)' : 'transparent',
                          color: showArchivedCards ? 'white' : 'var(--text-mute)',
                          border: '1px solid var(--border-color)',
                          borderRadius: '6px',
                          padding: '4px 10px',
                          fontSize: '11px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease'
                        }}
                      >
                        {showArchivedCards ? 'Hide Archived' : 'Show Archived'}
                      </button>
                    )}
                  </div>
                  
                  {/* Admin Inject Toolbar */}
                  {(isTenantAdmin || isGlobalRole) && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ fontSize: '11px', color: 'var(--text-mute)' }}>
                        Scope: <strong>{selectedUserContext === 'self' ? 'Self' : (formatScopeUserLabel(operators.find(op => op.id.toString() === selectedUserContext)) || 'Selected User') }</strong>
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setStudioTab('template_designer');
                        }}
                        style={{
                          backgroundColor: 'transparent',
                          color: '#60a5fa',
                          border: '1px solid #3b82f6',
                          borderRadius: '6px',
                          padding: '6px 12px',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          transition: 'all 0.15s ease'
                        }}
                        title="Go to Card Templates to Export or Import Trophy Cards"
                      >
                        <span>📥</span>
                        <span>Card Templates / Import</span>
                      </button>
                      <button
                        type="button"
                        onClick={handleCreateAdminCardSlot}
                        style={{
                          backgroundColor: 'var(--primary-color)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '6px 14px',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.filter = 'brightness(1.1)'}
                        onMouseOut={(e) => e.currentTarget.style.filter = 'none'}
                      >
                        + New
                      </button>
                    </div>
                  )}
                </div>

                {/* Trophy Cards Grid */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                  gap: '20px',
                  width: '100%'
                }}>
                  {/* 1. Blank Card Outlines for Available Slots */}
                  {(() => {
                    const slots = [];
                    orders.forEach(order => {
                      if (order.payment_status === 'paid') {
                        const items = order.items_snapshot?.items || {};
                        const hasBragPage = items.custom_brag_page > 0;
                        if (hasBragPage) {
                          (order.coin_details || []).forEach(detail => {
                            if (!detail.brag_page_design_id) {
                              slots.push({
                                orderId: order.id,
                                detailId: detail.id,
                                packageName: order.package_name || 'Coin Set Package'
                              });
                            }
                          });
                        }
                      }
                    });

                    return slots.map((slot, index) => (
                      <div
                        key={`blank-slot-${slot.detailId}-${index}`}
                        onClick={() => {
                          setSelectedOrderSlot(slot);
                          setWizardStep(1);
                          const bases = designs.filter(d => d.design_type === 'brag_page' && d.config && (d.config.is_template || d.config.status === 'published' || d.config.status === 'approved'));
                          setSelectedBasisDesign(bases.length > 0 ? bases[0] : null);
                          setActiveCarouselIndex(0);
                          setWizardBrief('');
                          setSelectedAssetsForBrief([]);
                          setBragViewMode('wizard');
                        }}
                        style={{
                          border: '2px dashed var(--border-color)',
                          borderRadius: '12px',
                          aspectRatio: '1 / 1.4',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          backgroundColor: 'rgba(255,255,255,0.02)',
                          transition: 'all 0.2s ease',
                          padding: '16px',
                          textAlign: 'center',
                          gap: '12px'
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.borderColor = 'var(--primary-color)';
                          e.currentTarget.style.backgroundColor = 'rgba(56, 139, 253, 0.05)';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.borderColor = 'var(--border-color)';
                          e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.02)';
                        }}
                      >
                        <div style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '50%',
                          backgroundColor: 'rgba(56, 139, 253, 0.1)',
                          color: 'var(--primary-color)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '24px',
                          fontWeight: '700'
                        }}>
                          +
                        </div>
                        <div>
                          <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-main)' }}>Configure Design</div>
                          <div style={{ fontSize: '10px', color: 'var(--text-mute)', marginTop: '4px' }}>
                            {slot.packageName}
                          </div>
                        </div>
                      </div>
                    ));
                  })()}

                  {/* 2. Saved Trophy Cards */}
                  {designs
                    .filter(d => {
                      if (d.design_type !== 'brag_page' || !d.config || d.config.is_template) return false;
                      const status = d.config.status || 'proofing';
                      if (status === 'archived') {
                        if (user && user.role === 'OPERATOR') return false;
                        return showArchivedCards;
                      }
                      return true;
                    })
                    .map(d => {
                      const status = d.config.status || 'proofing';
                      const badgeColors = {
                        proofing: { bg: 'var(--warning-bg, rgba(245, 158, 11, 0.15))', text: 'var(--warning-text, #b45309)' },
                        approved: { bg: 'var(--success-bg, rgba(34, 197, 94, 0.15))', text: 'var(--success-text, #15803d)' },
                        published: { bg: 'var(--info-bg, rgba(56, 189, 248, 0.15))', text: 'var(--info-text, #0369a1)' },
                        archived: { bg: 'var(--badge-bg, rgba(110, 118, 129, 0.15))', text: 'var(--badge-text, var(--text-mute, #64748b))' }
                      };
                      const badge = badgeColors[status] || badgeColors.proofing;
                      
                      return (
                        <div
                          key={d.id}
                          onClick={() => {
                            if (user && user.role === 'OPERATOR' && (status === 'approved' || status === 'published')) {
                              setProofingDesign(d);
                              setPreviewFlipped(false);
                              setIsProofer3DOpen(true);
                            } else {
                              setProofingDesign(d);
                              setBragViewMode('proofer');
                            }
                          }}
                          style={{
                            border: '1px solid var(--border-color)',
                            borderRadius: '12px',
                            backgroundColor: 'var(--card-bg)',
                            aspectRatio: '1 / 1.4',
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            padding: '16px',
                            position: 'relative',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                          }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.borderColor = 'var(--primary-color)';
                            e.currentTarget.style.transform = 'translateY(-2px)';
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.borderColor = 'var(--border-color)';
                            e.currentTarget.style.transform = 'none';
                          }}
                        >
                          {/* Top Status Bar */}
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                            <span style={{
                              fontSize: '9px',
                              fontWeight: '700',
                              padding: '2px 6px',
                              borderRadius: '10px',
                              backgroundColor: badge.bg,
                              color: badge.text,
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>
                              {status}
                            </span>
                            
                            <div style={{ display: 'flex', gap: '6px' }}>
                              <button
                                type="button"
                                title="Delete Card"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteDesign(d.id, e);
                                }}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', color: '#ff7b72' }}
                              >
                                ✕
                              </button>
                            </div>
                          </div>

                           {/* Center Preview Emblem/Thumbnail */}
                           <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '12px 0', opacity: 0.85, height: '112px', overflow: 'hidden' }}>
                             {d.config.preview_image ? (
                               <img src={d.config.preview_image} alt={d.name} style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain', borderRadius: '4px' }} />
                             ) : (
                               <svg width="80" height="112" viewBox="0 0 200 200" style={{ overflow: 'visible' }}>
                                 <circle cx="100" cy="100" r="95" fill="none" stroke="var(--primary-color)" strokeWidth="4" />
                                 <text x="100" y="115" fontSize="50" textAnchor="middle" fill="var(--text-main)">🏆</text>
                               </svg>
                             )}
                           </div>
 
                           {/* Bottom Details */}
                           <div>
                             <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-main)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                               {d.name}
                             </div>
                             <div style={{ fontSize: '10px', color: 'var(--text-mute)', marginTop: '4px' }}>
                               Trophy Card
                             </div>
                           </div>
                        </div>
                      );
                    })}

                  {designs.filter(d => d.design_type === 'brag_page' && d.config && !d.config.is_template).length === 0 && (
                    <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px', color: 'var(--text-mute)', fontStyle: 'italic', fontSize: '13px' }}>
                      No Trophy Cards yet. Order a coin set or configure an available slot.
                    </div>
                  )}
                </div>
              </div>
            )}

            {bragViewMode === 'wizard' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', width: '100%', maxWidth: '600px', margin: '0 auto', backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '24px' }}>
                {/* Wizard Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-color)', paddingBottom: '12px' }}>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: 'var(--text-main)' }}>Configure Trophy Card</h3>
                    <span style={{ fontSize: '11px', color: 'var(--text-mute)' }}>Step {wizardStep} of 3</span>
                  </div>
                  <button
                    type="button"
                    onClick={exitToGrid}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: 'var(--text-mute)' }}
                  >
                    Cancel
                  </button>
                </div>

                {/* Step 1: Select Template or Basis Card */}
                {wizardStep === 1 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-mute)' }}>
                      Select an operator template or an existing card as the basis for your new design:
                    </p>
                    
                    {(() => {
                      const carouselDesigns = designs.filter(
                        d => d.design_type === 'brag_page' && 
                        d.config && 
                        (d.config.is_template || d.config.status === 'published' || d.config.status === 'approved')
                      );

                      if (carouselDesigns.length === 0) {
                        return (
                          <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-mute)', fontStyle: 'italic', fontSize: '12px' }}>
                            No templates or existing cards found. A default outline will be used.
                          </div>
                        );
                      }

                      const activeDesign = carouselDesigns[activeCarouselIndex] || carouselDesigns[0];

                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', gap: '16px' }}>
                          {/* Carousel viewport with perspective */}
                          <div style={{
                            position: 'relative',
                            width: '100%',
                            height: '200px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            perspective: '1000px',
                            overflow: 'hidden',
                            userSelect: 'none',
                            margin: '10px 0'
                          }}>
                            {/* Navigation controls */}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                const newIndex = (activeCarouselIndex - 1 + carouselDesigns.length) % carouselDesigns.length;
                                setActiveCarouselIndex(newIndex);
                                setSelectedBasisDesign(carouselDesigns[newIndex]);
                              }}
                              style={{
                                position: 'absolute',
                                left: '10px',
                                zIndex: 20,
                                backgroundColor: 'rgba(0,0,0,0.6)',
                                border: '1px solid var(--border-color)',
                                borderRadius: '50%',
                                width: '32px',
                                height: '32px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                cursor: 'pointer',
                                fontSize: '16px',
                                transition: 'all 0.2s ease',
                                boxShadow: '0 2px 5px rgba(0,0,0,0.3)'
                              }}
                              onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'var(--primary-color)'}
                              onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.6)'}
                            >
                              ‹
                            </button>

                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                const newIndex = (activeCarouselIndex + 1) % carouselDesigns.length;
                                setActiveCarouselIndex(newIndex);
                                setSelectedBasisDesign(carouselDesigns[newIndex]);
                              }}
                              style={{
                                position: 'absolute',
                                right: '10px',
                                zIndex: 20,
                                backgroundColor: 'rgba(0,0,0,0.6)',
                                border: '1px solid var(--border-color)',
                                borderRadius: '50%',
                                width: '32px',
                                height: '32px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                cursor: 'pointer',
                                fontSize: '16px',
                                transition: 'all 0.2s ease',
                                boxShadow: '0 2px 5px rgba(0,0,0,0.3)'
                              }}
                              onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'var(--primary-color)'}
                              onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.6)'}
                            >
                              ›
                            </button>

                            {/* Render all visible cards using 3D cover flow math */}
                            {carouselDesigns.map((d, index) => {
                              const offset = index - activeCarouselIndex;
                              const isVisible = Math.abs(offset) <= 2;
                              if (!isVisible) return null;

                              const isSelected = selectedBasisDesign?.id === d.id;

                              let rotateY = 0;
                              let translateZ = 0;
                              let translateX = offset * 90; 
                              let scale = 0.8;
                              let opacity = 0.55;
                              let zIndex = 10 - Math.abs(offset);

                              if (offset === 0) {
                                scale = 1.15;
                                opacity = 1;
                                translateZ = 50;
                                rotateY = 0;
                              } else if (offset < 0) {
                                rotateY = 35; 
                                translateZ = -40;
                                translateX -= 15; 
                              } else if (offset > 0) {
                                rotateY = -35;
                                translateZ = -40;
                                translateX += 15;
                              }

                              const cardImg = d.config?.preview_image;

                              return (
                                <div
                                  key={`basis-${d.id}`}
                                  onClick={() => {
                                    setActiveCarouselIndex(index);
                                    setSelectedBasisDesign(d);
                                  }}
                                  style={{
                                    position: 'absolute',
                                    width: '100px',
                                    height: '140px', 
                                    borderRadius: '8px',
                                    border: isSelected ? '3px solid var(--primary-color)' : '1px solid var(--border-color)',
                                    backgroundColor: '#0c0a09',
                                    cursor: 'pointer',
                                    transition: 'all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1)',
                                    transform: `translateX(${translateX}px) translateZ(${translateZ}px) scale(${scale}) rotateY(${rotateY}deg)`,
                                    transformStyle: 'preserve-3d',
                                    zIndex: zIndex,
                                    opacity: opacity,
                                    boxShadow: isSelected 
                                      ? '0 8px 24px rgba(56, 139, 253, 0.35)' 
                                      : '0 4px 12px rgba(0,0,0,0.4)',
                                    overflow: 'hidden',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  {cardImg ? (
                                    <img src={cardImg} alt={d.name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                  ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', color: 'var(--text-mute)', fontSize: '8px', textAlign: 'center', padding: '10px' }}>
                                      <span>🏆</span>
                                      <span style={{ marginTop: '4px' }}>No Preview</span>
                                    </div>
                                  )}

                                  {isSelected && (
                                    <div style={{
                                      position: 'absolute',
                                      top: '6px',
                                      right: '6px',
                                      backgroundColor: 'var(--primary-color)',
                                      color: 'white',
                                      borderRadius: '50%',
                                      width: '18px',
                                      height: '18px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '10px',
                                      fontWeight: 'bold',
                                      boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                                      zIndex: 30
                                    }}>
                                      ✓
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>

                          {/* Selected template metadata */}
                          {activeDesign && (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                              <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-main)', textAlign: 'center' }}>
                                {activeDesign.name}
                              </div>
                              <div style={{ fontSize: '10px', color: 'var(--text-mute)', textAlign: 'center' }}>
                                {activeDesign.config.is_template ? 'Official Template' : `Trophy Card (${activeDesign.config.status})`}
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  setPreviewDesign(activeDesign);
                                  setPreviewFlipped(false);
                                  setIsPreviewModalOpen(true);
                                }}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                  border: '1px solid var(--border-color)',
                                  borderRadius: '6px',
                                  padding: '6px 12px',
                                  fontSize: '11px',
                                  color: 'var(--text-mute)',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease',
                                  marginTop: '6px'
                                }}
                                onMouseOver={(e) => {
                                  e.currentTarget.style.borderColor = 'var(--primary-color)';
                                  e.currentTarget.style.color = 'var(--text-main)';
                                }}
                                onMouseOut={(e) => {
                                  e.currentTarget.style.borderColor = 'var(--border-color)';
                                  e.currentTarget.style.color = 'var(--text-mute)';
                                }}
                              >
                                🔍 Preview Full Size
                              </button>
                            </div>
                          )}

                          {/* Dot pagination */}
                          <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', marginTop: '-4px' }}>
                            {carouselDesigns.map((_, idx) => (
                              <div
                                key={`dot-${idx}`}
                                onClick={() => {
                                  setActiveCarouselIndex(idx);
                                  setSelectedBasisDesign(carouselDesigns[idx]);
                                }}
                                style={{
                                  width: '6px',
                                  height: '6px',
                                  borderRadius: '50%',
                                  backgroundColor: idx === activeCarouselIndex ? 'var(--primary-color)' : 'var(--border-color)',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease'
                                }}
                              />
                            ))}
                          </div>
                        </div>
                      );
                    })()}

                    <button
                      type="button"
                      onClick={() => setWizardStep(2)}
                      style={{
                        backgroundColor: 'var(--primary-color)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '10px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        marginTop: '12px'
                      }}
                    >
                      Next Step: Upload Artwork
                    </button>
                  </div>
                )}

                {/* Step 2: Upload Artwork */}
                {wizardStep === 2 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-mute)' }}>
                      Upload any artwork, logos, or photo assets you want the designer to integrate into this card:
                    </p>

                    {/* Simple File Uploader */}
                    <div style={{
                      border: '2px dashed var(--border-color)',
                      borderRadius: '8px',
                      padding: '24px',
                      textAlign: 'center',
                      cursor: 'pointer',
                      backgroundColor: 'rgba(255,255,255,0.02)'
                    }}
                    onClick={() => {
                      const fileInput = document.createElement('input');
                      fileInput.type = 'file';
                      fileInput.accept = 'image/*';
                      fileInput.onchange = async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        
                        const formData = new FormData();
                        formData.append('file', file);
                        
                        try {
                          const res = await fetch(`${API_BASE}/api/upload`, {
                            method: 'POST',
                            headers: { 'Authorization': `Bearer ${token}` },
                            body: formData
                          });
                          const data = await res.json();
                          if (res.ok && data.file) {
                            const newAsset = {
                              id: data.file.id,
                              file_url: `${API_BASE}${data.file.location}`,
                              original_name: data.file.original_name,
                              mime_type: data.file.mime_type
                            };
                            setSelectedAssetsForBrief([...selectedAssetsForBrief, newAsset]);
                            fetchStudioAssets(); // Refresh user library
                          } else {
                            alert(data.detail || 'Upload failed');
                          }
                        } catch (err) {
                          console.error('Error uploading file in wizard:', err);
                        }
                      };
                      fileInput.click();
                    }}
                    >
                      <span style={{ fontSize: '24px' }}>📁</span>
                      <div style={{ fontSize: '12px', fontWeight: '700', marginTop: '8px' }}>Click to Upload Media</div>
                      <span style={{ fontSize: '10px', color: 'var(--text-mute)' }}>Supports JPG, PNG, GIF up to 5MB</span>
                    </div>

                    {/* Uploaded List */}
                    {selectedAssetsForBrief.length > 0 && (
                      <div>
                        <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-mute)', marginBottom: '6px' }}>UPLOADED ASSETS</div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(60px, 1fr))', gap: '8px' }}>
                          {selectedAssetsForBrief.map((asset, index) => (
                            <div key={`upload-art-${asset.id}-${index}`} style={{ position: 'relative', width: '100%', aspectRatio: '1/1', borderRadius: '6px', overflow: 'hidden', border: '1px solid var(--border-color)' }}>
                              <img src={asset.file_url} alt="asset" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedAssetsForBrief(selectedAssetsForBrief.filter((_, i) => i !== index));
                                }}
                                style={{
                                  position: 'absolute', top: '2px', right: '2px', backgroundColor: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: '50%', color: 'white', cursor: 'pointer', width: '16px', height: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px'
                                }}
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: '10px', marginTop: '12px' }}>
                      <button
                        type="button"
                        onClick={() => setWizardStep(1)}
                        style={{
                          flex: 1,
                          backgroundColor: 'transparent',
                          color: 'var(--text-mute)',
                          border: '1px solid var(--border-color)',
                          borderRadius: '6px',
                          padding: '10px',
                          fontSize: '13px',
                          cursor: 'pointer'
                        }}
                      >
                        Back
                      </button>
                      <button
                        type="button"
                        onClick={() => setWizardStep(3)}
                        style={{
                          flex: 1,
                          backgroundColor: 'var(--primary-color)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '10px',
                          fontSize: '13px',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        Next Step: Submit Brief
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 3: Brief Text & Insertion Toolbar */}
                {wizardStep === 3 && (() => {
                  const allAvailableAssets = (() => {
                    const merged = [];
                    const seenIds = new Set();
                    selectedAssetsForBrief.forEach(asset => {
                      if (asset && asset.id && !seenIds.has(asset.id)) {
                        seenIds.add(asset.id);
                        merged.push({
                          id: asset.id,
                          file_url: asset.file_url,
                          original_name: asset.original_name
                        });
                      }
                    });
                    studioAssets.forEach(asset => {
                      if (asset && asset.id && !seenIds.has(asset.id)) {
                        seenIds.add(asset.id);
                        merged.push({
                          id: asset.id,
                          file_url: `${API_BASE}${asset.location}`,
                          original_name: asset.original_name
                        });
                      }
                    });
                    return merged;
                  })();

                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-mute)' }}>
                        Write a detailed design brief for the artist. You can insert color tags or preview icons of your uploaded assets inline:
                      </p>

                      {/* Brief Formatting Toolbar */}
                      <div style={{
                        display: 'flex',
                        gap: '8px',
                        padding: '8px',
                        backgroundColor: 'var(--bg-color)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '6px 6px 0 0',
                        borderBottom: 'none'
                      }}>
                        <button
                          type="button"
                          onClick={() => setShowColorPickerForBrief(!showColorPickerForBrief)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '4px 8px',
                            border: '1px solid var(--border-color)',
                            borderRadius: '4px',
                            backgroundColor: 'var(--card-bg)',
                            color: 'var(--text-main)',
                            fontSize: '11px',
                            cursor: 'pointer'
                          }}
                        >
                          🎨 Insert Color
                        </button>

                        <button
                          type="button"
                          onClick={() => setShowAssetSelectorForBrief(!showAssetSelectorForBrief)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '4px 8px',
                            border: '1px solid var(--border-color)',
                            borderRadius: '4px',
                            backgroundColor: 'var(--card-bg)',
                            color: 'var(--text-main)',
                            fontSize: '11px',
                            cursor: 'pointer'
                          }}
                        >
                          📎 Insert Asset
                        </button>
                      </div>

                      {/* Color picker overlay */}
                      {showColorPickerForBrief && (
                        <div style={{ display: 'flex', gap: '10px', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', backgroundColor: 'var(--bg-color)' }}>
                          <input
                            type="color"
                            value={colorPickerHex}
                            onChange={(e) => setColorPickerHex(e.target.value)}
                            style={{ width: '40px', height: '30px', cursor: 'pointer', border: 'none', background: 'none' }}
                          />
                          <input
                            type="text"
                            value={colorPickerHex}
                            onChange={(e) => setColorPickerHex(e.target.value)}
                            style={{
                              backgroundColor: 'var(--input-bg)',
                              border: '1px solid var(--input-border)',
                              borderRadius: '4px',
                              color: 'var(--input-text)',
                              padding: '4px 8px',
                              fontSize: '12px',
                              width: '80px'
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setWizardBrief(wizardBrief + " [color:" + colorPickerHex + "]");
                              setShowColorPickerForBrief(false);
                            }}
                            style={{ backgroundColor: 'var(--primary-color)', border: 'none', borderRadius: '4px', color: 'white', padding: '4px 10px', fontSize: '11px', cursor: 'pointer' }}
                          >
                            Insert
                          </button>
                        </div>
                      )}

                      {/* Asset selector overlay */}
                      {showAssetSelectorForBrief && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', backgroundColor: 'var(--bg-color)' }}>
                          <div style={{ fontSize: '10px', fontWeight: '700', color: 'var(--text-mute)' }}>CLICK TO INSERT ASSET FROM LIBRARY:</div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(50px, 1fr))', gap: '6px', maxHeight: '120px', overflowY: 'auto', padding: '2px' }}>
                            {allAvailableAssets.map((asset, index) => (
                              <img
                                key={`sel-brief-art-${index}`}
                                src={asset.file_url}
                                alt={asset.original_name || "brief option"}
                                title={asset.original_name || "Click to insert"}
                                onClick={() => {
                                  setWizardBrief(wizardBrief + " [asset_img:" + asset.file_url + "]");
                                  setShowAssetSelectorForBrief(false);
                                }}
                                style={{ width: '100%', aspectRatio: '1/1', objectFit: 'cover', borderRadius: '4px', cursor: 'pointer', border: '1px solid var(--border-color)' }}
                              />
                            ))}
                            {allAvailableAssets.length === 0 && (
                              <span style={{ fontSize: '10px', color: 'var(--text-mute)', fontStyle: 'italic', gridColumn: '1/-1' }}>No artwork found in your library.</span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Textarea */}
                      <textarea
                        value={wizardBrief}
                        onChange={(e) => setWizardBrief(e.target.value)}
                        placeholder="Enter instructions, requirements, color suggestions, and comments..."
                        rows="6"
                        style={{
                          backgroundColor: 'var(--input-bg)',
                          border: '1px solid var(--input-border)',
                          borderRadius: '0 0 6px 6px',
                          padding: '10px',
                          color: 'var(--input-text)',
                          fontSize: '12px',
                          fontFamily: 'inherit',
                          resize: 'vertical',
                          marginTop: '-17px'
                        }}
                      />

                      <div style={{ display: 'flex', gap: '10px', marginTop: '12px' }}>
                        <button
                          type="button"
                          onClick={() => setWizardStep(2)}
                          style={{
                            flex: 1,
                            backgroundColor: 'transparent',
                            color: 'var(--text-mute)',
                            border: '1px solid var(--border-color)',
                            borderRadius: '6px',
                            padding: '10px',
                            fontSize: '13px',
                            cursor: 'pointer'
                          }}
                        >
                          Back
                        </button>
                        <button
                          type="button"
                          onClick={async () => {
                            if (!wizardBrief.trim()) {
                              alert('Brief cannot be empty.');
                              return;
                            }
                            try {
                              // 1. Duplicate design
                              const baseConfig = selectedBasisDesign?.config || {};
                              const payload = {
                                name: (user.first_name || user.email) + "'s Trophy Card",
                                design_type: 'brag_page',
                                config: {
                                  ...baseConfig,
                                  is_template: false,
                                  status: 'proofing'
                                },
                                associated_user_id: user.id,
                                tenant_id: user.tenant_id
                              };
                              
                              const dRes = await fetch(API_BASE + "/api/v1/designs", {
                                method: 'POST',
                                headers: {
                                  'Content-Type': 'application/json',
                                  'Authorization': "Bearer " + token
                                },
                                body: JSON.stringify(payload)
                              });
                              const designData = await dRes.json();
                              if (!dRes.ok) throw new Error(designData.detail || 'Failed to create card design');
                              
                              // 2. Link card design to Order Details slot
                              const aRes = await fetch(API_BASE + "/api/v1/mint/orders/" + selectedOrderSlot.orderId + "/assign-artwork", {
                                method: 'POST',
                                headers: {
                                  'Content-Type': 'application/json',
                                  'Authorization': "Bearer " + token
                                },
                                body: JSON.stringify({
                                  brag_page_design_id: designData.id
                                })
                              });
                              if (!aRes.ok) {
                                const aData = await aRes.json();
                                throw new Error(aData.detail || 'Failed to assign artwork to order');
                              }

                              // 3. Post first brief message into Chat
                              await fetch(API_BASE + "/api/v1/chat/design/" + designData.id, {
                                method: 'POST',
                                headers: {
                                  'Content-Type': 'application/json',
                                  'Authorization': "Bearer " + token
                                },
                                body: JSON.stringify({
                                  message_text: "[BRIEF] " + wizardBrief
                                })
                              });
                              
                              // Refresh lists
                              fetchDesigns();
                              fetchOrders();
                              
                              // Load design in proofer
                              setProofingDesign(designData);
                              setBragViewMode('proofer');
                            } catch (err) {
                              alert(err.message || 'Error occurred during wizard setup.');
                            }
                          }}
                          style={{
                            flex: 1,
                            backgroundColor: 'var(--primary-color)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '10px',
                            fontSize: '13px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          Finalize & Start Proofing
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {bragViewMode === 'proofer' && proofingDesign && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', width: '100%' }}>
                {/* Proofer Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-color)', paddingBottom: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <button
                      type="button"
                      onClick={exitToGrid}
                      style={{
                        backgroundColor: 'transparent', border: '1px solid var(--border-color)', borderRadius: '6px', padding: '6px 12px', fontSize: '12px', fontWeight: '600', color: 'var(--text-mute)', cursor: 'pointer'
                      }}
                    >
                      ← Back to Cards
                    </button>
                    <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: 'var(--text-main)' }}>
                      Card Proofer - {proofingDesign.name}
                    </h3>
                  </div>

                  <div style={{ display: 'flex', gap: '8px' }}>
                    {/* Tenant user edit action (restricted to proofing status and non-operators) */}
                    {user.tenant_id !== null && user.role !== 'OPERATOR' && proofingDesign.config.status === 'proofing' && (
                      <button
                        type="button"
                        onClick={() => setProoferEditingDesignId(proofingDesign.id)}
                        style={{
                          backgroundColor: '#1677ff',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '6px 12px',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        Edit Card
                      </button>
                    )}

                    {/* Unarchive Action */}
                    {user.tenant_id !== null && user.role !== 'OPERATOR' && proofingDesign.config.status === 'archived' && (
                      <button
                        type="button"
                        onClick={async () => {
                          const confirmed = await modal.confirm("Are you sure you want to unarchive this trophy card?", {
                            title: "Unarchive Trophy Card",
                            confirmText: "Unarchive"
                          });
                          if (!confirmed) return;
                          const conf = { ...proofingDesign.config, status: 'approved' };
                          try {
                            const res = await fetch(API_BASE + "/api/v1/designs/" + proofingDesign.id, {
                              method: 'PATCH',
                              headers: {
                                'Content-Type': 'application/json',
                                'Authorization': "Bearer " + token
                              },
                              body: JSON.stringify({ config: conf })
                            });
                            const data = await res.json();
                            if (res.ok) {
                              await fetch(API_BASE + "/api/v1/chat/design/" + proofingDesign.id, {
                                method: 'POST',
                                headers: {
                                  'Content-Type': 'application/json',
                                  'Authorization': "Bearer " + token
                                },
                                body: JSON.stringify({ message_text: `🔓 Trophy Card unarchived by client (${user?.email || 'unknown user'}).` })
                              });
                              setProofingDesign(data);
                              fetchDesigns();
                              fetchOrders();
                            }
                          } catch (err) {
                            console.error(err);
                          }
                        }}
                        style={{
                          backgroundColor: 'var(--primary-color)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '6px 12px',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        Unarchive
                      </button>
                    )}
                    {/* User Actions */}
                    {proofingDesign.config.status === 'proofing' && (
                      <button
                        type="button"
                        onClick={() => setIsProoferApprovalOpen(true)}
                        style={{ backgroundColor: '#2ea043', color: 'white', border: 'none', borderRadius: '6px', padding: '6px 12px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}
                      >
                        ✓ Approve Card Design
                      </button>
                    )}

                    {(proofingDesign.config.status === 'approved' || proofingDesign.config.status === 'published') && user.tenant_id !== null && user.role !== 'OPERATOR' && (
                      <button
                        type="button"
                        onClick={async () => {
                          const confirmed = await modal.confirm("Are you sure you want to revoke approval for this trophy card? Reverting to proofing status will allow operators to view the card and tenant users to edit it again.", {
                            title: "Revoke Trophy Card Approval",
                            confirmText: "Revoke Approval",
                            danger: true
                          });
                          if (!confirmed) return;
                          const conf = { ...proofingDesign.config, status: 'proofing' };
                          try {
                            const res = await fetch(API_BASE + "/api/v1/designs/" + proofingDesign.id, {
                              method: 'PATCH',
                              headers: {
                                'Content-Type': 'application/json',
                                'Authorization': "Bearer " + token
                              },
                              body: JSON.stringify({ config: conf })
                            });
                            const data = await res.json();
                            if (res.ok) {
                              await fetch(API_BASE + "/api/v1/chat/design/" + proofingDesign.id, {
                                method: 'POST',
                                headers: {
                                  'Content-Type': 'application/json',
                                  'Authorization': "Bearer " + token
                                },
                                body: JSON.stringify({ message_text: `🔓 Card approval has been revoked by tenant admin (${user?.email || 'unknown user'}). Reverted back to proofing.` })
                              });
                              setProofingDesign(data);
                              fetchDesigns();
                              fetchOrders();
                            }
                          } catch (err) {
                            console.error(err);
                          }
                        }}
                        style={{ backgroundColor: '#d93f3f', color: 'white', border: 'none', borderRadius: '6px', padding: '6px 12px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}
                      >
                        Revoke Approval
                      </button>
                    )}


                  </div>
                </div>

                {/* Split workspace */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: window.innerWidth < 992 ? '1fr' : '1fr 380px',
                  gap: '24px',
                  width: '100%',
                  alignItems: 'stretch'
                }}>
                  {/* LEFT: Live Dynamic Card Views */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center', width: '100%' }}>
                    {/* Controls Toolbar */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                      gap: '12px',
                      padding: '8px 16px',
                      backgroundColor: 'var(--card-bg)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '8px',
                      boxSizing: 'border-box'
                    }}>
                      {/* Front/Back Toggle Buttons */}
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          type="button"
                          onClick={() => setProoferSide('front')}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: prooferSide === 'front' ? 'var(--primary-color)' : 'transparent',
                            color: prooferSide === 'front' ? 'white' : 'var(--text-main)',
                            border: '1px solid var(--border-color)',
                            borderRadius: '6px',
                            fontSize: '11px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          Front Side
                        </button>
                        <button
                          type="button"
                          onClick={() => setProoferSide('back')}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: prooferSide === 'back' ? 'var(--primary-color)' : 'transparent',
                            color: prooferSide === 'back' ? 'white' : 'var(--text-main)',
                            border: '1px solid var(--border-color)',
                            borderRadius: '6px',
                            fontSize: '11px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          Back Side
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setPreviewFlipped(false);
                            setIsProofer3DOpen(true);
                          }}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: 'transparent',
                            color: 'var(--primary-color)',
                            border: '1px solid var(--primary-color)',
                            borderRadius: '6px',
                            fontSize: '11px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            marginLeft: '8px'
                          }}
                        >
                           Flip Preview
                        </button>
                      </div>

                      {/* Zoom control slider */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '11px', color: 'var(--text-mute)', fontWeight: '600' }}>Zoom:</span>
                        <input
                          type="range"
                          min="0.4"
                          max="1.5"
                          step="0.05"
                          value={prooferZoom}
                          onChange={(e) => setProoferZoom(parseFloat(e.target.value))}
                          style={{ width: '100px', cursor: 'ew-resize' }}
                        />
                        <span style={{ fontSize: '11px', color: 'var(--text-main)', minWidth: '35px', textAlign: 'right', fontWeight: '700' }}>
                          {Math.round(prooferZoom * 100)}%
                        </span>
                      </div>
                    </div>

                    {/* Canvas Container */}
                    <div style={{
                      width: '100%',
                      height: '618px',
                      flex: 'none',
                      display: 'flex',
                      backgroundColor: '#141416',
                      borderRadius: '12px',
                      border: '1px solid var(--border-color)',
                      overflow: 'auto',
                      padding: '24px',
                      boxSizing: 'border-box'
                    }}>
                       <div style={{
                        width: `${(proofingDesign.config?.cardDimensions?.width || 400) * prooferZoom}px`,
                        height: `${(proofingDesign.config?.cardDimensions?.height || 560) * prooferZoom}px`,
                        overflow: 'hidden',
                        flexShrink: 0,
                        position: 'relative',
                        margin: 'auto'
                      }}>
                        <BragCardCanvas
                          design={proofingDesign}
                          side={prooferSide}
                          scale={prooferZoom}
                          testDataOverrides={testDataOverrides}
                          datasets={datasets}
                          user={user}
                          orgData={orgData}
                          tenantConfig={tenantConfig}
                        />
                      </div>
                    </div>
                  </div>

                  {/* RIGHT: Tabbed Chat & Test Data Panel */}
                  <div style={{
                    backgroundColor: 'var(--card-bg)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '12px',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '680px',
                    overflow: 'hidden'
                  }}>
                    {/* Tab Headers */}
                    <div style={{ display: 'flex', borderBottom: '1px solid var(--border-color)', backgroundColor: 'var(--bg-color)' }}>
                      <button
                        type="button"
                        onClick={() => setProoferRightTab('chat')}
                        style={{
                          flex: 1,
                          padding: '12px',
                          background: prooferRightTab === 'chat' ? 'var(--card-bg)' : 'transparent',
                          border: 'none',
                          borderBottom: prooferRightTab === 'chat' ? '2px solid var(--primary-color)' : 'none',
                          color: prooferRightTab === 'chat' ? 'var(--primary-color)' : 'var(--text-mute)',
                          fontWeight: prooferRightTab === 'chat' ? '700' : '500',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                      >
                        Chat & Support
                      </button>
                      <button
                        type="button"
                        onClick={() => setProoferRightTab('test-data')}
                        style={{
                          flex: 1,
                          padding: '12px',
                          background: prooferRightTab === 'test-data' ? 'var(--card-bg)' : 'transparent',
                          border: 'none',
                          borderBottom: prooferRightTab === 'test-data' ? '2px solid var(--primary-color)' : 'none',
                          color: prooferRightTab === 'test-data' ? 'var(--primary-color)' : 'var(--text-mute)',
                          fontWeight: prooferRightTab === 'test-data' ? '700' : '500',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                      >
                        Test Data
                      </button>
                    </div>

                    {/* Tab Content: Chat */}
                    {prooferRightTab === 'chat' && (
                      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, height: 'calc(100% - 41px)', overflow: 'hidden' }}>
                        {/* Messages Body */}
                        <div ref={chatContainerRef} style={{
                          flex: 1,
                          padding: '16px',
                          overflowY: 'auto',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '12px',
                          backgroundColor: 'rgba(0,0,0,0.1)'
                        }}>
                          {chatMessages.map((msg, index) => {
                            const isSelf = msg.sender_id === user.id;
                            const senderName = msg.sender ? (msg.sender.first_name || "") + " " + (msg.sender.last_name || "") : 'Unknown';
                            
                            return (
                              <div
                                key={`chat-msg-${msg.id}-${index}`}
                                style={{
                                  alignSelf: isSelf ? 'flex-end' : 'flex-start',
                                  maxWidth: '85%',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: isSelf ? 'flex-end' : 'flex-start'
                                }}
                              >
                                <span style={{ fontSize: '9px', color: 'var(--text-mute)', marginBottom: '3px', fontWeight: '600' }}>
                                  {senderName} {msg.sender ? "(" + msg.sender.role.replace('_', ' ') + ")" : ''}
                                </span>
                                
                                <div style={{
                                  padding: '10px 12px',
                                  borderRadius: isSelf ? '12px 12px 0 12px' : '12px 12px 12px 0',
                                  backgroundColor: isSelf ? 'var(--primary-color)' : '#2a2b30',
                                  color: isSelf ? 'white' : '#f5f5f7',
                                  border: isSelf ? 'none' : '1px solid #3e4046',
                                  fontSize: '12px',
                                  lineHeight: '1.4',
                                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                  wordBreak: 'break-word'
                                }}>
                                  {parseMessageText(msg.message_text)}
                                </div>
                                
                                <span style={{ fontSize: '8px', color: 'var(--text-mute)', marginTop: '2px' }}>
                                  {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </span>
                              </div>
                            );
                          })}
                          {chatMessages.length === 0 && !chatLoading && (
                            <div style={{ textAlign: 'center', color: 'var(--text-mute)', fontStyle: 'italic', fontSize: '11px', margin: 'auto' }}>
                              No messages yet. Send a message to start.
                            </div>
                          )}
                        </div>

                        {/* Chat Input Footer */}
                        <div style={{ borderTop: '1px solid var(--border-color)', padding: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <button
                              type="button"
                              onClick={() => setShowColorPickerForBrief(!showColorPickerForBrief)}
                              style={{ background: 'none', border: '1px solid var(--border-color)', borderRadius: '4px', padding: '2px 6px', fontSize: '10px', color: 'var(--text-mute)', cursor: 'pointer' }}
                            >
                              Color
                            </button>
                            <button
                              type="button"
                              onClick={() => setShowAssetSelectorForBrief(!showAssetSelectorForBrief)}
                              style={{ background: 'none', border: '1px solid var(--border-color)', borderRadius: '4px', padding: '2px 6px', fontSize: '10px', color: 'var(--text-mute)', cursor: 'pointer' }}
                            >
                              Asset
                            </button>
                          </div>

                          {showColorPickerForBrief && (
                            <div style={{ display: 'flex', gap: '6px', alignItems: 'center', backgroundColor: 'var(--bg-color)', padding: '6px', borderRadius: '4px', border: '1px solid var(--border-color)' }}>
                              <input type="color" value={colorPickerHex} onChange={(e) => setColorPickerHex(e.target.value)} style={{ width: '30px', height: '24px', cursor: 'pointer', border: 'none', background: 'none' }} />
                              <button
                                type="button"
                                onClick={() => {
                                  setNewMessage(newMessage + " [color:" + colorPickerHex + "]");
                                  setShowColorPickerForBrief(false);
                                }}
                                style={{ backgroundColor: 'var(--primary-color)', color: 'white', border: 'none', borderRadius: '3px', padding: '2px 6px', fontSize: '9px', cursor: 'pointer' }}
                              >
                                Insert
                              </button>
                            </div>
                          )}

                          {showAssetSelectorForBrief && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', backgroundColor: 'var(--bg-color)', padding: '6px', borderRadius: '4px', border: '1px solid var(--border-color)', maxHeight: '100px', overflowY: 'auto' }}>
                              <div style={{ fontSize: '9px', fontWeight: '700', color: 'var(--text-mute)' }}>SELECT ATTACHMENT:</div>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(30px, 1fr))', gap: '4px' }}>
                                {studioAssets.map((asset, i) => {
                                  const assetUrl = `${API_BASE}${asset.location}`;
                                  return (
                                    <img
                                      key={`quick-art-${i}`}
                                      src={assetUrl}
                                      alt={asset.original_name || "option"}
                                      title={asset.original_name || "Click to attach"}
                                      onClick={() => {
                                        setNewMessage(newMessage + " [asset_img:" + assetUrl + "]");
                                        setShowAssetSelectorForBrief(false);
                                      }}
                                      style={{ width: '100%', aspectRatio: '1/1', objectFit: 'cover', borderRadius: '2px', cursor: 'pointer', border: '1px solid var(--border-color)' }}
                                    />
                                  );
                                })}
                                {studioAssets.length === 0 && (
                                  <span style={{ fontSize: '9px', color: 'var(--text-mute)', fontStyle: 'italic' }}>No assets uploaded yet.</span>
                                )}
                              </div>
                            </div>
                          )}

                          <form
                            onSubmit={async (e) => {
                              e.preventDefault();
                              if (!newMessage.trim() || !proofingDesign) return;
                              
                              const text = newMessage;
                              setNewMessage('');
                              try {
                                const res = await fetch(API_BASE + "/api/v1/chat/design/" + proofingDesign.id, {
                                  method: 'POST',
                                  headers: {
                                    'Content-Type': 'application/json',
                                    'Authorization': "Bearer " + token
                                  },
                                  body: JSON.stringify({ message_text: text })
                                });
                                if (res.ok) {
                                  fetchChatMessages(proofingDesign.id);
                                }
                              } catch (err) {
                                    console.error(err);
                              }
                            }}
                            style={{ display: 'flex', gap: '8px' }}
                          >
                            <input
                              type="text"
                              value={newMessage}
                              onChange={(e) => setNewMessage(e.target.value)}
                              placeholder="Type a message..."
                              style={{
                                flex: 1,
                                backgroundColor: 'var(--input-bg)',
                                border: '1px solid var(--input-border)',
                                borderRadius: '6px',
                                padding: '8px 12px',
                                color: 'var(--input-text)',
                                fontSize: '12px'
                              }}
                            />
                            <button
                              type="submit"
                              style={{
                                backgroundColor: 'var(--primary-color)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                padding: '8px 16px',
                                fontSize: '12px',
                                fontWeight: '600',
                                cursor: 'pointer'
                              }}
                            >
                              Send
                            </button>
                          </form>
                        </div>
                      </div>
                    )}

                    {/* Tab Content: Test Data Overrides Form */}
                    {prooferRightTab === 'test-data' && (
                      <div style={{
                        flex: 1,
                        padding: '16px',
                        overflowY: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '14px',
                        background: 'var(--panel-color)',
                        height: 'calc(100% - 41px)',
                        boxSizing: 'border-box'
                      }}>
                        <div style={{ fontSize: '11px', color: 'var(--text-mute)', marginBottom: '4px', lineHeight: '1.4' }}>
                          Modify card fields below to preview changes. Automatically mapped organization and user fields are locked.
                        </div>

                        {(() => {
                          const fields = getDynamicFieldsInDesign(proofingDesign, datasets);
                          const inputStyle = {
                            backgroundColor: 'var(--input-bg)',
                            border: '1px solid var(--input-border)',
                            borderRadius: '6px',
                            color: 'var(--input-text)',
                            padding: '6px 10px',
                            fontSize: '12px',
                            width: '100%',
                            boxSizing: 'border-box',
                            marginTop: '2px'
                          };

                          const disabledInputStyle = {
                            ...inputStyle,
                            backgroundColor: 'var(--disabled-bg, rgba(0,0,0,0.25))',
                            borderColor: 'var(--border-color)',
                            color: 'var(--text-mute)',
                            cursor: 'not-allowed'
                          };

                          const fieldMappings = proofingDesign?.config?.field_mappings || {};
                          const lockedOptions = proofingDesign?.config?.locked_options || {};
                          const requiredFields = proofingDesign?.config?.required_fields || {};
                          const formDatasetOrder = proofingDesign?.config?.form_dataset_order || [];

                          // Group fields by dataset name
                          const groups = {};
                          fields.forEach(field => {
                            const dsId = field.datasetKey || '';
                            let dsName = 'General Variables';
                            if (dsId && datasets[dsId]) {
                              dsName = datasets[dsId].name || dsId;
                            }
                            if (!groups[dsName]) {
                              groups[dsName] = [];
                            }
                            groups[dsName].push(field);
                          });

                          const existingGroupNames = Object.keys(groups);
                          const orderedGroupNames = [
                            ...formDatasetOrder.filter(name => existingGroupNames.includes(name)),
                            ...existingGroupNames.filter(name => !formDatasetOrder.includes(name))
                          ];

                          const allCollapsed = orderedGroupNames.length > 0 && orderedGroupNames.every(name => collapsedProoferDatasets[name]);

                          const handleToggleCollapseAll = () => {
                            if (allCollapsed) {
                              setCollapsedProoferDatasets({});
                            } else {
                              const next = {};
                              orderedGroupNames.forEach(name => { next[name] = true; });
                              setCollapsedProoferDatasets(next);
                            }
                          };

                          return (
                            <>
                              {/* Top Toolbar */}
                              {orderedGroupNames.length > 0 && (
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  padding: '6px 10px',
                                  backgroundColor: 'rgba(0,0,0,0.25)',
                                  borderRadius: '6px',
                                  border: '1px solid var(--border-color)',
                                  marginBottom: '2px'
                                }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <span style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-main)' }}>Data Groups</span>
                                    <span style={{
                                      fontSize: '10px',
                                      color: 'var(--text-mute)',
                                      backgroundColor: 'var(--card-bg)',
                                      border: '1px solid var(--border-color)',
                                      borderRadius: '10px',
                                      padding: '1px 7px'
                                    }}>
                                      {orderedGroupNames.length}
                                    </span>
                                  </div>

                                  <button
                                    type="button"
                                    onClick={handleToggleCollapseAll}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      color: 'var(--primary-color)',
                                      fontSize: '11px',
                                      fontWeight: '600',
                                      cursor: 'pointer',
                                      padding: '2px 6px',
                                      borderRadius: '4px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '4px'
                                    }}
                                  >
                                    <span>{allCollapsed ? '▾ Expand All' : '▴ Collapse All'}</span>
                                  </button>
                                </div>
                              )}

                              {orderedGroupNames.map((groupName) => {
                                const groupFields = groups[groupName];
                                const isCollapsed = !!collapsedProoferDatasets[groupName];

                                return (
                                  <fieldset
                                    key={groupName}
                                    style={{
                                      border: '1px solid var(--border-color)',
                                      borderRadius: '8px',
                                      padding: isCollapsed ? '0 10px' : '10px 12px 12px 12px',
                                      margin: 0,
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: '10px',
                                      backgroundColor: 'rgba(255,255,255,0.01)',
                                      transition: 'padding 0.15s ease'
                                    }}
                                  >
                                    <legend
                                      onClick={() => setCollapsedProoferDatasets(prev => ({ ...prev, [groupName]: !prev[groupName] }))}
                                      style={{
                                        fontSize: '11px',
                                        fontWeight: '700',
                                        color: 'var(--text-mute)',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        padding: '0 6px',
                                        cursor: 'pointer',
                                        userSelect: 'none'
                                      }}
                                    >
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <span style={{ fontSize: '10px', color: 'var(--text-mute)' }}>
                                          {isCollapsed ? '▶' : '▼'}
                                        </span>
                                        <span>{groupName}</span>
                                        <span style={{ fontSize: '9px', color: 'var(--text-mute)', fontWeight: 'normal', textTransform: 'none' }}>
                                          ({groupFields.length} {groupFields.length === 1 ? 'field' : 'fields'})
                                        </span>
                                      </div>
                                    </legend>

                                    {!isCollapsed && groupFields.map(field => {
                                      const mappingKey = fieldMappings[field.label] || '';
                                      const isMapped = !!mappingKey;
                                      const isExifMapping = isMapped && mappingKey.startsWith('photo_exif.');
                                      const isOptionLocked = lockedOptions[field.label] !== undefined && lockedOptions[field.label] !== null && lockedOptions[field.label] !== '';
                                      const isRequired = requiredFields[field.label] !== undefined ? !!requiredFields[field.label] : !!field.fieldDef?.required;
                                      const resolvedVal = isMapped ? resolveMappedValue(mappingKey, user, orgData, tenantConfig) : null;
                                      
                                      let currentValue = '';
                                      if (isOptionLocked) {
                                        currentValue = lockedOptions[field.label];
                                      } else if (isMapped && !isExifMapping && resolvedVal !== null && resolvedVal !== undefined && resolvedVal !== '') {
                                        currentValue = resolvedVal;
                                      } else if (testDataOverrides[field.label] !== undefined && testDataOverrides[field.label] !== '') {
                                        currentValue = testDataOverrides[field.label];
                                      } else if (isExifMapping && resolvedVal !== null && resolvedVal !== undefined && resolvedVal !== '') {
                                        currentValue = resolvedVal;
                                      } else {
                                        currentValue = field.sampleValue || field.fieldDef?.sample_data || (field.options?.[0] || '');
                                      }

                                      const isLocked = (isMapped && !isExifMapping) || isOptionLocked;

                                      return (
                                        <div key={`${field.datasetKey}-${field.label}`} style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <label style={{ fontSize: '11px', fontWeight: '600', color: isLocked ? 'var(--text-mute)' : 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '3px' }}>
                                              <span>{field.alias || field.label.replace(/_/g, ' ')}</span>
                                              {isRequired && <span style={{ color: '#ef4444', fontWeight: '700' }} title="Required field">*</span>}
                                            </label>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                              {isRequired && (
                                                <span style={{ fontSize: '9px', backgroundColor: 'rgba(239, 68, 68, 0.15)', color: '#ef4444', padding: '1px 5px', borderRadius: '3px', fontWeight: '700' }}>
                                                  REQUIRED
                                                </span>
                                              )}
                                              {isLocked ? (
                                                <span style={{ fontSize: '9px', color: isOptionLocked ? 'var(--warning-text, #b45309)' : 'var(--text-mute)', display: 'flex', alignItems: 'center', gap: '3px' }}>
                                                  🔒 {isOptionLocked ? 'Locked Option' : 'Auto-Mapped (Read-Only)'}
                                                </span>
                                              ) : isExifMapping ? (
                                                <span style={{ fontSize: '9px', color: 'var(--primary-color)', display: 'flex', alignItems: 'center', gap: '3px' }}>
                                                  📍 Photo EXIF (Editable)
                                                </span>
                                              ) : null}
                                            </div>
                                          </div>
                                          
                                          {field.options && field.options.length > 0 ? (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                              <select
                                                disabled={isLocked}
                                                required={isRequired}
                                                value={currentValue}
                                                onChange={(e) => setTestDataOverrides({ ...testDataOverrides, [field.label]: e.target.value })}
                                                style={isLocked ? disabledInputStyle : inputStyle}
                                              >
                                                {field.options.map(opt => (
                                                  <option key={opt} value={opt}>{opt}</option>
                                                ))}
                                              </select>
                                              {isOptionLocked && (
                                                <div style={{
                                                  fontSize: '9px',
                                                  color: 'var(--warning-text, #b45309)',
                                                  backgroundColor: 'var(--warning-bg, rgba(245, 158, 11, 0.12))',
                                                  padding: '3px 6px',
                                                  borderRadius: '4px',
                                                  border: '1px solid var(--warning-border, rgba(245, 158, 11, 0.3))',
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  gap: '4px'
                                                }}>
                                                  <span>🔒 Option locked to "{currentValue}" (Read-Only)</span>
                                                </div>
                                              )}
                                            </div>
                                          ) : field.type === 'image' ? (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                              <input
                                                type="text"
                                                disabled={isLocked}
                                                required={isRequired}
                                                value={currentValue}
                                                onChange={(e) => setTestDataOverrides({ ...testDataOverrides, [field.label]: e.target.value })}
                                                placeholder="Enter image URL..."
                                                style={isLocked ? disabledInputStyle : inputStyle}
                                              />
                                              {!isLocked && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                  <input
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={async (e) => {
                                                      const file = e.target.files[0];
                                                      if (!file) return;

                                                      // Extract EXIF data from photo (GPS coordinates, altitude, timestamp)
                                                      try {
                                                        const exif = await extractExifFromBlob(file);
                                                        if (exif) {
                                                          setTestDataOverrides(prev => {
                                                            const updated = { ...prev };
                                                            fields.forEach(f => {
                                                              const fMap = fieldMappings[f.label];
                                                              if (fMap && fMap.startsWith('photo_exif.')) {
                                                                const subKey = fMap.replace('photo_exif.', '');
                                                                if (exif[subKey]) {
                                                                  updated[f.label] = exif[subKey];
                                                                }
                                                              }
                                                            });
                                                            return updated;
                                                          });

                                                          if (exif.gps_latitude && exif.gps_longitude) {
                                                            reverseGeocodePlace(exif.gps_latitude, exif.gps_longitude).then(placeName => {
                                                              if (placeName) {
                                                                setTestDataOverrides(prev => {
                                                                  const updated = { ...prev };
                                                                  fields.forEach(f => {
                                                                    if (fieldMappings[f.label] === 'photo_exif.place') {
                                                                      updated[f.label] = placeName;
                                                                    }
                                                                  });
                                                                  return updated;
                                                                });
                                                              }
                                                            });
                                                          }
                                                        }
                                                      } catch (exifErr) {
                                                        console.warn("Could not read EXIF data", exifErr);
                                                      }

                                                      const formData = new FormData();
                                                      formData.append('file', file);
                                                      try {
                                                        const res = await fetch(`${API_BASE}/api/upload`, {
                                                          method: 'POST',
                                                          headers: { 'Authorization': `Bearer ${token}` },
                                                          body: formData
                                                        });
                                                        const data = await res.json();
                                                        if (res.ok && data.file) {
                                                          setTestDataOverrides(prev => ({
                                                            ...prev,
                                                            [field.label]: `${API_BASE}${data.file.location}`
                                                          }));
                                                        }
                                                      } catch (err) {
                                                        console.error("Upload failed", err);
                                                      }
                                                    }}
                                                    style={{ fontSize: '10px', color: 'var(--text-mute)', flex: 1 }}
                                                  />
                                                </div>
                                              )}
                                              {currentValue && (
                                                <img
                                                  src={currentValue}
                                                  alt="Preview"
                                                  style={{ width: '28px', height: '28px', objectFit: 'cover', borderRadius: '4px', border: '1px solid var(--border-color)', marginTop: '2px' }}
                                                />
                                              )}
                                            </div>
                                          ) : (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                              <input
                                                type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                                                disabled={isLocked}
                                                required={isRequired}
                                                value={currentValue}
                                                onChange={(e) => setTestDataOverrides({ ...testDataOverrides, [field.label]: e.target.value })}
                                                placeholder={isExifMapping ? "Enter coordinates or upload photo with GPS..." : ""}
                                                style={isLocked ? disabledInputStyle : inputStyle}
                                              />
                                              {isExifMapping && (
                                                <span style={{ fontSize: '9px', color: 'var(--text-mute)' }}>
                                                  📍 Auto-suggested from photo GPS if available. Editable if manual entry needed.
                                                </span>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </fieldset>
                                );
                              })}
                              {fields.length === 0 && (
                                <div style={{ fontStyle: 'italic', fontSize: '11px', color: 'var(--text-mute)', textAlign: 'center', marginTop: '20px' }}>
                                  No dynamic fields found in this card layout template.
                                </div>
                              )}

                              {fields.length > 0 && (
                                <button
                                  type="button"
                                  onClick={() => setTestDataOverrides({})}
                                  style={{
                                    marginTop: '12px',
                                    backgroundColor: 'transparent',
                                    border: '1px solid var(--border-color)',
                                    borderRadius: '6px',
                                    color: 'var(--text-mute)',
                                    padding: '8px',
                                    fontSize: '11px',
                                    cursor: 'pointer',
                                    fontWeight: '600',
                                    boxSizing: 'border-box',
                                    width: '100%'
                                  }}
                                >
                                  Reset Overrides
                                </button>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* 3D Flippable Full Size Preview Modal */}
        {isPreviewModalOpen && previewDesign && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.88)',
              backdropFilter: 'blur(10px)',
              zIndex: 1000,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '24px',
              padding: '20px'
            }}>
              {/* Modal Title */}
              <div style={{ textAlign: 'center', zIndex: 1010 }}>
                <h3 style={{ margin: 0, color: 'white', fontSize: '18px', fontWeight: '700' }}>
                  {previewDesign.name}
                </h3>
                <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: '4px', display: 'block' }}>
                  {previewDesign.config?.is_template ? 'Official Template Preview' : 'Trophy Card Preview'}
                </span>
              </div>

              {/* 3D Card Container */}
              <div style={{
                perspective: '1500px',
                WebkitPerspective: '1500px',
                width: '300px',
                height: '420px',
                position: 'relative'
              }}>
                <div style={{
                  width: '100%',
                  height: '100%',
                  position: 'relative',
                  transformStyle: 'preserve-3d',
                  WebkitTransformStyle: 'preserve-3d',
                  transition: 'transform 0.6s cubic-bezier(0.4, 0.0, 0.2, 1)',
                  transform: previewFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
                  WebkitTransform: previewFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)'
                }}>
                  {/* Front Side */}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backfaceVisibility: 'hidden',
                    WebkitBackfaceVisibility: 'hidden',
                    transform: 'rotateY(0deg) translateZ(1px)',
                    WebkitTransform: 'rotateY(0deg) translateZ(1px)',
                    transformStyle: 'flat',
                    WebkitTransformStyle: 'flat',
                    borderRadius: '16px',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    backgroundColor: '#0c0a09',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 12px 36px rgba(0,0,0,0.6)',
                    zIndex: previewFlipped ? 1 : 2,
                    pointerEvents: previewFlipped ? 'none' : 'auto'
                  }}>
                    <div style={{ width: '100%', height: '100%', borderRadius: 'inherit', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {previewDesign.config?.preview_image ? (
                        <img src={previewDesign.config.preview_image} alt="Front View" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                      ) : (
                        <div style={{ color: 'var(--text-mute)', fontSize: '14px', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <span style={{ fontSize: '28px' }}>🏆</span>
                          <span>No Front Preview</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Back Side */}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backfaceVisibility: 'hidden',
                    WebkitBackfaceVisibility: 'hidden',
                    transform: 'rotateY(180deg) translateZ(1px)',
                    WebkitTransform: 'rotateY(180deg) translateZ(1px)',
                    transformStyle: 'flat',
                    WebkitTransformStyle: 'flat',
                    borderRadius: '16px',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    backgroundColor: '#0c0a09',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 12px 36px rgba(0,0,0,0.6)',
                    zIndex: previewFlipped ? 2 : 1,
                    pointerEvents: previewFlipped ? 'auto' : 'none'
                  }}>
                    <div style={{ width: '100%', height: '100%', borderRadius: 'inherit', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {previewDesign.config?.preview_image_back ? (
                        <img src={previewDesign.config.preview_image_back} alt="Back View" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                      ) : previewDesign.config?.preview_image ? (
                        <img src={previewDesign.config.preview_image} alt="Back View Fallback" style={{ width: '100%', height: '100%', objectFit: 'contain', opacity: 0.8 }} />
                      ) : (
                        <div style={{ color: 'var(--text-mute)', fontSize: '14px', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <span style={{ fontSize: '28px' }}>🏆</span>
                          <span>No Back Preview</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Controls */}
              <div style={{ display: 'flex', gap: '12px', zIndex: 1010 }}>
                <button
                  type="button"
                  onClick={() => setPreviewFlipped(!previewFlipped)}
                  style={{
                    backgroundColor: 'var(--primary-color)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '8px 18px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#1f72e6'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'var(--primary-color)'}
                >
                  🔄 Flip Card (Show {previewFlipped ? 'Front' : 'Back'})
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setIsPreviewModalOpen(false);
                    setPreviewDesign(null);
                  }}
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.08)',
                    color: 'white',
                    border: '1px solid rgba(255,255,255,0.15)',
                    borderRadius: '6px',
                    padding: '8px 18px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                    transition: 'all 0.2'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.15)'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)'}
                >
                  Close
                </button>
              </div>
            </div>
          )}

          {prooferEditingDesignId && createPortal(
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'var(--panel-bg, #1b1b1d)',
              zIndex: 9999,
              display: 'flex',
              flexDirection: 'column'
            }}>
              <TemplateDesigner
                user={user}
                tenantConfig={tenantConfig}
                initialDesignId={prooferEditingDesignId}
                isTrophyCardMode={true}
                onClose={() => {
                  setProoferEditingDesignId(null);
                }}
                onSaveSuccess={(updatedDesign) => {
                  setProofingDesign(updatedDesign);
                  fetchDesigns();
                  fetchOrders();
                }}
              />
            </div>,
            document.body
          )}

          {isSetupWizardOpen && selectedCoin && createPortal(
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.85)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 99999,
              backdropFilter: 'blur(8px)',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
            }}>
              <div style={{
                background: 'var(--card-bg, #161b22)',
                border: '1px solid var(--border-color, #30363d)',
                borderRadius: '16px',
                padding: '24px',
                width: '490px',
                maxWidth: '92vw',
                maxHeight: '90vh',
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                boxShadow: '0 20px 40px rgba(0,0,0,0.25)',
                color: 'var(--text-main, #ffffff)'
              }}>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: 'var(--text-main, #ffffff)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {wizardIsSystemCoin 
                    ? <><span style={{ color: 'var(--accent-color, var(--primary-color))' }}>⚙</span> {selectedCoin.id === 'new' ? 'Create New System Coin' : 'Edit System Coin'}</>
                    : (selectedCoin.id === 'new' ? 'Create Custom Physical Coin' : 'Setup Custom Physical Coin')}
                </h3>
                
                {/* Coin Name */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-mute, #8b949e)' }}>Coin Name</label>
                  <input
                    type="text"
                    value={wizardCoinName}
                    onChange={(e) => setWizardCoinName(e.target.value)}
                    style={{
                      backgroundColor: 'var(--input-bg, #0d1117)',
                      border: '1px solid var(--input-border, var(--border-color, #30363d))',
                      borderRadius: '6px',
                      color: 'var(--input-text, var(--text-main, #ffffff))',
                      padding: '8px 12px',
                      fontSize: '13px',
                      outline: 'none'
                    }}
                  />
                </div>

                {/* Graphical Face Selectors */}
                <div style={{ display: 'flex', gap: '30px', justifyContent: 'center', margin: '8px 0', width: '100%' }}>
                  {/* Heads Graphical Selector */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                    <label style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-mute, #8b949e)', textTransform: 'uppercase' }}>Heads (Front)</label>
                    <div
                      onClick={() => setWizardSelectingFace('heads')}
                      style={{
                        width: '120px',
                        height: '120px',
                        borderRadius: '50%',
                        overflow: 'hidden',
                        boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
                        border: '2px dashed var(--border-color, #30363d)',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'var(--input-bg, #0d1117)',
                        position: 'relative',
                        transition: 'border-color 0.15s ease'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.borderColor = 'var(--primary-color, #38bdf8)'}
                      onMouseOut={(e) => e.currentTarget.style.borderColor = 'var(--border-color, #30363d)'}
                    >
                      {(() => {
                        const hDesign = designs.find(d => d.id === parseInt(wizardHeadsDesignId)) || (selectedCoin && selectedCoin.face_1_design_id === parseInt(wizardHeadsDesignId) ? selectedCoin.face_1_design : null);
                        if (!hDesign) {
                          return (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', color: 'var(--text-mute, #8b949e)', fontSize: '10px', textAlign: 'center', padding: '10px' }}>
                              <span style={{ fontSize: '20px' }}>＋</span>
                              <span>Select Design</span>
                            </div>
                          );
                        }
                        const cfg = (typeof hDesign.config === 'object' && hDesign.config !== null) ? hDesign.config : {};
                        const coinMetal = cfg.metal || 'gold';
                        const coinSymbol = cfg.symbol || '';
                        const coinMetalConfig = getSafeCoinMetalConfig(coinMetal);

                        return (
                          <>
                            {cfg.previewImage ? (
                              <img src={cfg.previewImage} alt="Heads Preview" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                            ) : (
                              <svg viewBox="0 0 200 200" style={{ width: '100%', height: '100%', overflow: 'visible' }}>
                                {coinMetal === 'white' ? (
                                  <circle cx="100" cy="100" r="95" fill="white" />
                                ) : (
                                  <>
                                    <circle cx="100" cy="100" r="95" fill={coinMetalConfig?.rim || '#ca8a04'} />
                                    <circle cx="100" cy="100" r="85" fill={coinMetalConfig?.base || '#854d0e'} />
                                    {coinSymbol && (coinSymbol.startsWith('http') || coinSymbol.includes('.') || coinSymbol.includes('/')) ? (
                                      <image href={coinSymbol} x="59" y="59" width="82" height="82" />
                                    ) : (
                                      <text x="100" y="115" fontSize="50" textAnchor="middle">{coinSymbol}</text>
                                    )}
                                  </>
                                )}
                              </svg>
                            )}
                            <div style={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              backgroundColor: 'rgba(0,0,0,0.6)',
                              color: 'white',
                              fontSize: '9px',
                              textAlign: 'center',
                              padding: '2px 0'
                            }}>
                              Change
                            </div>
                          </>
                        );
                      })()}
                    </div>
                    {wizardHeadsDesignId && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setWizardHeadsDesignId(''); }}
                        style={{ background: 'none', border: 'none', color: '#ff7b72', fontSize: '10px', cursor: 'pointer', padding: '2px 6px' }}
                      >
                        Clear Face
                      </button>
                    )}
                  </div>

                  {/* Tails Graphical Selector */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                    <label style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-mute, #8b949e)', textTransform: 'uppercase' }}>Tails (Back)</label>
                    <div
                      onClick={() => setWizardSelectingFace('tails')}
                      style={{
                        width: '120px',
                        height: '120px',
                        borderRadius: '50%',
                        overflow: 'hidden',
                        boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
                        border: '2px dashed var(--border-color, #30363d)',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'var(--input-bg, #0d1117)',
                        position: 'relative',
                        transition: 'border-color 0.15s ease'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.borderColor = 'var(--primary-color, #38bdf8)'}
                      onMouseOut={(e) => e.currentTarget.style.borderColor = 'var(--border-color, #30363d)'}
                    >
                      {(() => {
                        const tDesign = designs.find(d => d.id === parseInt(wizardTailsDesignId)) || (selectedCoin && selectedCoin.face_2_design_id === parseInt(wizardTailsDesignId) ? selectedCoin.face_2_design : null);
                        if (!tDesign) {
                          return (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', color: 'var(--text-mute, #8b949e)', fontSize: '10px', textAlign: 'center', padding: '10px' }}>
                              <span style={{ fontSize: '20px' }}>＋</span>
                              <span>Select Design</span>
                            </div>
                          );
                        }
                        const cfg = (typeof tDesign.config === 'object' && tDesign.config !== null) ? tDesign.config : {};
                        const coinMetal = cfg.metal || 'gold';
                        const coinSymbol = cfg.symbol || '';
                        const coinMetalConfig = getSafeCoinMetalConfig(coinMetal);

                        return (
                          <>
                            {cfg.previewImage ? (
                              <img src={cfg.previewImage} alt="Tails Preview" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                            ) : (
                              <svg viewBox="0 0 200 200" style={{ width: '100%', height: '100%', overflow: 'visible' }}>
                                {coinMetal === 'white' ? (
                                  <circle cx="100" cy="100" r="95" fill="white" />
                                ) : (
                                  <>
                                    <circle cx="100" cy="100" r="95" fill={coinMetalConfig?.rim || '#ca8a04'} />
                                    <circle cx="100" cy="100" r="85" fill={coinMetalConfig?.base || '#854d0e'} />
                                    {coinSymbol && (coinSymbol.startsWith('http') || coinSymbol.includes('.') || coinSymbol.includes('/')) ? (
                                      <image href={coinSymbol} x="59" y="59" width="82" height="82" />
                                    ) : (
                                      <text x="100" y="115" fontSize="50" textAnchor="middle">{coinSymbol}</text>
                                    )}
                                  </>
                                )}
                              </svg>
                            )}
                            <div style={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              backgroundColor: 'rgba(0,0,0,0.6)',
                              color: 'white',
                              fontSize: '9px',
                              textAlign: 'center',
                              padding: '2px 0'
                            }}>
                              Change
                            </div>
                          </>
                        );
                      })()}
                    </div>
                    {wizardTailsDesignId && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setWizardTailsDesignId(''); }}
                        style={{ background: 'none', border: 'none', color: '#ff7b72', fontSize: '10px', cursor: 'pointer', padding: '2px 6px' }}
                      >
                        Clear Face
                      </button>
                    )}
                  </div>
                </div>

                {/* Filaments Definition Area */}
                <div style={{
                  borderTop: '1px solid var(--border-color, #30363d)',
                  paddingTop: '14px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px'
                }}>
                  <div style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-main, #ffffff)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span>🧵</span>
                    <span>Production Filaments Assignment</span>
                  </div>

                  {/* Body Filament Selector with Color Swatch */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-mute, #8b949e)' }}>
                      Body Filament
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <select
                        value={wizardBodyFilament}
                        onChange={(e) => setWizardBodyFilament(e.target.value)}
                        style={{
                          flex: 1,
                          backgroundColor: 'var(--input-bg, #0d1117)',
                          border: '1px solid var(--input-border, var(--border-color, #30363d))',
                          borderRadius: '6px',
                          color: 'var(--input-text, var(--text-main, #ffffff))',
                          padding: '7px 10px',
                          fontSize: '12px',
                          outline: 'none',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="" style={{ backgroundColor: 'var(--input-bg, #161b22)', color: 'var(--input-text, var(--text-main, #ffffff))' }}>-- Select Body Filament --</option>
                        {availableProductionFilaments.map((fil, idx) => {
                          const val = `${fil.brand} ${fil.product_name} - ${fil.color_name}`;
                          return (
                            <option key={`body-fil-${fil.id || idx}`} value={val} style={{ backgroundColor: 'var(--input-bg, #161b22)', color: 'var(--input-text, var(--text-main, #ffffff))' }}>
                              {fil.brand} {fil.product_name} - {fil.color_name} {fil.material_type ? `(${fil.material_type})` : ''} {fil.sku ? `[${fil.sku}]` : ''}
                            </option>
                          );
                        })}
                      </select>
                      {/* Live Color Swatch */}
                      {(() => {
                        const matched = availableProductionFilaments.find(f => 
                          wizardBodyFilament && `${f.brand} ${f.product_name} - ${f.color_name}` === wizardBodyFilament
                        );
                        const hex = matched?.color_hex || '#333333';
                        return (
                          <div 
                            title={wizardBodyFilament ? `Color: ${hex}` : 'No filament selected'}
                            style={{
                              width: '26px',
                              height: '26px',
                              borderRadius: '50%',
                              backgroundColor: hex,
                              border: '2px solid var(--border-color, #30363d)',
                              boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                              flexShrink: 0,
                              transition: 'background-color 0.2s ease'
                            }} 
                          />
                        );
                      })()}
                    </div>
                  </div>

                  {/* Accent Filament Selector with Color Swatch */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-mute, #8b949e)' }}>
                      Accent Filament
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <select
                        value={wizardAccentFilament}
                        onChange={(e) => setWizardAccentFilament(e.target.value)}
                        style={{
                          flex: 1,
                          backgroundColor: 'var(--input-bg, #0d1117)',
                          border: '1px solid var(--input-border, var(--border-color, #30363d))',
                          borderRadius: '6px',
                          color: 'var(--input-text, var(--text-main, #ffffff))',
                          padding: '7px 10px',
                          fontSize: '12px',
                          outline: 'none',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="" style={{ backgroundColor: 'var(--input-bg, #161b22)', color: 'var(--input-text, var(--text-main, #ffffff))' }}>-- Select Accent Filament --</option>
                        {availableProductionFilaments.map((fil, idx) => {
                          const val = `${fil.brand} ${fil.product_name} - ${fil.color_name}`;
                          return (
                            <option key={`accent-fil-${fil.id || idx}`} value={val} style={{ backgroundColor: 'var(--input-bg, #161b22)', color: 'var(--input-text, var(--text-main, #ffffff))' }}>
                              {fil.brand} {fil.product_name} - {fil.color_name} {fil.material_type ? `(${fil.material_type})` : ''} {fil.sku ? `[${fil.sku}]` : ''}
                            </option>
                          );
                        })}
                      </select>
                      {/* Live Color Swatch */}
                      {(() => {
                        const matched = availableProductionFilaments.find(f => 
                          wizardAccentFilament && `${f.brand} ${f.product_name} - ${f.color_name}` === wizardAccentFilament
                        );
                        const hex = matched?.color_hex || '#e3b341';
                        return (
                          <div 
                            title={wizardAccentFilament ? `Color: ${hex}` : 'No filament selected'}
                            style={{
                              width: '26px',
                              height: '26px',
                              borderRadius: '50%',
                              backgroundColor: hex,
                              border: '2px solid var(--border-color, #30363d)',
                              boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                              flexShrink: 0,
                              transition: 'background-color 0.2s ease'
                            }} 
                          />
                        );
                      })()}
                    </div>
                  </div>
                </div>

                <div style={{ fontSize: '11px', color: 'var(--text-mute, #8b949e)', borderTop: '1px solid var(--border-color, #30363d)', paddingTop: '10px', marginTop: '2px' }}>
                  {wizardIsSystemCoin 
                    ? '💡 For system coins, you may leave one or both faces blank. Filaments are recorded for production.'
                    : '💡 Both faces must be defined to transition the coin status to "proofing".'}
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '8px' }}>
                  <button
                    type="button"
                    onClick={() => { setIsSetupWizardOpen(false); setSelectedCoin(null); }}
                    style={{
                      backgroundColor: 'transparent',
                      border: '1px solid var(--border-color, #30363d)',
                      color: 'var(--text-main, #ffffff)',
                      borderRadius: '6px',
                      padding: '8px 16px',
                      fontSize: '12px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'var(--panel-bg, rgba(255,255,255,0.05))'}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      if (!wizardCoinName.trim()) {
                        alert("Coin name cannot be empty.");
                        return;
                      }

                      const isNew = selectedCoin.id === 'new';
                      const url = isNew 
                        ? `${API_BASE}/api/v1/physical-coins` 
                        : `${API_BASE}/api/v1/physical-coins/${selectedCoin.id}`;
                      const method = isNew ? 'POST' : 'PATCH';

                      const payload = {
                        name: wizardCoinName.trim(),
                        face_1_design_id: wizardHeadsDesignId ? parseInt(wizardHeadsDesignId) : null,
                        face_2_design_id: wizardTailsDesignId ? parseInt(wizardTailsDesignId) : null,
                        body_filament: wizardBodyFilament || null,
                        accent_filament: wizardAccentFilament || null
                      };

                      if (wizardIsSystemCoin || isTenantAdmin || isGlobalRole) {
                        payload.is_system_coin = wizardIsSystemCoin;
                      }

                      if (isNew) {
                        payload.associated_user_id = selectedCoin.associated_user_id;
                      }
                      
                      try {
                        const res = await fetch(url, {
                          method: method,
                          headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`,
                            'X-Tenant-Domain': window.location.hostname
                          },
                          body: JSON.stringify(payload)
                        });
                        if (!res.ok) {
                          const errData = await res.json().catch(() => ({}));
                          throw new Error(errData.detail || "Failed to save coin configuration.");
                        }
                        
                        const updatedCoin = await res.json();
                        setIsSetupWizardOpen(false);
                        fetchPhysicalCoins();
                        fetchDesigns();
                        
                        if (wizardIsSystemCoin) {
                          setSelectedCoin(null);
                          setCoinViewMode('grid');
                          alert(isNew ? "New system coin created successfully!" : "System coin updated successfully.");
                        } else if (updatedCoin.status === 'proofing') {
                          setSelectedCoin(updatedCoin);
                          const bColor = resolveFilamentHex(updatedCoin.body_filament || wizardBodyFilament, '#1A1A1A');
                          const aColor = resolveFilamentHex(updatedCoin.accent_filament || wizardAccentFilament, '#D4AF37');
                          setCoinBodyColor(bColor);
                          setCoinAccentColor(aColor);
                          setCoinViewMode('proofer');
                          setCoinSupportTab('chat');
                          alert(isNew ? "New coin created and setup complete! Status transitioned to 'proofing'." : "Coin setup complete! Status has transitioned to 'proofing'.");
                        } else {
                          setSelectedCoin(null);
                          alert(isNew ? "New physical coin created successfully!" : "Coin updated successfully.");
                        }
                      } catch (err) {
                        alert(err.message);
                      }
                    }}
                    style={{
                      backgroundColor: 'var(--primary-color, #38bdf8)',
                      color: isDarkColor(resolveSolidColor(tenantConfig?.primary_color, '#38bdf8')) ? '#ffffff' : '#111827',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '8px 16px',
                      fontSize: '12px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'filter 0.15s ease'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.filter = 'brightness(1.1)'}
                    onMouseOut={(e) => e.currentTarget.style.filter = 'none'}
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}

          {wizardSelectingFace && createPortal(
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.85)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 999999,
              backdropFilter: 'blur(10px)',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
            }}>
              <div style={{
                background: 'var(--card-bg, #161b22)',
                border: '1px solid var(--border-color, #30363d)',
                borderRadius: '16px',
                padding: '24px',
                width: '540px',
                maxHeight: '80vh',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
                color: 'var(--text-main, #ffffff)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-color, #30363d)', paddingBottom: '12px' }}>
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', textTransform: 'capitalize', color: 'var(--text-main, #ffffff)' }}>
                    Select {wizardSelectingFace} Coin Design
                  </h3>
                  <button
                    type="button"
                    onClick={() => setWizardSelectingFace(null)}
                    style={{ background: 'none', border: 'none', color: 'var(--text-mute, #8b949e)', fontSize: '18px', cursor: 'pointer' }}
                  >
                    ✕
                  </button>
                </div>

                <div style={{
                  flex: 1,
                  overflowY: 'auto',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
                  gap: '16px',
                  paddingRight: '6px'
                }}>
                  {designs
                    .filter(d => {
                      if (d.design_type !== 'coin_face') return false;
                      if (d.config?.is_template === false || d.config?.is_template === 'false' || d.config?.coin_id) return false;
                      const sideTag = d.config?.side;
                      if (!sideTag) return true; // Backward compatibility
                      return sideTag === wizardSelectingFace;
                    })
                    .map(d => {
                      const cfg = (typeof d.config === 'object' && d.config !== null) ? d.config : {};
                      const coinMetal = cfg.metal || 'gold';
                      const coinSymbol = cfg.symbol || '';
                      const coinMetalConfig = getSafeCoinMetalConfig(coinMetal);

                      return (
                        <div
                          key={`sel-design-${d.id}`}
                          onClick={() => {
                            if (wizardSelectingFace === 'heads') {
                              setWizardHeadsDesignId(d.id.toString());
                            } else {
                              setWizardTailsDesignId(d.id.toString());
                            }
                            setWizardSelectingFace(null);
                          }}
                          style={{
                            border: '1px solid var(--border-color, #30363d)',
                            borderRadius: '8px',
                            padding: '12px',
                            backgroundColor: 'var(--input-bg, #0d1117)',
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '8px',
                            transition: 'border-color 0.15s ease'
                          }}
                          onMouseOver={(e) => e.currentTarget.style.borderColor = 'var(--primary-color, #38bdf8)'}
                          onMouseOut={(e) => e.currentTarget.style.borderColor = 'var(--border-color, #30363d)'}
                        >
                          <div style={{ width: '80px', height: '80px', borderRadius: '50%', overflow: 'hidden', position: 'relative' }}>
                            {cfg.previewImage ? (
                              <img src={cfg.previewImage} alt={d.name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                            ) : (
                              <svg viewBox="0 0 200 200" style={{ width: '100%', height: '100%', overflow: 'visible' }}>
                                {coinMetal === 'white' ? (
                                  <circle cx="100" cy="100" r="95" fill="white" />
                                ) : (
                                  <>
                                    <circle cx="100" cy="100" r="95" fill={coinMetalConfig?.rim || '#ca8a04'} />
                                    <circle cx="100" cy="100" r="85" fill={coinMetalConfig?.base || '#854d0e'} />
                                    {coinSymbol && (coinSymbol.startsWith('http') || coinSymbol.includes('.') || coinSymbol.includes('/')) ? (
                                      <image href={coinSymbol} x="59" y="59" width="82" height="82" />
                                    ) : (
                                      <text x="100" y="115" fontSize="50" textAnchor="middle">{coinSymbol}</text>
                                    )}
                                  </>
                                )}
                              </svg>
                            )}
                          </div>
                          <span style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-main, #ffffff)', textAlign: 'center', display: 'block', maxWidth: '100%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {d.name}
                          </span>
                        </div>
                      );
                    })}
                  {designs.filter(d => {
                    if (d.design_type !== 'coin_face') return false;
                    if (d.config?.is_template === false || d.config?.is_template === 'false' || d.config?.coin_id) return false;
                    const sideTag = d.config?.side;
                    if (!sideTag) return true;
                    return sideTag === wizardSelectingFace;
                  }).length === 0 && (
                    <div style={{ gridColumn: '1/-1', padding: '30px', textAlign: 'center', color: 'var(--text-mute, #8b949e)', fontSize: '12px', fontStyle: 'italic' }}>
                      No designed {wizardSelectingFace} coin faces available.
                    </div>
                  )}
                </div>
              </div>
            </div>,
            document.body
          )}

          {isProofer3DOpen && proofingDesign && createPortal(
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.92)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 99999,
              backdropFilter: 'blur(8px)',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
            }}>
              <div style={{
                background: 'var(--modal-bg, var(--card-bg, #202023))',
                border: '1px solid var(--modal-border, var(--border-color, #3c3c42))',
                borderRadius: '16px',
                padding: '24px',
                width: '480px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                position: 'relative',
                boxShadow: 'var(--card-shadow, 0 20px 40px rgba(0,0,0,0.4))',
                color: 'var(--text-main, #ffffff)'
              }}>
                <button
                  onClick={() => setIsProofer3DOpen(false)}
                  style={{
                    position: 'absolute',
                    top: '15px',
                    right: '15px',
                    backgroundColor: 'var(--badge-bg, rgba(125,125,125,0.12))',
                    color: 'var(--text-main, #fff)',
                    border: '1px solid var(--border-color, transparent)',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  ✕
                </button>

                <h4 style={{ margin: '0 0 4px 0', fontSize: '18px', fontWeight: '800', color: 'var(--primary-color, #1677ff)' }}>Mobile 3D Live Experience</h4>
                <p style={{ margin: '0 0 20px 0', fontSize: '12px', color: 'var(--text-mute, #8e8e93)' }}>Click card to flip between Front and Back design layouts</p>

                {/* 3D Flipping container */}
                <div
                  onClick={() => setPreviewFlipped(!previewFlipped)}
                  style={{
                    width: `${proofingDesign.config?.cardDimensions?.width || 400}px`,
                    height: `${proofingDesign.config?.cardDimensions?.height || 560}px`,
                    perspective: '1000px',
                    WebkitPerspective: '1000px',
                    cursor: 'pointer',
                    marginBottom: '20px'
                  }}
                >
                  <div style={{
                    width: '100%',
                    height: '100%',
                    transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                    transformStyle: 'preserve-3d',
                    WebkitTransformStyle: 'preserve-3d',
                    position: 'relative',
                    transform: previewFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
                    WebkitTransform: previewFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)'
                  }}>
                    {/* Front Side */}
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      backfaceVisibility: 'hidden',
                      WebkitBackfaceVisibility: 'hidden',
                      transform: 'rotateY(0deg) translateZ(1px)',
                      WebkitTransform: 'rotateY(0deg) translateZ(1px)',
                      transformStyle: 'flat',
                      WebkitTransformStyle: 'flat',
                      borderRadius: '16px',
                      boxShadow: '0 12px 36px rgba(0,0,0,0.6)',
                      backgroundColor: '#111111',
                      zIndex: previewFlipped ? 1 : 2,
                      pointerEvents: previewFlipped ? 'none' : 'auto'
                    }}>
                      <BragCardCanvas
                        design={proofingDesign}
                        side="front"
                        scale={1.0}
                        testDataOverrides={testDataOverrides}
                        datasets={datasets}
                        user={user}
                        orgData={orgData}
                        tenantConfig={tenantConfig}
                      />
                    </div>

                    {/* Back Side */}
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      backfaceVisibility: 'hidden',
                      WebkitBackfaceVisibility: 'hidden',
                      transform: 'rotateY(180deg) translateZ(1px)',
                      WebkitTransform: 'rotateY(180deg) translateZ(1px)',
                      transformStyle: 'flat',
                      WebkitTransformStyle: 'flat',
                      borderRadius: '16px',
                      boxShadow: '0 12px 36px rgba(0,0,0,0.6)',
                      backgroundColor: '#111111',
                      zIndex: previewFlipped ? 2 : 1,
                      pointerEvents: previewFlipped ? 'auto' : 'none'
                    }}>
                      <BragCardCanvas
                        design={proofingDesign}
                        side="back"
                        scale={1.0}
                        testDataOverrides={testDataOverrides}
                        datasets={datasets}
                        user={user}
                        orgData={orgData}
                        tenantConfig={tenantConfig}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>,
            document.body
          )}

          {isProoferApprovalOpen && proofingDesign && createPortal(
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.85)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 999999,
              backdropFilter: 'blur(8px)',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
            }}>
              <div style={{
                background: 'var(--modal-bg, var(--card-bg, #202023))',
                border: '1px solid var(--modal-border, var(--border-color, #3c3c42))',
                borderRadius: '16px',
                padding: '28px',
                width: '650px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                boxShadow: 'var(--card-shadow, 0 20px 40px rgba(0,0,0,0.4))',
                color: 'var(--text-main, #ffffff)'
              }}>
                <h3 style={{ margin: '0 0 20px 0', fontSize: '20px', fontWeight: '800', color: 'var(--text-main, #ffffff)', textAlign: 'center' }}>
                  Confirm Card Approval
                </h3>

                {/* Side-by-side previews */}
                <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', marginBottom: '24px', width: '100%' }}>
                  {/* Front Preview */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', flex: 1 }}>
                    <span style={{ fontSize: '11px', color: 'var(--text-mute)', fontWeight: '600' }}>FRONT SIDE</span>
                    {proofingDesign.config?.preview_image ? (
                      <img
                        src={proofingDesign.config.preview_image}
                        alt="Front Preview"
                        style={{
                          width: '100%',
                          maxHeight: '280px',
                          objectFit: 'contain',
                          borderRadius: '8px',
                          border: '1px solid var(--border-color, #3c3c42)',
                          backgroundColor: 'var(--card-bg, #111111)'
                        }}
                      />
                    ) : (
                      <div style={{ width: '100%', height: '280px', borderRadius: '8px', border: '1px solid var(--border-color, #3c3c42)', backgroundColor: 'var(--input-bg, #111111)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-mute, #8e8e93)', fontSize: '11px' }}>
                        No Front Image
                      </div>
                    )}
                  </div>

                  {/* Back Preview */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', flex: 1 }}>
                    <span style={{ fontSize: '11px', color: 'var(--text-mute)', fontWeight: '600' }}>BACK SIDE</span>
                    {proofingDesign.config?.preview_image_back ? (
                      <img
                        src={proofingDesign.config.preview_image_back}
                        alt="Back Preview"
                        style={{
                          width: '100%',
                          maxHeight: '280px',
                          objectFit: 'contain',
                          borderRadius: '8px',
                          border: '1px solid var(--border-color, #3c3c42)',
                          backgroundColor: 'var(--card-bg, #111111)'
                        }}
                      />
                    ) : (
                      <div style={{ width: '100%', height: '280px', borderRadius: '8px', border: '1px solid var(--border-color, #3c3c42)', backgroundColor: 'var(--input-bg, #111111)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-mute, #8e8e93)', fontSize: '11px' }}>
                        No Back Image
                      </div>
                    )}
                  </div>
                </div>

                {/* Warning Text */}
                <div style={{
                  backgroundColor: 'var(--warning-bg, rgba(245, 158, 11, 0.12))',
                  border: '1px solid var(--warning-border, rgba(217, 119, 6, 0.35))',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '24px',
                  color: 'var(--warning-text, #92400e)',
                  fontSize: '13px',
                  lineHeight: '1.5',
                  textAlign: 'center',
                  fontWeight: '600'
                }}>
                  ⚠️ Once the card is approved it becomes available for production. If it is used in production to mint coins, there will be no way to edit the trophy card, are you sure you are ready to approve this trophy card?
                </div>

                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: '12px', width: '100%', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={() => setIsProoferApprovalOpen(false)}
                    style={{
                      backgroundColor: 'var(--button-secondary-bg, rgba(0,0,0,0.04))',
                      color: 'var(--button-secondary-text, var(--text-main, #334155))',
                      border: '1px solid var(--button-secondary-border, var(--border-color, #cbd5e1))',
                      borderRadius: '6px',
                      padding: '8px 20px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    onClick={async () => {
                      setIsProoferApprovalOpen(false);
                      const conf = { ...proofingDesign.config, status: 'approved' };
                      try {
                        const res = await fetch(API_BASE + "/api/v1/designs/" + proofingDesign.id, {
                          method: 'PATCH',
                          headers: {
                            'Content-Type': 'application/json',
                            'Authorization': "Bearer " + token
                          },
                          body: JSON.stringify({ config: conf })
                        });
                        await res.json();
                        if (res.ok) {
                          // Post chat confirmation
                          await fetch(API_BASE + "/api/v1/chat/design/" + proofingDesign.id, {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json',
                              'Authorization': "Bearer " + token
                            },
                            body: JSON.stringify({ message_text: `🏆 Card design approved by client (${user?.email || 'unknown user'}).` })
                          });
                          
                          exitToGrid();
                        }
                      } catch (err) {
                        console.error(err);
                      }
                    }}
                    style={{
                      backgroundColor: '#2ea043',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '8px 24px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Confirm Approval
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}

          {isCoinApprovalOpen && selectedCoin && createPortal(
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.85)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 999999,
              backdropFilter: 'blur(8px)',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
            }}>
              <div style={{
                background: 'var(--modal-bg, var(--card-bg, #202023))',
                border: '1px solid var(--modal-border, var(--border-color, #3c3c42))',
                borderRadius: '16px',
                padding: '28px',
                width: '650px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                boxShadow: 'var(--card-shadow, 0 20px 40px rgba(0,0,0,0.4))',
                color: 'var(--text-main, #ffffff)'
              }}>
                <h3 style={{ margin: '0 0 20px 0', fontSize: '20px', fontWeight: '800', color: 'var(--text-main, #ffffff)', textAlign: 'center' }}>
                  Confirm Coin Approval
                </h3>

                {/* Side-by-side previews */}
                <div style={{ display: 'flex', gap: '40px', justifyContent: 'center', marginBottom: '24px', width: '100%' }}>
                  {(() => {
                    const hDesign = designs.find(d => d.id === selectedCoin.face_1_design_id) || selectedCoin.face_1_design;
                    const tDesign = designs.find(d => d.id === selectedCoin.face_2_design_id) || selectedCoin.face_2_design;

                    const renderModalCoinFaceSVG = (design, title = 'Heads') => {
                      if (!design) {
                        return (
                          <div style={{
                            width: '180px',
                            height: '180px',
                            borderRadius: '50%',
                            border: '2px dashed var(--border-color, #3c3c42)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'var(--text-mute, #8e8e93)',
                            fontSize: '11px'
                          }}>
                            No {title} Face
                          </div>
                        );
                      }
                      const cfg = (typeof design.config === 'object' && design.config !== null) ? design.config : {};
                      const coinMetal = cfg.metal || 'gold';
                      const coinSymbol = cfg.symbol || '';
                      const coinMetalConfig = getSafeCoinMetalConfig(coinMetal);

                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '11px', color: 'var(--text-mute)', fontWeight: '600', textTransform: 'uppercase' }}>{title} SIDE</span>
                          <div style={{ width: '180px', height: '180px', borderRadius: '50%', overflow: 'hidden', boxShadow: '0 8px 24px rgba(0,0,0,0.3)', position: 'relative' }}>
                            {cfg.previewImage ? (
                              <img
                                src={cfg.previewImage}
                                alt={design.name}
                                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                              />
                            ) : (
                              <svg viewBox="0 0 200 200" style={{ width: '100%', height: '100%', overflow: 'visible' }}>
                                {coinMetal === 'white' ? (
                                  <circle cx="100" cy="100" r="95" fill="white" />
                                ) : (
                                  <>
                                    <circle cx="100" cy="100" r="95" fill={coinMetalConfig?.rim || '#ca8a04'} />
                                    <circle cx="100" cy="100" r="85" fill={coinMetalConfig?.base || '#854d0e'} />
                                    {coinSymbol && (coinSymbol.startsWith('http') || coinSymbol.includes('.') || coinSymbol.includes('/')) ? (
                                      <image href={coinSymbol} x="59" y="59" width="82" height="82" />
                                    ) : (
                                      <text x="100" y="115" fontSize="50" textAnchor="middle">{coinSymbol}</text>
                                    )}
                                  </>
                                )}
                              </svg>
                            )}
                          </div>
                        </div>
                      );
                    };

                    return (
                      <>
                        {renderModalCoinFaceSVG(hDesign, 'Heads')}
                        {renderModalCoinFaceSVG(tDesign, 'Tails')}
                      </>
                    );
                  })()}
                </div>

                {/* Warning Text */}
                <div style={{
                  backgroundColor: 'var(--warning-bg, rgba(245, 158, 11, 0.12))',
                  border: '1px solid var(--warning-border, rgba(217, 119, 6, 0.35))',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '24px',
                  color: 'var(--warning-text, #92400e)',
                  fontSize: '13px',
                  lineHeight: '1.5',
                  textAlign: 'center',
                  fontWeight: '600'
                }}>
                  ⚠️ Once the coin is approved it becomes available for production. If it is used in production to mint physical coins, there will be no way to edit the coin designs, are you sure you are ready to approve this coin?
                </div>

                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: '12px', width: '100%', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={() => setIsCoinApprovalOpen(false)}
                    style={{
                      backgroundColor: 'var(--button-secondary-bg, rgba(0,0,0,0.04))',
                      color: 'var(--button-secondary-text, var(--text-main, #334155))',
                      border: '1px solid var(--button-secondary-border, var(--border-color, #cbd5e1))',
                      borderRadius: '6px',
                      padding: '8px 20px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    onClick={async () => {
                      setIsCoinApprovalOpen(false);
                      try {
                        const res = await fetch(`${API_BASE}/api/v1/physical-coins/${selectedCoin.id}`, {
                          method: 'PATCH',
                          headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`,
                            'X-Tenant-Domain': window.location.hostname
                          },
                          body: JSON.stringify({ status: 'approved' })
                        });
                        if (!res.ok) throw new Error("Failed to approve coin");

                        await fetch(`${API_BASE}/api/v1/chat/coin/${selectedCoin.id}`, {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                          },
                          body: JSON.stringify({ message_text: `🏆 Coin approved for production by client (${user?.email || 'unknown user'}).` })
                        });

                        fetchCoinDetail(selectedCoin.id);
                        alert("Coin approved successfully!");
                      } catch (err) {
                        alert(err.message);
                      }
                    }}
                    style={{
                      backgroundColor: '#2ea043',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '8px 24px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Confirm Approval
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}

          {isCoin3DPreviewOpen && selectedCoin && createPortal(
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.92)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 999999,
              backdropFilter: 'blur(8px)',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
            }}>
              <div style={{
                background: 'var(--card-bg, #1c1c1f)',
                border: '1px solid var(--border-color, #3c3c42)',
                borderRadius: '16px',
                padding: '24px',
                width: '450px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                position: 'relative',
                boxShadow: '0 20px 40px rgba(0,0,0,0.6)',
                color: 'var(--text-main, #ffffff)'
              }}>
                <button
                  onClick={() => setIsCoin3DPreviewOpen(false)}
                  style={{
                    position: 'absolute',
                    top: '15px',
                    right: '15px',
                    backgroundColor: 'rgba(255,255,255,0.06)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10
                  }}
                >
                  ✕
                </button>

                <h3 style={{ margin: '0 0 4px 0', fontSize: '18px', fontWeight: '800', color: '#58a6ff' }}>3D Spinning Coin Preview</h3>
                <p style={{ margin: '0 0 8px 0', fontSize: '11px', color: '#8e8e93', textAlign: 'center', lineHeight: '1.4' }}>
                  {selectedCoin.name} • 40mm coin with 4mm thickness. Face reliefs raised to 0.8mm.
                  <span style={{ display: 'block', fontSize: '10px', color: '#7e7e83', marginTop: '3px', fontStyle: 'italic' }}>
                    (This is a 3D graphical representation of the coin only, colours may vary.)
                  </span>
                </p>

                {(selectedCoin.body_filament || selectedCoin.accent_filament) && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px', flexWrap: 'wrap', justifyContent: 'center' }}>
                    {selectedCoin.body_filament && (
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: '11px',
                        padding: '3px 8px',
                        borderRadius: '6px',
                        backgroundColor: 'rgba(255,255,255,0.06)',
                        border: '1px solid rgba(255,255,255,0.1)'
                      }}>
                        <span style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: coinBodyColor, border: '1px solid #777' }} />
                        <span>Body: <strong style={{ color: '#ffffff' }}>{selectedCoin.body_filament}</strong></span>
                      </span>
                    )}
                    {selectedCoin.accent_filament && (
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: '11px',
                        padding: '3px 8px',
                        borderRadius: '6px',
                        backgroundColor: 'rgba(255,255,255,0.06)',
                        border: '1px solid rgba(255,255,255,0.1)'
                      }}>
                        <span style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: coinAccentColor, border: '1px solid #777' }} />
                        <span>Accent: <strong style={{ color: '#ffffff' }}>{selectedCoin.accent_filament}</strong></span>
                      </span>
                    )}
                  </div>
                )}

                <Coin3DRendererContainer
                  hDesign={designs.find(d => d.id === selectedCoin.face_1_design_id) || selectedCoin.face_1_design}
                  tDesign={designs.find(d => d.id === selectedCoin.face_2_design_id) || selectedCoin.face_2_design}
                  bodyColor={coinBodyColor}
                  accentColor={coinAccentColor}
                  invert={coinInvert}
                />

                {/* 3D Viewer Invert & Color Controls */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '16px',
                  marginTop: '16px',
                  width: '100%',
                  paddingTop: '12px',
                  borderTop: '1px solid rgba(255,255,255,0.1)',
                  flexWrap: 'wrap'
                }}>
                  <button
                    type="button"
                    onClick={() => setCoinInvert(prev => !prev)}
                    style={{
                      backgroundColor: coinInvert ? '#0969da' : 'rgba(255,255,255,0.08)',
                      color: '#ffffff',
                      border: `1px solid ${coinInvert ? '#58a6ff' : 'rgba(255,255,255,0.2)'}`,
                      borderRadius: '8px',
                      padding: '8px 18px',
                      fontSize: '12px',
                      fontWeight: '700',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'all 0.15s ease',
                      boxShadow: coinInvert ? '0 2px 10px rgba(9, 105, 218, 0.4)' : 'none'
                    }}
                    onMouseOver={(e) => {
                      if (!coinInvert) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.15)';
                    }}
                    onMouseOut={(e) => {
                      if (!coinInvert) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)';
                    }}
                  >
                    <span>🔄</span>
                    <span>Invert Face Reliefs: {coinInvert ? 'ON' : 'OFF'}</span>
                  </button>

                  {/* Quick Color Swatches in 3D Modal */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <label style={{ fontSize: '10px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase' }}>Body</label>
                      <input
                        type="color"
                        value={coinBodyColor}
                        onChange={(e) => setCoinBodyColor(e.target.value)}
                        style={{
                          width: '26px',
                          height: '26px',
                          border: '1px solid rgba(255,255,255,0.2)',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          backgroundColor: 'transparent',
                          padding: 0
                        }}
                      />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <label style={{ fontSize: '10px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase' }}>Accent</label>
                      <input
                        type="color"
                        value={coinAccentColor}
                        onChange={(e) => setCoinAccentColor(e.target.value)}
                        style={{
                          width: '26px',
                          height: '26px',
                          border: '1px solid rgba(255,255,255,0.2)',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          backgroundColor: 'transparent',
                          padding: 0
                        }}
                      />
                    </div>
                    {(selectedCoin.body_filament || selectedCoin.accent_filament) && (
                      <button
                        type="button"
                        onClick={() => {
                          const b = resolveFilamentHex(selectedCoin.body_filament, '#1A1A1A');
                          const a = resolveFilamentHex(selectedCoin.accent_filament, '#D4AF37');
                          setCoinBodyColor(b);
                          setCoinAccentColor(a);
                        }}
                        style={{
                          backgroundColor: 'rgba(255,255,255,0.08)',
                          border: '1px solid rgba(255,255,255,0.2)',
                          color: '#cbd5e1',
                          borderRadius: '6px',
                          padding: '5px 10px',
                          fontSize: '11px',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        ↺ Reset Filaments
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>,
            document.body
          )}

          {isFullscreenFaceEditorOpen && createPortal(
            <ErrorBoundary
              fallback={(error, reset) => (
                <div style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  width: '100vw',
                  height: '100vh',
                  zIndex: 999999,
                  backgroundColor: '#1a1a1c',
                  color: '#dfdfe3',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '16px',
                  padding: '20px'
                }}>
                  <div style={{ fontSize: '48px' }}>⚠️</div>
                  <h2 style={{ margin: 0, color: '#f87171' }}>Failed to Open Face Editor</h2>
                  <p style={{ margin: 0, maxWidth: '500px', textAlign: 'center', color: '#94a3b8', fontSize: '13px', lineHeight: '1.5' }}>
                    {error?.message || 'An unexpected rendering error occurred while loading this coin face template.'}
                  </p>
                  <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                    <button
                      type="button"
                      onClick={() => setIsFullscreenFaceEditorOpen(false)}
                      style={{
                        backgroundColor: '#262629',
                        color: '#dfdfe3',
                        border: '1px solid #3c3c42',
                        borderRadius: '6px',
                        padding: '8px 16px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      Close Editor
                    </button>
                    <button
                      type="button"
                      onClick={reset}
                      style={{
                        backgroundColor: '#3b82f6',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '8px 16px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      Retry
                    </button>
                  </div>
                </div>
              )}
            >
              <FullscreenFaceEditor
                user={user}
                tenantConfig={tenantConfig}
                designName={designName}
                setDesignName={setDesignName}
                selectedDesignId={selectedDesignId}
                metal={metal}
                setMetal={setMetal}
                faceSide={faceSide}
                setFaceSide={setFaceSide}
                symbol={symbol}
                setSymbol={setSymbol}
                upperText={upperText}
                setUpperText={setUpperText}
                lowerText={lowerText}
                setLowerText={setLowerText}
                textMode={textMode}
                setTextMode={setTextMode}
                singleText={singleText}
                setSingleText={setSingleText}
                textFontWeight={textFontWeight}
                setTextFontWeight={setTextFontWeight}
                textFontStyle={textFontStyle}
                setTextFontStyle={setTextFontStyle}
                textCase={textCase}
                setTextCase={setTextCase}
                edgeStyle={edgeStyle}
                setEdgeStyle={setEdgeStyle}
                depth={depth}
                setDepth={setDepth}
                textGrayscale={textGrayscale}
                setTextGrayscale={setTextGrayscale}
                textRadius={textRadius}
                setTextRadius={setTextRadius}
                textSize={textSize}
                setTextSize={setTextSize}
                textKerning={textKerning}
                setTextKerning={setTextKerning}
                textFont={textFont}
                setTextFont={setTextFont}
                rings={rings}
                setRings={setRings}
                selectedRingId={selectedRingId}
                setSelectedRingId={setSelectedRingId}
                addRing={addRing}
                deleteRing={deleteRing}
                moveRingUp={moveRingUp}
                moveRingDown={moveRingDown}
                updateSelectedRing={updateSelectedRing}
                handleAssetDoubleClick={handleAssetDoubleClick}
                studioAssets={studioAssets}
                fetchStudioAssets={fetchStudioAssets}
                assetsSearch={assetsSearch}
                setAssetsSearch={setAssetsSearch}
                assetsType={assetsType}
                setAssetsType={setAssetsType}
                handleSaveConfig={handleSaveConfig}
                handleExportHighResPNG={handleExportHighResPNG}
                exportTypographySVG={exportTypographySVG}
                setIsFullscreenFaceEditorOpen={setIsFullscreenFaceEditorOpen}
              />
            </ErrorBoundary>,
            document.body
          )}
      </div>
      </div>
    </div>
  );
}

const Coin3DRendererContainer = ({ hDesign, tDesign, bodyColor, accentColor, invert }) => {
  const canvasRef = useRef(null);
  const [threeLoaded, setThreeLoaded] = useState(!!window.THREE);

  useEffect(() => {
    if (window.THREE) {
      setThreeLoaded(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js';
    script.async = true;
    script.onload = () => {
      setThreeLoaded(true);
    };
    document.head.appendChild(script);
    return () => {
      // Keep script loaded
    };
  }, []);

  useEffect(() => {
    if (!threeLoaded || !canvasRef.current || !window.THREE) return;

    const THREE = window.THREE;
    const width = 400;
    const height = 400;

    const scene = new THREE.Scene();

    const camera = new THREE.PerspectiveCamera(40, width / height, 0.1, 100);
    camera.position.set(0, 0, 4.5);

    const renderer = new THREE.WebGLRenderer({ canvas: canvasRef.current, antialias: true, alpha: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(window.devicePixelRatio || 1);

    // Studio lighting environment
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    scene.add(ambientLight);

    const dirLight1 = new THREE.DirectionalLight(0xffffff, 0.8);
    dirLight1.position.set(4, 5, 3);
    scene.add(dirLight1);

    const dirLight2 = new THREE.DirectionalLight(0xffffff, 0.3);
    dirLight2.position.set(-4, -5, -3);
    scene.add(dirLight2);

    const pointLight = new THREE.PointLight(0xffffff, 0.5, 10);
    pointLight.position.set(0, 0, 5);
    scene.add(pointLight);

    // Cylinder representing 40mm coin with 4mm thickness (10% ratio)
    const geometry = new THREE.CylinderGeometry(1.0, 1.0, 0.2, 64);

    // Create repeating micro-grain noise texture for the plastic edge
    const createNoiseTexture = () => {
      const noiseCanvas = document.createElement('canvas');
      noiseCanvas.width = 128;
      noiseCanvas.height = 128;
      const noiseCtx = noiseCanvas.getContext('2d');
      const noiseData = noiseCtx.createImageData(128, 128);
      const noisePixels = noiseData.data;
      for (let i = 0; i < noisePixels.length; i += 4) {
        const val = Math.floor(128 + (Math.random() - 0.5) * 32);
        noisePixels[i] = val;
        noisePixels[i+1] = val;
        noisePixels[i+2] = val;
        noisePixels[i+3] = 255;
      }
      noiseCtx.putImageData(noiseData, 0, 0);
      const tex = new THREE.CanvasTexture(noiseCanvas);
      tex.wrapS = THREE.RepeatWrapping;
      tex.wrapT = THREE.RepeatWrapping;
      tex.repeat.set(16, 4); // repeating grain pattern
      return tex;
    };
    const sideNoiseTex = createNoiseTexture();

    const materials = [];

    // Edge
    materials.push(new THREE.MeshStandardMaterial({
      color: 0xd4af37,
      metalness: 0.0,
      roughness: 0.3,
      bumpMap: sideNoiseTex,
      bumpScale: 0.012 // increased ABS satin finish micro-texture
    }));

    // Heads
    const headsMaterial = new THREE.MeshStandardMaterial({
      color: 0xd4af37,
      metalness: 0.0,
      roughness: 0.3
    });
    materials.push(headsMaterial);

    // Tails
    const tailsMaterial = new THREE.MeshStandardMaterial({
      color: 0xd4af37,
      metalness: 0.0,
      roughness: 0.3
    });
    materials.push(tailsMaterial);

    // Edge material base color matches ABS body color
    const safeBody = (bodyColor && bodyColor.startsWith('#')) ? bodyColor : (bodyColor ? `#${bodyColor}` : '#1A1A1A');
    const safeAccent = (accentColor && accentColor.startsWith('#')) ? accentColor : (accentColor ? `#${accentColor}` : '#D4AF37');
    materials[0].color.setStyle(safeBody);

    // Heads and Tails materials default to bodyColor unless texture map is loaded
    headsMaterial.color.setStyle(safeBody);
    tailsMaterial.color.setStyle(safeBody);

    const loadColorizedTexture = (url, material, isHeads = true) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = 512;
        canvas.height = 512;
        const ctx = canvas.getContext('2d');

        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = 512;
        tempCanvas.height = 512;
        const tempCtx = tempCanvas.getContext('2d');
        tempCtx.drawImage(img, 0, 0, 512, 512);

        const imgData = tempCtx.getImageData(0, 0, 512, 512);
        const pixels = imgData.data;

        const hexToRgb = (hex) => {
          const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
          const fullHex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);
          const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(fullHex);
          return result ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16)
          } : { r: 26, g: 26, b: 26 };
        };

        const bRgb = hexToRgb(safeBody);
        const aRgb = hexToRgb(safeAccent);

        const targetData = ctx.createImageData(512, 512);
        const targetPixels = targetData.data;

        let sum = 0;
        let count = 0;
        for (let j = 0; j < pixels.length; j += 4) {
          if (pixels[j+3] > 10) {
            sum += (pixels[j] + pixels[j+1] + pixels[j+2]) / 3;
            count++;
          }
        }
        const avgBrightness = count > 0 ? (sum / count) : 128;

        for (let i = 0; i < pixels.length; i += 4) {
          const r = pixels[i];
          const g = pixels[i+1];
          const b = pixels[i+2];
          const a = pixels[i+3];

          if (a < 10) {
            targetPixels[i] = bRgb.r;
            targetPixels[i+1] = bRgb.g;
            targetPixels[i+2] = bRgb.b;
            targetPixels[i+3] = 0;
            continue;
          }

          const v = (r + g + b) / 3;
          let weight = 0;

          if (avgBrightness > 100) {
            weight = Math.min(1, Math.max(0, (160 - v) / 100));
          } else {
            weight = Math.min(1, Math.max(0, (v - 50) / 100));
          }

          if (invert) {
            weight = 1 - weight;
          }

          targetPixels[i] = Math.min(255, Math.max(0, Math.round(bRgb.r * (1 - weight) + aRgb.r * weight)));
          targetPixels[i+1] = Math.min(255, Math.max(0, Math.round(bRgb.g * (1 - weight) + aRgb.g * weight)));
          targetPixels[i+2] = Math.min(255, Math.max(0, Math.round(bRgb.b * (1 - weight) + aRgb.b * weight)));
          targetPixels[i+3] = a;
        }

        ctx.putImageData(targetData, 0, 0);

        const canvasTexture = new THREE.CanvasTexture(canvas);
        canvasTexture.center.set(0.5, 0.5);
        // Front face rotated 90deg clockwise (Math.PI - Math.PI/2 = Math.PI/2)
        // Back face rotated 90deg anti-clockwise (0 + Math.PI/2 = Math.PI/2)
        canvasTexture.rotation = Math.PI / 2;
        material.color.setStyle('#ffffff');
        material.map = canvasTexture;
        material.bumpMap = canvasTexture;
        material.bumpScale = invert ? -0.04 : 0.04;

        material.needsUpdate = true;
      };
      img.src = url;
    };

    if (hDesign?.config?.previewImage) {
      loadColorizedTexture(hDesign.config.previewImage, headsMaterial, true);
    }

    if (tDesign?.config?.previewImage) {
      loadColorizedTexture(tDesign.config.previewImage, tailsMaterial, false);
    }

    const coin = new THREE.Mesh(geometry, materials);
    coin.rotation.order = 'YXZ';
    coin.rotation.x = Math.PI / 2;
    coin.rotation.z = Math.PI;
    scene.add(coin);

    let animId;
    let currentYRotation = 0;
    const animate = () => {
      animId = requestAnimationFrame(animate);
      currentYRotation += 0.0135; // Horizontal coin flip spin (slowed by 10%)
      coin.rotation.y = currentYRotation;
      
      // Dynamic wobble/tilt on X/Z axis to view thickness and 3D reliefs
      coin.rotation.x = (Math.PI / 2) + Math.sin(Date.now() * 0.001) * 0.15;
      coin.rotation.z = Math.PI + Math.cos(Date.now() * 0.001) * 0.1;
      
      renderer.render(scene, camera);
    };
    animate();

    return () => {
      cancelAnimationFrame(animId);
      geometry.dispose();
      materials.forEach(m => m.dispose());
      renderer.dispose();
    };
  }, [threeLoaded, hDesign, tDesign, bodyColor, accentColor, invert]);

  return (
    <div style={{
      width: '400px',
      height: '400px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#121214',
      borderRadius: '12px',
      border: '1px solid #2d2d30',
      overflow: 'hidden'
    }}>
      {!threeLoaded ? (
        <span style={{ fontSize: '12px', color: '#8e8e93' }}>Loading live 3D assets...</span>
      ) : (
        <canvas ref={canvasRef} style={{ display: 'block', width: '100%', height: '100%' }} />
      )}
    </div>
  );
};
